import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { 
  Heart, DollarSign, Users, Target, FileText, Calendar, 
  Plus, Search, Filter, Download, Printer, TrendingUp, 
  CheckCircle, Clock, AlertCircle, ChevronRight, X,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  Building, Mail, Phone, MapPin, Edit, Trash2, Eye,
  Settings, Save, LogOut, Lock, User, UserPlus, Shield,
  Activity, Key, UserCheck, UserX, Upload, FileSpreadsheet,
  AlertTriangle, Check, Landmark, Bell, Award
} from 'lucide-react';

// Utility functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const generateId = () => Math.random().toString(36).substr(2, 9);

// Simple hash function for passwords (in production, use proper hashing)
const hashPassword = (password) => {
  let hash = 0;
  for (let i = 0; i < password.length; i++) {
    const char = password.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(16);
};

// Default admin user (password: admin123)
const defaultUsers = [
  { 
    id: 'admin', 
    username: 'admin', 
    password: hashPassword('admin123'), 
    name: 'Administrator', 
    email: 'admin@nonprofit.org',
    role: 'admin', 
    status: 'active',
    createdAt: '2025-01-01T00:00:00Z',
    createdBy: 'system'
  }
];

// Default settings
const defaultSettings = {
  organizationName: 'Non-Profit Organization',
  taxId: '12-3456789',
  address: '123 Charity Lane, Anytown, ST 12345',
  phone: '(555) 123-4567',
  email: 'info@nonprofit.org',
  website: 'www.nonprofit.org',
  logo: '', // Base64 encoded logo image
  campaigns: ['General Fund', 'Building Fund', 'Youth Program', 'Emergency Relief', 'Scholarship Fund', 'Community Outreach'],
  annualFundGoals: {
    'Maintenance': 5000,
    'Security': 3000,
    'Electricity': 6000,
    'Water': 2000,
    'Gas': 2500,
    'Landscaping': 1500,
    'Phone/Internet': 1800,
    'Trash': 1200,
    'Supplies': 2000,
    'Activities': 3000,
    'Insurance': 4000
  },
  expansionGoal: 500000, // Expansion Donation Goal
  expansionCashOnHand: 0 // Cash on hand for expansion
};

// Initial sample data
const initialDonors = [
  { id: '1', name: 'John Smith', email: 'john@email.com', phone: '555-0101', address: '123 Main St, City, ST 12345', totalDonated: 2500, donationCount: 5 },
  { id: '2', name: 'Sarah Johnson', email: 'sarah@email.com', phone: '555-0102', address: '456 Oak Ave, Town, ST 23456', totalDonated: 1000, donationCount: 2 },
  { id: '3', name: 'Michael Brown', email: 'michael@email.com', phone: '555-0103', address: '789 Pine Rd, Village, ST 34567', totalDonated: 5000, donationCount: 10 },
];

const initialDonations = [
  { id: '1', donorId: '1', donorName: 'John Smith', amount: 500, date: '2025-01-15', type: 'one-time', campaign: 'General Fund', method: 'Credit Card', notes: '' },
  { id: '2', donorId: '2', donorName: 'Sarah Johnson', amount: 1000, date: '2025-01-10', type: 'one-time', campaign: 'Building Fund', method: 'Check', notes: 'Check #1234' },
  { id: '3', donorId: '3', donorName: 'Michael Brown', amount: 250, date: '2025-01-08', type: 'recurring', campaign: 'Youth Program', method: 'Bank Transfer', notes: '' },
  { id: '4', donorId: '1', donorName: 'John Smith', amount: 200, date: '2025-01-05', type: 'one-time', campaign: 'Emergency Relief', method: 'Credit Card', notes: '' },
  { id: '5', donorId: '3', donorName: 'Michael Brown', amount: 500, date: '2024-12-20', type: 'one-time', campaign: 'General Fund', method: 'Cash', notes: '' },
];

const initialPledges = [
  { id: '1', donorId: '1', donorName: 'John Smith', totalAmount: 1200, paidAmount: 400, startDate: '2025-01-01', endDate: '2025-12-31', frequency: 'monthly', campaign: 'Building Fund', status: 'active', payments: [
    { date: '2025-01-15', amount: 400 }
  ]},
  { id: '2', donorId: '2', donorName: 'Sarah Johnson', totalAmount: 500, paidAmount: 0, startDate: '2025-02-01', endDate: '2025-06-30', frequency: 'monthly', campaign: 'Youth Program', status: 'pending', payments: [] },
  { id: '3', donorId: '3', donorName: 'Michael Brown', totalAmount: 2400, paidAmount: 2400, startDate: '2024-01-01', endDate: '2024-12-31', frequency: 'monthly', campaign: 'General Fund', status: 'completed', payments: [] },
];

const initialGoals = [
  { id: '1', name: 'Annual Fundraising Goal', target: 50000, current: 18500, deadline: '2025-12-31', campaign: 'General Fund', description: 'Our yearly fundraising target' },
  { id: '2', name: 'Building Renovation', target: 25000, current: 12000, deadline: '2025-06-30', campaign: 'Building Fund', description: 'Funds for facility improvements' },
  { id: '3', name: 'Youth Summer Camp', target: 10000, current: 3500, deadline: '2025-05-15', campaign: 'Youth Program', description: 'Sponsor kids for summer camp' },
];

const paymentMethods = ['Credit Card', 'Check', 'Cash', 'Bank Transfer', 'PayPal', 'Other'];

export default function DonationSystem() {
  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState(defaultUsers);
  const [activityLog, setActivityLog] = useState([]);
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [donors, setDonors] = useState(initialDonors);
  const [donations, setDonations] = useState(initialDonations);
  const [pledges, setPledges] = useState(initialPledges);
  const [goals, setGoals] = useState(initialGoals);
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showDonorModal, setShowDonorModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showPledgePaymentModal, setShowPledgePaymentModal] = useState(false);
  const [showPledgeReceiptModal, setShowPledgeReceiptModal] = useState(false);
  const [showPledgeSummaryModal, setShowPledgeSummaryModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  
  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [pledgePaymentReceipt, setPledgePaymentReceipt] = useState(null);
  const [pledgeSummaryReceipt, setPledgeSummaryReceipt] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Filter states
  const [reportDateRange, setReportDateRange] = useState({ start: '', end: '' });
  const [reportType, setReportType] = useState('all');
  
  // Search states
  const [donationSearch, setDonationSearch] = useState('');
  
  // Pledge view state
  const [pledgeViewFilter, setPledgeViewFilter] = useState('active'); // 'all', 'active', 'completed'

  // Donation Box modal state
  const [showDonationBoxModal, setShowDonationBoxModal] = useState(false);
  const [donationBoxCampaign, setDonationBoxCampaign] = useState('');

  // Annual Fund Raising modal state
  const [showAnnualFundModal, setShowAnnualFundModal] = useState(false);
  const [annualFundCategory, setAnnualFundCategory] = useState('');
  const [annualFundTab, setAnnualFundTab] = useState('overview');
  
  // Expansion portal state
  const [expansionPaymentType, setExpansionPaymentType] = useState('pledge-payment');
  const [expansionPortalTab, setExpansionPortalTab] = useState('overview');
  const [editingExpansionPledge, setEditingExpansionPledge] = useState(null);
  const [expansionSearchTerm, setExpansionSearchTerm] = useState('');
  const [expansionPledgeReceipt, setExpansionPledgeReceipt] = useState(null);
  
  // Bank Activity state
  const [bankDeposits, setBankDeposits] = useState([]);
  const [bankActivityMonth, setBankActivityMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [bankActivityFilter, setBankActivityFilter] = useState('all');
  const [showBankDepositModal, setShowBankDepositModal] = useState(false);
  const [editingBankDeposit, setEditingBankDeposit] = useState(null);
  
  // Admin menu dropdown state
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  
  // Close admin menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAdminMenu && !event.target.closest('.admin-menu-container')) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdminMenu]);
  
  // Annual Fund Categories (names only - goals stored in settings)
  const annualFundCategories = [
    'Maintenance',
    'Security',
    'Electricity',
    'Water',
    'Gas',
    'Landscaping',
    'Phone/Internet',
    'Trash',
    'Supplies',
    'Activities',
    'Insurance'
  ];
  
  // Annual Fund Goal Edit state
  const [editingGoalCategory, setEditingGoalCategory] = useState(null);
  const [editingGoalAmount, setEditingGoalAmount] = useState('');
  
  // Annual Fund Payment Mode state
  const [annualFundPaymentMode, setAnnualFundPaymentMode] = useState('one-time'); // 'one-time', 'paid-in-full', 'monthly-pledge'
  const [monthlyPledgePayments, setMonthlyPledgePayments] = useState(Array(12).fill(false));
  const [monthlyPledgeAmount, setMonthlyPledgeAmount] = useState('');
  const [showExistingPayments, setShowExistingPayments] = useState(false);

  // Email sending states
  const [emailStatus, setEmailStatus] = useState({ show: false, success: false, message: '' });

  // Import states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState(''); // 'donors', 'donations', 'pledges'
  const [importData, setImportData] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: map columns, 3: preview, 4: importing
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState(0);

  // Load data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        // Check for existing session in localStorage
        const savedSession = localStorage.getItem('donation-session');
        if (savedSession) {
          const session = JSON.parse(savedSession);
          if (session.user && session.expiry > Date.now()) {
            setCurrentUser(session.user);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('donation-session');
          }
        }

        // Load all data from Supabase
        const [
          { data: donorsData },
          { data: donationsData },
          { data: pledgesData },
          { data: goalsData },
          { data: settingsData },
          { data: usersData },
          { data: activityData },
          { data: bankDepositsData }
        ] = await Promise.all([
          supabase.from('donors').select('*').order('created_at', { ascending: false }),
          supabase.from('donations').select('*').order('date', { ascending: false }),
          supabase.from('pledges').select('*').order('created_at', { ascending: false }),
          supabase.from('goals').select('*').order('created_at', { ascending: false }),
          supabase.from('settings').select('*').eq('id', 1).single(),
          supabase.from('users').select('*').order('created_at', { ascending: false }),
          supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(100),
          supabase.from('bank_deposits').select('*').order('date', { ascending: false })
        ]);

        if (donorsData) {
          setDonors(donorsData.map(d => ({
            id: d.id,
            name: d.name,
            email: d.email,
            phone: d.phone,
            address: d.address,
            totalDonated: parseFloat(d.total_donated) || 0,
            donationCount: d.donation_count || 0,
            pledgeForm: d.pledge_form || '',
            responsible: d.responsible || '',
            donorNotes: d.donor_notes || ''
          })));
        }
        
        if (donationsData) {
          setDonations(donationsData.map(d => ({
            id: d.id,
            donorId: d.donor_id,
            donorName: d.donor_name,
            amount: parseFloat(d.amount),
            date: d.date,
            type: d.type,
            campaign: d.campaign,
            method: d.method,
            notes: d.notes
          })));
        }
        
        if (pledgesData) {
          // Load pledge payments for each pledge
          const pledgesWithPayments = await Promise.all(
            pledgesData.map(async (pledge) => {
              const { data: payments } = await supabase
                .from('pledge_payments')
                .select('*')
                .eq('pledge_id', pledge.id)
                .order('date', { ascending: true });
              return {
                id: pledge.id,
                donorId: pledge.donor_id,
                donorName: pledge.donor_name,
                donorEmail: pledge.donor_email || '',
                totalAmount: parseFloat(pledge.total_amount),
                paidAmount: parseFloat(pledge.paid_amount) || 0,
                startDate: pledge.start_date,
                endDate: pledge.end_date,
                frequency: pledge.frequency,
                campaign: pledge.campaign,
                status: pledge.status,
                responsible: pledge.responsible || '',
                pledgeForm: pledge.pledge_form || 'N',
                pledgeNotes: pledge.pledge_notes || '',
                payments: (payments || []).map(p => ({ date: p.date, amount: parseFloat(p.amount) }))
              };
            })
          );
          setPledges(pledgesWithPayments);
        }
        
        if (goalsData) {
          setGoals(goalsData.map(g => ({
            id: g.id,
            name: g.name,
            target: parseFloat(g.target),
            current: parseFloat(g.current) || 0,
            deadline: g.deadline,
            campaign: g.campaign,
            description: g.description
          })));
        }
        
        if (settingsData) {
          setSettings({
            organizationName: settingsData.organization_name || 'Non-Profit Organization',
            taxId: settingsData.tax_id || '',
            address: settingsData.address || '',
            phone: settingsData.phone || '',
            email: settingsData.email || '',
            website: settingsData.website || '',
            logo: settingsData.logo || '',
            campaigns: settingsData.campaigns || ['General Fund'],
            annualFundGoals: settingsData.annual_fund_goals || {
              'Maintenance': 5000,
              'Security': 3000,
              'Electricity': 6000,
              'Water': 2000,
              'Gas': 2500,
              'Landscaping': 1500,
              'Phone/Internet': 1800,
              'Trash': 1200,
              'Supplies': 2000,
              'Activities': 3000,
              'Insurance': 4000
            },
            expansionGoal: settingsData.expansion_goal || 500000,
            expansionCashOnHand: settingsData.expansion_cash_on_hand || 0
          });
        }
        
        if (usersData) {
          setUsers(usersData.map(u => ({
            id: u.id,
            username: u.username,
            password: u.password_hash,
            name: u.name,
            email: u.email,
            role: u.role,
            status: u.status,
            createdAt: u.created_at,
            createdBy: u.created_by
          })));
        }
        
        if (activityData) {
          setActivityLog(activityData.map(a => ({
            id: a.id,
            userId: a.user_id,
            userName: a.user_name,
            userRole: a.user_role,
            action: a.action,
            details: a.details,
            targetType: a.target_type,
            targetId: a.target_id,
            timestamp: a.created_at,
            ipAddress: a.ip_address
          })));
        }
        
        if (bankDepositsData) {
          setBankDeposits(bankDepositsData.map(d => ({
            id: d.id,
            date: d.date,
            type: d.type,
            amount: parseFloat(d.amount) || 0,
            reference: d.reference || '',
            description: d.description || '',
            recorded_by: d.recorded_by || ''
          })));
        }

      } catch (error) {
        console.error('Error loading data from Supabase:', error);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Log activity function (saves to Supabase)
  const logActivity = async (action, details, targetType = null, targetId = null) => {
    if (!currentUser) return;
    
    const activity = {
      user_id: currentUser.id,
      user_name: currentUser.name,
      user_role: currentUser.role,
      action,
      details,
      target_type: targetType,
      target_id: targetId,
      ip_address: 'web-client'
    };
    
    const { data, error } = await supabase
      .from('activity_log')
      .insert([activity])
      .select()
      .single();
    
    if (data && !error) {
      setActivityLog(prev => [{
        id: data.id,
        userId: data.user_id,
        userName: data.user_name,
        userRole: data.user_role,
        action: data.action,
        details: data.details,
        targetType: data.target_type,
        targetId: data.target_id,
        timestamp: data.created_at,
        ipAddress: data.ip_address
      }, ...prev]);
    }
  };

  // Authentication handlers
  const handleLogin = async (username, password) => {
    try {
      const hashedPassword = hashPassword(password);
      
      // Find user in local state (already loaded from Supabase)
      const user = users.find(u => u.username === username && u.password === hashedPassword && u.status === 'active');
      
      if (user) {
        setCurrentUser(user);
        setIsAuthenticated(true);
        
        // Save session to localStorage (expires in 8 hours)
        try {
          const session = {
            user,
            expiry: Date.now() + (8 * 60 * 60 * 1000)
          };
          localStorage.setItem('donation-session', JSON.stringify(session));
        } catch (storageError) {
          // localStorage might not be available (private browsing, etc.)
          console.warn('Could not save session to localStorage:', storageError);
        }
        
        // Log login activity to Supabase
        try {
          await supabase.from('activity_log').insert([{
            user_id: user.id,
            user_name: user.name,
            user_role: user.role,
            action: 'LOGIN',
            details: 'User logged in successfully',
            ip_address: 'web-client'
          }]);
        } catch (logError) {
          console.warn('Could not log activity:', logError);
        }
        
        return { success: true };
      }
      
      return { success: false, error: 'Invalid username or password' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  // Send email via server API (Nodemailer)
  const sendEmail = async (toEmail, toName, subject, body) => {
    // Check if recipient email is provided
    if (!toEmail || toEmail.trim() === '') {
      setEmailStatus({ show: true, success: false, message: 'No email address found for this donor. Please add an email in the Donors section.' });
      setTimeout(() => setEmailStatus({ show: false, success: false, message: '' }), 4000);
      return { success: false, error: 'No recipient email' };
    }
    
    try {
      setEmailStatus({ show: true, success: false, message: 'Sending email...' });
      
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: toEmail,
          toName: toName,
          subject: subject,
          body: body,
          replyTo: settings.email
        }),
      });
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Email API not available. Please check server configuration.');
      }
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setEmailStatus({ show: true, success: true, message: `Email sent successfully to ${toEmail}!` });
        
        // Auto-hide after 3 seconds
        setTimeout(() => setEmailStatus({ show: false, success: false, message: '' }), 3000);
        
        // Log the activity
        await logActivity('SEND_EMAIL', `Email sent to ${toName} (${toEmail})`, 'email');
        
        return { success: true };
      } else {
        throw new Error(data.error || data.details || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      
      // Show error and offer mailto fallback
      setEmailStatus({ 
        show: true, 
        success: false, 
        message: `${error.message}. Opening email client...` 
      });
      
      // Fallback to mailto after showing error
      setTimeout(() => {
        setEmailStatus({ show: false, success: false, message: '' });
        const mailtoLink = `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailtoLink, '_blank');
      }, 2000);
      
      return { success: false, error: error.message };
    }
  };

  const handleLogout = async () => {
    if (currentUser) {
      await supabase.from('activity_log').insert([{
        user_id: currentUser.id,
        user_name: currentUser.name,
        user_role: currentUser.role,
        action: 'LOGOUT',
        details: 'User logged out',
        ip_address: 'web-client'
      }]);
    }
    setCurrentUser(null);
    setIsAuthenticated(false);
    setActiveTab('dashboard');
    localStorage.removeItem('donation-session');
  };

  // User management handlers (admin only)
  const handleCreateUser = async (userData) => {
    if (currentUser?.role !== 'admin') return;
    
    const { data: newUser, error } = await supabase
      .from('users')
      .insert([{
        username: userData.username,
        password_hash: hashPassword(userData.password),
        name: userData.name,
        email: userData.email,
        role: userData.role,
        status: 'active',
        created_by: currentUser.id
      }])
      .select()
      .single();
    
    if (newUser && !error) {
      setUsers(prev => [...prev, {
        id: newUser.id,
        username: newUser.username,
        password: newUser.password_hash,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        status: newUser.status,
        createdAt: newUser.created_at,
        createdBy: newUser.created_by
      }]);
      await logActivity('CREATE_USER', `Created new user: ${userData.username}`, 'user', newUser.id);
    }
    setShowUserModal(false);
    setEditingUser(null);
  };

  const handleUpdateUser = async (userId, userData) => {
    if (currentUser?.role !== 'admin') return;
    
    const updateData = {
      username: userData.username,
      name: userData.name,
      email: userData.email,
      role: userData.role
    };
    
    if (userData.newPassword) {
      updateData.password_hash = hashPassword(userData.newPassword);
    }
    
    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);
    
    if (!error) {
      setUsers(prev => prev.map(u => {
        if (u.id === userId) {
          const updated = { ...u, ...userData };
          if (userData.newPassword) {
            updated.password = hashPassword(userData.newPassword);
          }
          return updated;
        }
        return u;
      }));
      await logActivity('UPDATE_USER', `Updated user: ${userData.username}`, 'user', userId);
    }
    setShowUserModal(false);
    setEditingUser(null);
  };

  const handleToggleUserStatus = async (userId) => {
    if (currentUser?.role !== 'admin' || userId === '00000000-0000-0000-0000-000000000001') return;
    
    const user = users.find(u => u.id === userId);
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    
    const { error } = await supabase
      .from('users')
      .update({ status: newStatus })
      .eq('id', userId);
    
    if (!error) {
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, status: newStatus } : u
      ));
      await logActivity('TOGGLE_USER_STATUS', `${newStatus === 'active' ? 'Activated' : 'Deactivated'} user: ${user.username}`, 'user', userId);
    }
  };

  const handleChangePassword = async (currentPassword, newPassword) => {
    if (hashPassword(currentPassword) !== currentUser.password) {
      return { success: false, error: 'Current password is incorrect' };
    }
    
    const { error } = await supabase
      .from('users')
      .update({ password_hash: hashPassword(newPassword) })
      .eq('id', currentUser.id);
    
    if (!error) {
      setUsers(prev => prev.map(u => 
        u.id === currentUser.id ? { ...u, password: hashPassword(newPassword) } : u
      ));
      setCurrentUser(prev => ({ ...prev, password: hashPassword(newPassword) }));
      await logActivity('CHANGE_PASSWORD', 'Changed own password');
      return { success: true };
    }
    
    return { success: false, error: 'Failed to update password' };
  };

  // Handle forgot password / reset password
  const handleResetPassword = async (username, email, newPassword) => {
    // Find user by username and email
    const user = users.find(u => 
      u.username.toLowerCase() === username.toLowerCase() && 
      u.email.toLowerCase() === email.toLowerCase()
    );
    
    if (!user) {
      return { success: false, error: 'No account found with that username and email combination' };
    }
    
    if (user.status !== 'active') {
      return { success: false, error: 'This account is deactivated. Please contact an administrator.' };
    }
    
    // Update password in database
    const { error } = await supabase
      .from('users')
      .update({ password_hash: hashPassword(newPassword) })
      .eq('id', user.id);
    
    if (!error) {
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, password: hashPassword(newPassword) } : u
      ));
      return { success: true, message: 'Password reset successfully. You can now sign in with your new password.' };
    }
    
    return { success: false, error: 'Failed to reset password. Please try again.' };
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    const weeklyDonations = donations.filter(d => new Date(d.date) >= startOfWeek);
    const monthlyDonations = donations.filter(d => new Date(d.date) >= startOfMonth);
    const yearlyDonations = donations.filter(d => new Date(d.date) >= startOfYear);

    const weeklyTotal = weeklyDonations.reduce((sum, d) => sum + d.amount, 0);
    const monthlyTotal = monthlyDonations.reduce((sum, d) => sum + d.amount, 0);
    const yearlyTotal = yearlyDonations.reduce((sum, d) => sum + d.amount, 0);
    const totalDonations = donations.reduce((sum, d) => sum + d.amount, 0);

    const activePledges = pledges.filter(p => p.status === 'active');
    const totalPledged = pledges.reduce((sum, p) => sum + p.totalAmount, 0);
    const totalPledgePaid = pledges.reduce((sum, p) => sum + p.paidAmount, 0);

    // Calculate monthly data for the last 12 months
    const monthlyChartData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthDonations = donations.filter(d => {
        const donationDate = new Date(d.date);
        return donationDate >= monthStart && donationDate <= monthEnd;
      });
      
      const monthTotal = monthDonations.reduce((sum, d) => sum + d.amount, 0);
      
      monthlyChartData.push({
        month: monthNames[date.getMonth()],
        year: date.getFullYear(),
        amount: monthTotal,
        count: monthDonations.length
      });
    }

    return {
      weeklyTotal,
      monthlyTotal,
      yearlyTotal,
      totalDonations,
      donorCount: donors.length,
      donationCount: donations.length,
      activePledgeCount: activePledges.length,
      totalPledged,
      totalPledgePaid,
      pledgeProgress: totalPledged > 0 ? (totalPledgePaid / totalPledged) * 100 : 0,
      monthlyChartData
    };
  }, [donations, donors, pledges]);

  // Navigation tabs
  const baseTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'annual-fund', label: 'CIC Expenses Portal', icon: Landmark },
    { id: 'expansion-portal', label: 'CIC Expansion Portal', icon: Building },
    { id: 'bank-activity', label: 'Bank Activity', icon: DollarSign },
    { id: 'pledges', label: 'Pledges', icon: Calendar },
    { id: 'donors', label: 'Donors', icon: Users },
    { id: 'goals', label: 'Goals', icon: Target },
    { id: 'reports', label: 'Reports', icon: FileText },
  ];
  
  // Admin submenu tabs
  const adminMenuTabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'users', label: 'Users', icon: UserCheck },
    { id: 'activity', label: 'Activity Log', icon: Activity },
  ];
  
  const tabs = baseTabs;

  // Handle donation submission
  const handleDonationSubmit = async (donationData) => {
    if (editingItem) {
      // Update existing donation in Supabase
      const { error } = await supabase
        .from('donations')
        .update({
          donor_id: donationData.donorId,
          donor_name: donationData.donorName,
          amount: donationData.amount,
          date: donationData.date,
          type: donationData.type,
          campaign: donationData.campaign,
          method: donationData.method,
          notes: donationData.notes
        })
        .eq('id', editingItem.id);
      
      if (!error) {
        setDonations(donations.map(d => d.id === editingItem.id ? { ...donationData, id: editingItem.id } : d));
        await logActivity('UPDATE_DONATION', `Updated donation: ${formatCurrency(donationData.amount)} from ${donationData.donorName}`, 'donation', editingItem.id);
      }
    } else {
      // Create new donation in Supabase
      const { data: newDonation, error } = await supabase
        .from('donations')
        .insert([{
          donor_id: donationData.donorId,
          donor_name: donationData.donorName,
          amount: donationData.amount,
          date: donationData.date,
          type: donationData.type,
          campaign: donationData.campaign,
          method: donationData.method,
          notes: donationData.notes,
          created_by: currentUser?.id
        }])
        .select()
        .single();
      
      if (newDonation && !error) {
        setDonations([{
          id: newDonation.id,
          donorId: newDonation.donor_id,
          donorName: newDonation.donor_name,
          amount: parseFloat(newDonation.amount),
          date: newDonation.date,
          type: newDonation.type,
          campaign: newDonation.campaign,
          method: newDonation.method,
          notes: newDonation.notes
        }, ...donations]);
        await logActivity('CREATE_DONATION', `Added donation: ${formatCurrency(donationData.amount)} from ${donationData.donorName}`, 'donation', newDonation.id);
        
        // Update donor stats in Supabase
        const donor = donors.find(d => d.id === donationData.donorId);
        if (donor) {
          await supabase
            .from('donors')
            .update({
              total_donated: donor.totalDonated + donationData.amount,
              donation_count: donor.donationCount + 1
            })
            .eq('id', donor.id);
          
          setDonors(donors.map(d => d.id === donor.id ? {
            ...d,
            totalDonated: d.totalDonated + donationData.amount,
            donationCount: d.donationCount + 1
          } : d));
        }
        
        // Update goal progress in Supabase
        const goal = goals.find(g => g.campaign === donationData.campaign);
        if (goal) {
          await supabase
            .from('goals')
            .update({ current: goal.current + donationData.amount })
            .eq('id', goal.id);
          
          setGoals(goals.map(g => g.id === goal.id ? { ...g, current: g.current + donationData.amount } : g));
        }
      }
    }
    setShowDonationModal(false);
    setEditingItem(null);
  };

  // Handle donation box submission (anonymous/box donations)
  const handleDonationBoxSubmit = async (cashAmount, checkAmount, date) => {
    const donationDate = date || new Date().toISOString().split('T')[0];
    const newDonations = [];
    let totalAmount = 0;

    // Add cash donation if amount > 0
    if (cashAmount > 0) {
      const { data: cashDonation, error: cashError } = await supabase
        .from('donations')
        .insert([{
          donor_id: null,
          donor_name: 'Donation Box',
          amount: cashAmount,
          date: donationDate,
          type: 'one-time',
          campaign: donationBoxCampaign,
          method: 'Cash',
          notes: 'Donation Box Collection - Cash',
          created_by: currentUser?.id
        }])
        .select()
        .single();
      
      if (cashDonation && !cashError) {
        newDonations.push({
          id: cashDonation.id,
          donorId: cashDonation.donor_id,
          donorName: cashDonation.donor_name,
          amount: parseFloat(cashDonation.amount),
          date: cashDonation.date,
          type: cashDonation.type,
          campaign: cashDonation.campaign,
          method: cashDonation.method,
          notes: cashDonation.notes
        });
        totalAmount += cashAmount;
        await logActivity('CREATE_DONATION', `Added donation box (Cash): ${formatCurrency(cashAmount)} to ${donationBoxCampaign}`, 'donation', cashDonation.id);
      }
    }

    // Add check donation if amount > 0
    if (checkAmount > 0) {
      const { data: checkDonation, error: checkError } = await supabase
        .from('donations')
        .insert([{
          donor_id: null,
          donor_name: 'Donation Box',
          amount: checkAmount,
          date: donationDate,
          type: 'one-time',
          campaign: donationBoxCampaign,
          method: 'Check',
          notes: 'Donation Box Collection - Check',
          created_by: currentUser?.id
        }])
        .select()
        .single();
      
      if (checkDonation && !checkError) {
        newDonations.push({
          id: checkDonation.id,
          donorId: checkDonation.donor_id,
          donorName: checkDonation.donor_name,
          amount: parseFloat(checkDonation.amount),
          date: checkDonation.date,
          type: checkDonation.type,
          campaign: checkDonation.campaign,
          method: checkDonation.method,
          notes: checkDonation.notes
        });
        totalAmount += checkAmount;
        await logActivity('CREATE_DONATION', `Added donation box (Check): ${formatCurrency(checkAmount)} to ${donationBoxCampaign}`, 'donation', checkDonation.id);
      }
    }

    // Update donations list
    if (newDonations.length > 0) {
      setDonations([...newDonations, ...donations]);
      
      // Update goal progress
      const goal = goals.find(g => g.campaign === donationBoxCampaign);
      if (goal && totalAmount > 0) {
        await supabase
          .from('goals')
          .update({ current: goal.current + totalAmount })
          .eq('id', goal.id);
        
        setGoals(goals.map(g => g.id === goal.id ? { ...g, current: g.current + totalAmount } : g));
      }
    }
    
    setShowDonationBoxModal(false);
    setDonationBoxCampaign('');
  };

  // Handle annual fund submission
  const handleAnnualFundSubmit = async (cashAmount, checkAmount, date, isPaidInFull = false) => {
    const donationDate = date || new Date().toISOString().split('T')[0];
    const newDonations = [];
    let totalAmount = 0;
    const paidInFullTag = isPaidInFull ? ' [PAID IN FULL]' : '';

    // Add cash donation if amount > 0
    if (cashAmount > 0) {
      const { data: cashDonation, error: cashError } = await supabase
        .from('donations')
        .insert([{
          donor_id: null,
          donor_name: isPaidInFull ? 'Annual Fund - Paid in Full' : 'Annual Fund',
          amount: cashAmount,
          date: donationDate,
          type: 'annual-fund',
          campaign: annualFundCategory,
          method: 'Cash',
          notes: `Annual Fund Raising - ${annualFundCategory} (Cash)${paidInFullTag}`,
          created_by: currentUser?.id
        }])
        .select()
        .single();
      
      if (cashDonation && !cashError) {
        newDonations.push({
          id: cashDonation.id,
          donorId: cashDonation.donor_id,
          donorName: cashDonation.donor_name,
          amount: parseFloat(cashDonation.amount),
          date: cashDonation.date,
          type: cashDonation.type,
          campaign: cashDonation.campaign,
          method: cashDonation.method,
          notes: cashDonation.notes
        });
        totalAmount += cashAmount;
        await logActivity('CREATE_DONATION', `Added annual fund (Cash)${paidInFullTag}: ${formatCurrency(cashAmount)} to ${annualFundCategory}`, 'donation', cashDonation.id);
      }
    }

    // Add check donation if amount > 0
    if (checkAmount > 0) {
      const { data: checkDonation, error: checkError } = await supabase
        .from('donations')
        .insert([{
          donor_id: null,
          donor_name: isPaidInFull ? 'Annual Fund - Paid in Full' : 'Annual Fund',
          amount: checkAmount,
          date: donationDate,
          type: 'annual-fund',
          campaign: annualFundCategory,
          method: 'Check',
          notes: `Annual Fund Raising - ${annualFundCategory} (Check)${paidInFullTag}`,
          created_by: currentUser?.id
        }])
        .select()
        .single();
      
      if (checkDonation && !checkError) {
        newDonations.push({
          id: checkDonation.id,
          donorId: checkDonation.donor_id,
          donorName: checkDonation.donor_name,
          amount: parseFloat(checkDonation.amount),
          date: checkDonation.date,
          type: checkDonation.type,
          campaign: checkDonation.campaign,
          method: checkDonation.method,
          notes: checkDonation.notes
        });
        totalAmount += checkAmount;
        await logActivity('CREATE_DONATION', `Added annual fund (Check)${paidInFullTag}: ${formatCurrency(checkAmount)} to ${annualFundCategory}`, 'donation', checkDonation.id);
      }
    }

    // Update donations list
    if (newDonations.length > 0) {
      setDonations([...newDonations, ...donations]);
    }
    
    setShowAnnualFundModal(false);
    setAnnualFundCategory('');
    setAnnualFundPaymentMode('one-time');
    setMonthlyPledgePayments(Array(12).fill(false));
    setMonthlyPledgeAmount('');
    setShowExistingPayments(false);
  };

  // Handle annual fund goal update
  const handleUpdateAnnualFundGoal = async (category, newGoal) => {
    const updatedGoals = {
      ...settings.annualFundGoals,
      [category]: newGoal
    };
    
    const updatedSettings = {
      ...settings,
      annualFundGoals: updatedGoals
    };
    
    // Save to Supabase
    const { error } = await supabase
      .from('settings')
      .update({ 
        annual_fund_goals: updatedGoals,
        updated_at: new Date().toISOString()
      })
      .eq('id', 1);
    
    if (!error) {
      setSettings(updatedSettings);
      await logActivity('UPDATE_SETTINGS', `Updated annual fund goal for ${category} to ${formatCurrency(newGoal)}`, 'settings');
    }
    
    setEditingGoalCategory(null);
    setEditingGoalAmount('');
  };

  // Handle delete annual fund payment
  const handleDeleteAnnualFundPayment = async (donationId) => {
    const donation = donations.find(d => d.id === donationId);
    if (confirm(`Are you sure you want to delete this payment of ${formatCurrency(donation?.amount)}?`)) {
      const { error } = await supabase
        .from('donations')
        .delete()
        .eq('id', donationId);
      
      if (!error) {
        setDonations(donations.filter(d => d.id !== donationId));
        await logActivity('DELETE_DONATION', `Deleted annual fund payment: ${formatCurrency(donation?.amount)} from ${donation?.campaign}`, 'donation', donationId);
      }
    }
  };

  // Handle pledge submission
  const handlePledgeSubmit = async (pledgeData) => {
    if (editingItem) {
      const { error } = await supabase
        .from('pledges')
        .update({
          donor_id: pledgeData.donorId,
          donor_name: pledgeData.donorName,
          donor_email: pledgeData.donorEmail,
          total_amount: pledgeData.totalAmount,
          start_date: pledgeData.startDate,
          end_date: pledgeData.endDate,
          frequency: pledgeData.frequency,
          campaign: pledgeData.campaign,
          status: pledgeData.status,
          responsible: pledgeData.responsible,
          pledge_form: pledgeData.pledgeForm,
          pledge_notes: pledgeData.pledgeNotes
        })
        .eq('id', editingItem.id);
      
      if (!error) {
        setPledges(pledges.map(p => p.id === editingItem.id ? { ...pledgeData, id: editingItem.id, payments: editingItem.payments } : p));
        await logActivity('UPDATE_PLEDGE', `Updated pledge for ${pledgeData.donorName}`, 'pledge', editingItem.id);
      }
    } else {
      const { data: newPledge, error } = await supabase
        .from('pledges')
        .insert([{
          donor_id: pledgeData.donorId,
          donor_name: pledgeData.donorName,
          donor_email: pledgeData.donorEmail,
          total_amount: pledgeData.totalAmount,
          paid_amount: 0,
          start_date: pledgeData.startDate,
          end_date: pledgeData.endDate,
          frequency: pledgeData.frequency,
          campaign: pledgeData.campaign,
          status: 'pending',
          created_by: currentUser?.id,
          responsible: pledgeData.responsible,
          pledge_form: pledgeData.pledgeForm,
          pledge_notes: pledgeData.pledgeNotes
        }])
        .select()
        .single();
      
      if (newPledge && !error) {
        setPledges([{
          id: newPledge.id,
          donorId: newPledge.donor_id,
          donorName: newPledge.donor_name,
          donorEmail: newPledge.donor_email || '',
          totalAmount: parseFloat(newPledge.total_amount),
          paidAmount: 0,
          startDate: newPledge.start_date,
          endDate: newPledge.end_date,
          frequency: newPledge.frequency,
          campaign: newPledge.campaign,
          status: newPledge.status,
          responsible: newPledge.responsible || '',
          pledgeForm: newPledge.pledge_form || 'N',
          pledgeNotes: newPledge.pledge_notes || '',
          payments: []
        }, ...pledges]);
        await logActivity('CREATE_PLEDGE', `Created pledge: ${formatCurrency(pledgeData.totalAmount)} from ${pledgeData.donorName}`, 'pledge', newPledge.id);
      }
    }
    setShowPledgeModal(false);
    setEditingItem(null);
  };

  // Handle pledge payment
  const handlePledgePayment = async (pledgeId, paymentAmount, paymentMethod = 'Cash', checkDetails = null) => {
    const pledge = pledges.find(p => p.id === pledgeId);
    const donor = donors.find(d => d.id === pledge?.donorId);
    
    const newPaidAmount = (pledge.paidAmount || 0) + paymentAmount;
    const newRemainingBalance = pledge.totalAmount - newPaidAmount;
    const newStatus = newPaidAmount >= pledge.totalAmount ? 'completed' : 'active';
    const paymentDate = new Date().toISOString().split('T')[0];
    
    // Build notes with check details if applicable
    let paymentNotes = `Payment for pledge #${pledgeId}`;
    if (checkDetails) {
      paymentNotes += ` | Check #${checkDetails.checkNumber} | Bank: ${checkDetails.bankName}`;
    }
    
    // Insert payment record in Supabase
    await supabase
      .from('pledge_payments')
      .insert([{
        pledge_id: pledgeId,
        amount: paymentAmount,
        date: paymentDate,
        method: paymentMethod,
        check_number: checkDetails?.checkNumber || null,
        bank_name: checkDetails?.bankName || null
      }]);
    
    // Update pledge in Supabase
    await supabase
      .from('pledges')
      .update({
        paid_amount: newPaidAmount,
        status: newStatus
      })
      .eq('id', pledgeId);
    
    // Update local pledge state
    setPledges(pledges.map(p => {
      if (p.id === pledgeId) {
        return {
          ...p,
          paidAmount: newPaidAmount,
          status: newStatus,
          payments: [...(p.payments || []), { 
            date: paymentDate, 
            amount: paymentAmount, 
            method: paymentMethod,
            checkNumber: checkDetails?.checkNumber,
            bankName: checkDetails?.bankName
          }]
        };
      }
      return p;
    }));
    
    // Also record as a donation in Supabase
    const { data: newDonation } = await supabase
      .from('donations')
      .insert([{
        donor_id: pledge.donorId,
        donor_name: pledge.donorName,
        amount: paymentAmount,
        date: paymentDate,
        type: 'pledge-payment',
        campaign: pledge.campaign,
        method: paymentMethod,
        notes: paymentNotes,
        created_by: currentUser?.id
      }])
      .select()
      .single();
    
    if (newDonation) {
      setDonations([{
        id: newDonation.id,
        donorId: newDonation.donor_id,
        donorName: newDonation.donor_name,
        amount: parseFloat(newDonation.amount),
        date: newDonation.date,
        type: newDonation.type,
        campaign: newDonation.campaign,
        method: newDonation.method,
        notes: newDonation.notes
      }, ...donations]);
      
      // Update goal progress for the pledge's campaign
      const goal = goals.find(g => g.campaign === pledge.campaign);
      if (goal) {
        await supabase
          .from('goals')
          .update({ current: goal.current + paymentAmount })
          .eq('id', goal.id);
        
        setGoals(goals.map(g => g.id === goal.id ? { ...g, current: g.current + paymentAmount } : g));
      }
    }
    
    const logDetails = checkDetails 
      ? `Recorded pledge payment: ${formatCurrency(paymentAmount)} from ${pledge.donorName} via ${paymentMethod} (Check #${checkDetails.checkNumber}, ${checkDetails.bankName})`
      : `Recorded pledge payment: ${formatCurrency(paymentAmount)} from ${pledge.donorName} via ${paymentMethod}`;
    await logActivity('RECORD_PLEDGE_PAYMENT', logDetails, 'pledge', pledgeId);
    
    // Prepare receipt data
    setPledgePaymentReceipt({
      pledge: pledge,
      donor: donor,
      paymentAmount: paymentAmount,
      paymentMethod: paymentMethod,
      checkDetails: checkDetails,
      newPaidAmount: newPaidAmount,
      remainingBalance: newRemainingBalance,
      paymentDate: paymentDate,
      receiptId: newDonation?.id || generateId()
    });
    
    setShowPledgePaymentModal(false);
    setSelectedPledge(null);
    setShowPledgeReceiptModal(true);
  };

  // Handle goal submission
  const handleGoalSubmit = async (goalData) => {
    if (editingItem) {
      const { error } = await supabase
        .from('goals')
        .update({
          name: goalData.name,
          target: goalData.target,
          deadline: goalData.deadline,
          campaign: goalData.campaign,
          description: goalData.description
        })
        .eq('id', editingItem.id);
      
      if (!error) {
        setGoals(goals.map(g => g.id === editingItem.id ? { ...goalData, id: editingItem.id, current: editingItem.current } : g));
        await logActivity('UPDATE_GOAL', `Updated goal: ${goalData.name}`, 'goal', editingItem.id);
      }
    } else {
      const { data: newGoal, error } = await supabase
        .from('goals')
        .insert([{
          name: goalData.name,
          target: goalData.target,
          current: 0,
          deadline: goalData.deadline,
          campaign: goalData.campaign,
          description: goalData.description
        }])
        .select()
        .single();
      
      if (newGoal && !error) {
        setGoals([{
          id: newGoal.id,
          name: newGoal.name,
          target: parseFloat(newGoal.target),
          current: 0,
          deadline: newGoal.deadline,
          campaign: newGoal.campaign,
          description: newGoal.description
        }, ...goals]);
        await logActivity('CREATE_GOAL', `Created goal: ${goalData.name}`, 'goal', newGoal.id);
      }
    }
    setShowGoalModal(false);
    setEditingItem(null);
  };

  // Handle donor submission
  const handleDonorSubmit = async (donorData) => {
    if (editingItem) {
      const { error } = await supabase
        .from('donors')
        .update({
          name: donorData.name,
          email: donorData.email,
          phone: donorData.phone,
          address: donorData.address
        })
        .eq('id', editingItem.id);
      
      if (!error) {
        setDonors(donors.map(d => d.id === editingItem.id ? { ...donorData, id: editingItem.id, totalDonated: editingItem.totalDonated, donationCount: editingItem.donationCount } : d));
        await logActivity('UPDATE_DONOR', `Updated donor: ${donorData.name}`, 'donor', editingItem.id);
      }
    } else {
      const { data: newDonor, error } = await supabase
        .from('donors')
        .insert([{
          name: donorData.name,
          email: donorData.email,
          phone: donorData.phone,
          address: donorData.address,
          total_donated: 0,
          donation_count: 0
        }])
        .select()
        .single();
      
      if (newDonor && !error) {
        setDonors([{
          id: newDonor.id,
          name: newDonor.name,
          email: newDonor.email,
          phone: newDonor.phone,
          address: newDonor.address,
          totalDonated: 0,
          donationCount: 0
        }, ...donors]);
        await logActivity('CREATE_DONOR', `Created donor: ${donorData.name}`, 'donor', newDonor.id);
      }
    }
    setShowDonorModal(false);
    setEditingItem(null);
  };

  // Handle settings update
  const handleSettingsUpdate = async (newSettings) => {
    const { error } = await supabase
      .from('settings')
      .update({
        organization_name: newSettings.organizationName,
        tax_id: newSettings.taxId,
        address: newSettings.address,
        phone: newSettings.phone,
        email: newSettings.email,
        website: newSettings.website,
        logo: newSettings.logo,
        campaigns: newSettings.campaigns,
        annual_fund_goals: newSettings.annualFundGoals,
        expansion_goal: newSettings.expansionGoal,
        expansion_cash_on_hand: newSettings.expansionCashOnHand,
        updated_at: new Date().toISOString()
      })
      .eq('id', 1);
    
    if (error) {
      console.error('Settings update error:', error);
      alert('Error updating settings: ' + error.message);
      return;
    }
    
    setSettings(newSettings);
    await logActivity('UPDATE_SETTINGS', 'Updated organization settings');
  };

  // Delete handlers
  const handleDeleteDonation = async (id) => {
    const donation = donations.find(d => d.id === id);
    if (confirm('Are you sure you want to delete this donation?')) {
      const { error } = await supabase
        .from('donations')
        .delete()
        .eq('id', id);
      
      if (!error) {
        setDonations(donations.filter(d => d.id !== id));
        await logActivity('DELETE_DONATION', `Deleted donation: ${formatCurrency(donation.amount)} from ${donation.donorName}`, 'donation', id);
      }
    }
  };

  const handleDeletePledge = async (id) => {
    const pledge = pledges.find(p => p.id === id);
    if (confirm('Are you sure you want to delete this pledge?')) {
      // Delete pledge payments first
      await supabase.from('pledge_payments').delete().eq('pledge_id', id);
      
      const { error } = await supabase
        .from('pledges')
        .delete()
        .eq('id', id);
      
      if (!error) {
        setPledges(pledges.filter(p => p.id !== id));
        await logActivity('DELETE_PLEDGE', `Deleted pledge: ${formatCurrency(pledge.totalAmount)} from ${pledge.donorName}`, 'pledge', id);
      }
    }
  };

  const handleDeleteGoal = async (id) => {
    const goal = goals.find(g => g.id === id);
    if (confirm('Are you sure you want to delete this goal?')) {
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', id);
      
      if (!error) {
        setGoals(goals.filter(g => g.id !== id));
        await logActivity('DELETE_GOAL', `Deleted goal: ${goal.name}`, 'goal', id);
      }
    }
  };

  const handleDeleteDonor = async (id) => {
    const donor = donors.find(d => d.id === id);
    if (confirm('Are you sure you want to delete this donor? This will not delete their donation records.')) {
      const { error } = await supabase
        .from('donors')
        .delete()
        .eq('id', id);
      
      if (!error) {
        setDonors(donors.filter(d => d.id !== id));
        await logActivity('DELETE_DONOR', `Deleted donor: ${donor.name}`, 'donor', id);
      }
    }
  };

  // Print receipt
  const printReceipt = (donation) => {
    setSelectedReceipt(donation);
    setShowReceiptModal(true);
    logActivity('VIEW_RECEIPT', `Viewed receipt for donation: ${formatCurrency(donation.amount)}`, 'donation', donation.id);
  };

  // Filter donations for reports
  const getFilteredDonations = () => {
    let filtered = [...donations];
    
    if (reportDateRange.start) {
      const startDate = reportDateRange.start;
      filtered = filtered.filter(d => {
        if (!d.date) return false;
        const donationDate = String(d.date).split('T')[0]; // Get just YYYY-MM-DD part
        return donationDate >= startDate;
      });
    }
    if (reportDateRange.end) {
      const endDate = reportDateRange.end;
      filtered = filtered.filter(d => {
        if (!d.date) return false;
        const donationDate = String(d.date).split('T')[0]; // Get just YYYY-MM-DD part
        return donationDate <= endDate;
      });
    }
    if (reportType && reportType !== 'all') {
      filtered = filtered.filter(d => d.campaign === reportType);
    }
    
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
  };

  // Export to CSV
  const exportToCSV = () => {
    const filtered = getFilteredDonations();
    const headers = ['Date', 'Donor', 'Amount', 'Campaign', 'Method', 'Type', 'Notes'];
    const rows = filtered.map(d => [
      d.date,
      d.donorName,
      d.amount,
      d.campaign,
      d.method,
      d.type,
      d.notes
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donations-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Load SheetJS library for Excel export
  const loadXLSX = () => {
    return new Promise((resolve, reject) => {
      if (typeof XLSX !== 'undefined') {
        resolve(XLSX);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      script.onload = () => resolve(window.XLSX);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  // Export Donations to Excel
  const exportDonationsToExcel = async () => {
    try {
      const XLSX = await loadXLSX();
      const filtered = getFilteredDonations();
      
      const data = filtered.map(d => ({
        'Date': d.date,
        'Donor': d.donorName,
        'Amount': d.amount,
        'Campaign': d.campaign,
        'Method': d.method,
        'Type': d.type,
        'Notes': d.notes || ''
      }));
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Donations');
      
      // Auto-size columns
      const colWidths = [
        { wch: 12 }, // Date
        { wch: 25 }, // Donor
        { wch: 12 }, // Amount
        { wch: 20 }, // Campaign
        { wch: 15 }, // Method
        { wch: 12 }, // Type
        { wch: 30 }, // Notes
      ];
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, `donations-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Export Donors to CSV
  const exportDonorsToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Address', 'Total Donated', 'Donation Count'];
    const rows = donors.map(d => [
      d.name,
      d.email || '',
      d.phone || '',
      `"${(d.address || '').replace(/"/g, '""')}"`,
      d.totalDonated,
      d.donationCount
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donors-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Export Donors to Excel
  const exportDonorsToExcel = async () => {
    try {
      const XLSX = await loadXLSX();
      
      const data = donors.map(d => ({
        'Name': d.name,
        'Email': d.email || '',
        'Phone': d.phone || '',
        'Address': d.address || '',
        'Total Donated': d.totalDonated,
        'Donation Count': d.donationCount
      }));
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Donors');
      
      ws['!cols'] = [
        { wch: 25 }, // Name
        { wch: 25 }, // Email
        { wch: 15 }, // Phone
        { wch: 35 }, // Address
        { wch: 15 }, // Total Donated
        { wch: 15 }, // Donation Count
      ];
      
      XLSX.writeFile(wb, `donors-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Export Pledges to CSV
  const exportPledgesToCSV = () => {
    const headers = ['Donor', 'Campaign', 'Status', 'Total Amount', 'Paid Amount', 'Balance', 'Start Date', 'End Date', 'Frequency'];
    const rows = pledges.map(p => [
      p.donorName,
      p.campaign || '',
      p.status,
      p.totalAmount,
      p.paidAmount,
      p.totalAmount - p.paidAmount,
      p.startDate,
      p.endDate,
      p.frequency || ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pledges-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Export Pledges to Excel
  const exportPledgesToExcel = async () => {
    try {
      const XLSX = await loadXLSX();
      
      const data = pledges.map(p => ({
        'Donor': p.donorName,
        'Campaign': p.campaign || '',
        'Status': p.status,
        'Total Amount': p.totalAmount,
        'Paid Amount': p.paidAmount,
        'Balance': p.totalAmount - p.paidAmount,
        'Progress %': Math.round((p.paidAmount / p.totalAmount) * 100),
        'Start Date': p.startDate,
        'End Date': p.endDate,
        'Frequency': p.frequency || ''
      }));
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pledges');
      
      ws['!cols'] = [
        { wch: 25 }, // Donor
        { wch: 20 }, // Campaign
        { wch: 12 }, // Status
        { wch: 15 }, // Total Amount
        { wch: 15 }, // Paid Amount
        { wch: 12 }, // Balance
        { wch: 12 }, // Progress %
        { wch: 12 }, // Start Date
        { wch: 12 }, // End Date
        { wch: 12 }, // Frequency
      ];
      
      XLSX.writeFile(wb, `pledges-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Export Full Report to Excel (all data in one file)
  const exportFullReportToExcel = async () => {
    try {
      const XLSX = await loadXLSX();
      const wb = XLSX.utils.book_new();
      
      // Donations Sheet
      const donationsData = getFilteredDonations().map(d => ({
        'Date': d.date,
        'Donor': d.donorName,
        'Amount': d.amount,
        'Campaign': d.campaign,
        'Method': d.method,
        'Type': d.type,
        'Notes': d.notes || ''
      }));
      const wsDonations = XLSX.utils.json_to_sheet(donationsData);
      wsDonations['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, wsDonations, 'Donations');
      
      // Donors Sheet
      const donorsData = donors.map(d => ({
        'Name': d.name,
        'Email': d.email || '',
        'Phone': d.phone || '',
        'Address': d.address || '',
        'Total Donated': d.totalDonated,
        'Donation Count': d.donationCount
      }));
      const wsDonors = XLSX.utils.json_to_sheet(donorsData);
      wsDonors['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 35 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsDonors, 'Donors');
      
      // Pledges Sheet
      const pledgesData = pledges.map(p => ({
        'Donor': p.donorName,
        'Campaign': p.campaign || '',
        'Status': p.status,
        'Total Amount': p.totalAmount,
        'Paid Amount': p.paidAmount,
        'Balance': p.totalAmount - p.paidAmount,
        'Progress %': Math.round((p.paidAmount / p.totalAmount) * 100),
        'Start Date': p.startDate,
        'End Date': p.endDate,
        'Frequency': p.frequency || ''
      }));
      const wsPledges = XLSX.utils.json_to_sheet(pledgesData);
      wsPledges['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsPledges, 'Pledges');
      
      // Summary Sheet
      const summaryData = [
        { 'Metric': 'Total Donations', 'Value': formatCurrency(donations.reduce((sum, d) => sum + d.amount, 0)) },
        { 'Metric': 'Total Donors', 'Value': donors.length },
        { 'Metric': 'Total Pledged', 'Value': formatCurrency(pledges.reduce((sum, p) => sum + p.totalAmount, 0)) },
        { 'Metric': 'Total Collected from Pledges', 'Value': formatCurrency(pledges.reduce((sum, p) => sum + p.paidAmount, 0)) },
        { 'Metric': 'Outstanding Pledges', 'Value': formatCurrency(pledges.reduce((sum, p) => sum + (p.totalAmount - p.paidAmount), 0)) },
        { 'Metric': 'Active Pledges', 'Value': pledges.filter(p => p.status === 'active').length },
        { 'Metric': 'Completed Pledges', 'Value': pledges.filter(p => p.status === 'completed').length },
        { 'Metric': 'Report Generated', 'Value': new Date().toLocaleString() }
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      
      XLSX.writeFile(wb, `full-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Import functions
  const openImportModal = (type) => {
    setImportType(type);
    setImportData([]);
    setImportHeaders([]);
    setImportMapping({});
    setImportStep(1);
    setImportErrors([]);
    setImportSuccess(0);
    setShowImportModal(true);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], data: [] };
    
    const parseRow = (row) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const headers = parseRow(lines[0]);
    const data = lines.slice(1).map(line => {
      const values = parseRow(line);
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });
    
    return { headers, data };
  };

  const parseExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          // Use SheetJS library loaded from CDN
          if (typeof XLSX === 'undefined') {
            // Load XLSX library dynamically
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              const workbook = XLSX.read(data, { type: 'array' });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
              
              if (jsonData.length === 0) {
                resolve({ headers: [], data: [] });
                return;
              }
              
              const headers = jsonData[0].map(h => String(h || '').trim());
              const rows = jsonData.slice(1).map(row => {
                const obj = {};
                headers.forEach((header, index) => {
                  obj[header] = row[index] !== undefined ? String(row[index]).trim() : '';
                });
                return obj;
              }).filter(row => Object.values(row).some(v => v !== ''));
              
              resolve({ headers, data: rows });
            };
            document.head.appendChild(script);
          } else {
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length === 0) {
              resolve({ headers: [], data: [] });
              return;
            }
            
            const headers = jsonData[0].map(h => String(h || '').trim());
            const rows = jsonData.slice(1).map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index] !== undefined ? String(row[index]).trim() : '';
              });
              return obj;
            }).filter(row => Object.values(row).some(v => v !== ''));
            
            resolve({ headers, data: rows });
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      let result;
      if (file.name.endsWith('.csv')) {
        const text = await file.text();
        result = parseCSV(text);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        result = await parseExcel(file);
      } else {
        alert('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      
      if (result.data.length === 0) {
        alert('No data found in file');
        return;
      }
      
      setImportHeaders(result.headers);
      setImportData(result.data);
      
      // Auto-map columns based on common names
      const autoMapping = {};
      const fieldMappings = {
        donors: {
          name: ['name', 'donor name', 'donor', 'full name', 'fullname'],
          email: ['email', 'e-mail', 'email address'],
          phone: ['phone', 'telephone', 'phone number', 'mobile', 'cell'],
          address: ['address', 'street address', 'mailing address', 'location']
        },
        donations: {
          donorName: ['donor', 'donor name', 'name', 'contributor'],
          amount: ['amount', 'donation amount', 'value', 'sum', 'total'],
          date: ['date', 'donation date', 'payment date', 'received date'],
          campaign: ['campaign', 'fund', 'purpose', 'designation'],
          method: ['method', 'payment method', 'payment type', 'type of payment'],
          type: ['type', 'donation type', 'frequency'],
          notes: ['notes', 'comments', 'memo', 'description']
        },
        pledges: {
          donorName: ['donor', 'donor name', 'name', 'pledger'],
          totalAmount: ['amount', 'pledge amount', 'total', 'total amount', 'pledged'],
          startDate: ['start date', 'start', 'begin date', 'from'],
          endDate: ['end date', 'end', 'due date', 'to'],
          frequency: ['frequency', 'schedule', 'payment frequency'],
          campaign: ['campaign', 'fund', 'purpose', 'designation']
        }
      };
      
      const mappings = fieldMappings[importType] || {};
      result.headers.forEach(header => {
        const lowerHeader = header.toLowerCase();
        Object.entries(mappings).forEach(([field, aliases]) => {
          if (aliases.some(alias => lowerHeader.includes(alias))) {
            if (!autoMapping[field]) {
              autoMapping[field] = header;
            }
          }
        });
      });
      
      setImportMapping(autoMapping);
      setImportStep(2);
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error parsing file. Please check the format and try again.');
    }
  };

  const getRequiredFields = () => {
    switch (importType) {
      case 'donors':
        return [
          { key: 'name', label: 'Name', required: true },
          { key: 'email', label: 'Email', required: false },
          { key: 'phone', label: 'Phone', required: false },
          { key: 'address', label: 'Address', required: false }
        ];
      case 'donations':
        return [
          { key: 'donorName', label: 'Donor Name', required: true },
          { key: 'amount', label: 'Amount', required: true },
          { key: 'date', label: 'Date', required: true },
          { key: 'campaign', label: 'Campaign', required: false },
          { key: 'method', label: 'Payment Method', required: false },
          { key: 'type', label: 'Type', required: false },
          { key: 'notes', label: 'Notes', required: false }
        ];
      case 'pledges':
        return [
          { key: 'donorName', label: 'Donor Name', required: true },
          { key: 'totalAmount', label: 'Total Amount', required: true },
          { key: 'startDate', label: 'Start Date', required: true },
          { key: 'endDate', label: 'End Date', required: true },
          { key: 'frequency', label: 'Frequency', required: false },
          { key: 'campaign', label: 'Campaign', required: false }
        ];
      default:
        return [];
    }
  };

  const validateImportData = () => {
    const errors = [];
    const requiredFields = getRequiredFields().filter(f => f.required);
    
    // Check required mappings
    requiredFields.forEach(field => {
      if (!importMapping[field.key]) {
        errors.push(`Required field "${field.label}" is not mapped`);
      }
    });
    
    if (errors.length > 0) {
      setImportErrors(errors);
      return false;
    }
    
    // Validate data rows
    importData.forEach((row, index) => {
      requiredFields.forEach(field => {
        const value = row[importMapping[field.key]];
        if (!value || value.trim() === '') {
          errors.push(`Row ${index + 1}: Missing ${field.label}`);
        }
      });
      
      // Validate amount is a number
      if (importType === 'donations' || importType === 'pledges') {
        const amountField = importType === 'pledges' ? 'totalAmount' : 'amount';
        const amount = row[importMapping[amountField]];
        if (amount && isNaN(parseFloat(amount.replace(/[$,]/g, '')))) {
          errors.push(`Row ${index + 1}: Invalid amount "${amount}"`);
        }
      }
      
      // Validate date format
      if (importType === 'donations' && importMapping.date) {
        const date = row[importMapping.date];
        if (date && isNaN(Date.parse(date))) {
          errors.push(`Row ${index + 1}: Invalid date "${date}"`);
        }
      }
    });
    
    // Limit errors shown
    if (errors.length > 10) {
      errors.splice(10);
      errors.push('... and more errors');
    }
    
    setImportErrors(errors);
    return errors.length === 0;
  };

  const processImport = async () => {
    if (!validateImportData()) {
      return;
    }
    
    setImportStep(4);
    setImportErrors([]);
    let successCount = 0;
    const errors = [];
    
    for (let i = 0; i < importData.length; i++) {
      const row = importData[i];
      
      try {
        if (importType === 'donors') {
          const donorData = {
            name: row[importMapping.name]?.trim() || '',
            email: row[importMapping.email]?.trim() || '',
            phone: row[importMapping.phone]?.trim() || '',
            address: row[importMapping.address]?.trim() || '',
            total_donated: 0,
            donation_count: 0
          };
          
          const { data: newDonor, error } = await supabase
            .from('donors')
            .insert([donorData])
            .select()
            .single();
          
          if (error) throw error;
          
          if (newDonor) {
            setDonors(prev => [...prev, {
              id: newDonor.id,
              name: newDonor.name,
              email: newDonor.email,
              phone: newDonor.phone,
              address: newDonor.address,
              totalDonated: 0,
              donationCount: 0
            }]);
            successCount++;
          }
        } else if (importType === 'donations') {
          const amount = parseFloat((row[importMapping.amount] || '0').replace(/[$,]/g, ''));
          const donorName = row[importMapping.donorName]?.trim() || '';
          
          // Find or create donor
          let donor = donors.find(d => d.name.toLowerCase() === donorName.toLowerCase());
          
          const donationData = {
            donor_id: donor?.id || null,
            donor_name: donorName,
            amount: amount,
            date: row[importMapping.date] || new Date().toISOString().split('T')[0],
            campaign: row[importMapping.campaign]?.trim() || settings.campaigns[0] || 'General Fund',
            method: row[importMapping.method]?.trim() || 'Other',
            type: row[importMapping.type]?.trim() || 'one-time',
            notes: row[importMapping.notes]?.trim() || '',
            created_by: currentUser?.id
          };
          
          const { data: newDonation, error } = await supabase
            .from('donations')
            .insert([donationData])
            .select()
            .single();
          
          if (error) throw error;
          
          if (newDonation) {
            setDonations(prev => [...prev, {
              id: newDonation.id,
              donorId: newDonation.donor_id,
              donorName: newDonation.donor_name,
              amount: parseFloat(newDonation.amount),
              date: newDonation.date,
              type: newDonation.type,
              campaign: newDonation.campaign,
              method: newDonation.method,
              notes: newDonation.notes
            }]);
            successCount++;
          }
        } else if (importType === 'pledges') {
          const totalAmount = parseFloat((row[importMapping.totalAmount] || '0').replace(/[$,]/g, ''));
          const donorName = row[importMapping.donorName]?.trim() || '';
          
          let donor = donors.find(d => d.name.toLowerCase() === donorName.toLowerCase());
          
          const pledgeData = {
            donor_id: donor?.id || null,
            donor_name: donorName,
            total_amount: totalAmount,
            paid_amount: 0,
            start_date: row[importMapping.startDate] || new Date().toISOString().split('T')[0],
            end_date: row[importMapping.endDate] || new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
            frequency: row[importMapping.frequency]?.trim() || 'monthly',
            campaign: row[importMapping.campaign]?.trim() || settings.campaigns[0] || 'General Fund',
            status: 'active',
            created_by: currentUser?.id
          };
          
          const { data: newPledge, error } = await supabase
            .from('pledges')
            .insert([pledgeData])
            .select()
            .single();
          
          if (error) throw error;
          
          if (newPledge) {
            setPledges(prev => [...prev, {
              id: newPledge.id,
              donorId: newPledge.donor_id,
              donorName: newPledge.donor_name,
              totalAmount: parseFloat(newPledge.total_amount),
              paidAmount: 0,
              startDate: newPledge.start_date,
              endDate: newPledge.end_date,
              frequency: newPledge.frequency,
              campaign: newPledge.campaign,
              status: newPledge.status,
              payments: []
            }]);
            successCount++;
          }
        }
      } catch (error) {
        errors.push(`Row ${i + 1}: ${error.message}`);
      }
      
      setImportSuccess(successCount);
    }
    
    if (errors.length > 0) {
      setImportErrors(errors.slice(0, 10));
    }
    
    await logActivity('IMPORT_DATA', `Imported ${successCount} ${importType}`, importType);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading donation system...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginScreen 
        onLogin={handleLogin} 
        onResetPassword={handleResetPassword}
        organizationName={settings.organizationName}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Email Status Notification */}
      {emailStatus.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 ${
          emailStatus.success ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
        }`}>
          {emailStatus.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          )}
          <span>{emailStatus.message}</span>
          <button 
            onClick={() => setEmailStatus({ show: false, success: false, message: '' })}
            className="ml-2 hover:opacity-80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {settings.logo ? (
                <img 
                  src={settings.logo} 
                  alt="Logo" 
                  className="w-16 h-16 object-contain"
                />
              ) : (
                <div className="bg-emerald-600 p-3 rounded-lg">
                  <Heart className="w-8 h-8 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{settings.organizationName || 'Donation Management System'}</h1>
                <p className="text-sm text-gray-500">Donation Management System</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setEditingItem(null); setShowDonationModal(true); }}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Donation
              </button>
              
              {/* User Menu */}
              <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                {/* Admin Dropdown Menu */}
                <div className="relative admin-menu-container">
                  <button 
                    onClick={() => setShowAdminMenu(!showAdminMenu)}
                    className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${currentUser?.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                      {currentUser?.role === 'admin' ? (
                        <Shield className="w-5 h-5 text-purple-600" />
                      ) : (
                        <User className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{currentUser?.name}</p>
                      <p className={`text-xs font-medium capitalize ${currentUser?.role === 'admin' ? 'text-purple-600' : 'text-blue-600'}`}>
                        {currentUser?.role}
                      </p>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showAdminMenu ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showAdminMenu && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      {/* Settings - available to all */}
                      <button
                        onClick={() => { setActiveTab('settings'); setShowAdminMenu(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${activeTab === 'settings' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                      >
                        <Settings className="w-4 h-4" />
                        <span className="text-sm font-medium">Settings</span>
                      </button>
                      
                      {/* Admin-only options */}
                      {currentUser?.role === 'admin' && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <p className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase">Admin Only</p>
                          <button
                            onClick={() => { setActiveTab('users'); setShowAdminMenu(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${activeTab === 'users' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                          >
                            <UserCheck className="w-4 h-4" />
                            <span className="text-sm font-medium">Users</span>
                          </button>
                          <button
                            onClick={() => { setActiveTab('activity'); setShowAdminMenu(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${activeTab === 'activity' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                          >
                            <Activity className="w-4 h-4" />
                            <span className="text-sm font-medium">Activity Log</span>
                          </button>
                        </>
                      )}
                      
                      <div className="border-t border-gray-100 my-1"></div>
                      
                      {/* Change Password */}
                      <button
                        onClick={() => { setShowChangePasswordModal(true); setShowAdminMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Key className="w-4 h-4" />
                        <span className="text-sm font-medium">Change Password</span>
                      </button>
                      
                      {/* Logout */}
                      <button
                        onClick={() => { handleLogout(); setShowAdminMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50 transition-colors text-red-600"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Welcome Header */}
            <div className="text-center py-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to CIC Donation Management</h1>
              <p className="text-gray-500">Select a portal to get started</p>
            </div>

            {/* Two Portal Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* CIC Expenses Portal - Operational/Utility Theme */}
              <div
                onClick={() => setActiveTab('annual-fund')}
                className="group cursor-pointer bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-emerald-100 hover:border-emerald-400 transform hover:-translate-y-2"
              >
                <div className="h-52 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 relative overflow-hidden">
                  {/* Decorative pattern - circuit/utility lines */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full" style={{backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px)'}}></div>
                    <div className="absolute top-0 left-0 w-full h-full" style={{backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px)'}}></div>
                  </div>
                  <div className="absolute -right-16 -top-16 w-48 h-48 bg-white/10 rounded-full"></div>
                  <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/10 rounded-full"></div>
                  
                  {/* Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                      📅 Monthly Operations
                    </span>
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform border border-white/30">
                        <span className="text-4xl">⚡</span>
                      </div>
                      <h2 className="text-2xl font-bold text-white">Expenses Portal</h2>
                      <p className="text-emerald-100 text-sm mt-1">Utilities & Maintenance</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-gradient-to-b from-white to-emerald-50/30">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900">CIC Expenses Portal</h3>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-medium">OPERATIONAL</span>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">Track monthly operational costs - electricity, water, gas, maintenance, security, and landscaping expenses.</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <span className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center text-lg">💡</span>
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-lg">💧</span>
                      <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-lg">🔥</span>
                      <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-lg">🌿</span>
                      <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-lg">🔧</span>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-600 font-semibold group-hover:translate-x-2 transition-transform">
                      Enter
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>

              {/* CIC Expansion Donation Portal - Building/Growth Theme */}
              <div
                onClick={() => setActiveTab('expansion-portal')}
                className="group cursor-pointer bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-violet-100 hover:border-violet-400 transform hover:-translate-y-2"
              >
                <div className="h-52 bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-700 relative overflow-hidden">
                  {/* Decorative pattern - building blocks */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute bottom-0 left-4 w-12 h-20 bg-white/20 rounded-t-lg"></div>
                    <div className="absolute bottom-0 left-20 w-16 h-32 bg-white/20 rounded-t-lg"></div>
                    <div className="absolute bottom-0 right-20 w-14 h-24 bg-white/20 rounded-t-lg"></div>
                    <div className="absolute bottom-0 right-4 w-10 h-16 bg-white/20 rounded-t-lg"></div>
                  </div>
                  <div className="absolute -right-16 -top-16 w-48 h-48 bg-white/10 rounded-full"></div>
                  <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/10 rounded-full"></div>
                  
                  {/* Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                      🎯 Capital Campaign
                    </span>
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform border border-white/30">
                        <span className="text-4xl">🕌</span>
                      </div>
                      <h2 className="text-2xl font-bold text-white">Expansion Portal</h2>
                      <p className="text-violet-200 text-sm mt-1">Masjid Building Project</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-gradient-to-b from-white to-violet-50/30">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900">CIC Expansion Portal</h3>
                    <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-lg text-xs font-medium">CAPITAL</span>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">Manage building expansion pledges, track fundraising progress with thermometer, and send payment reminders.</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <span className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center text-lg">🏗️</span>
                      <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-lg">📊</span>
                      <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-lg">💳</span>
                      <span className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center text-lg">📧</span>
                      <span className="w-8 h-8 bg-fuchsia-100 rounded-lg flex items-center justify-center text-lg">📱</span>
                    </div>
                    <div className="flex items-center gap-2 text-violet-600 font-semibold group-hover:translate-x-2 transition-transform">
                      Enter
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Summary */}
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(donations.filter(d => d.type === 'annual-fund').reduce((sum, d) => sum + d.amount, 0))}</p>
                    <p className="text-sm text-gray-600">⚡ Expenses Collected</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                    <p className="text-2xl font-bold text-violet-600">{formatCurrency(pledges.filter(p => p.campaign === 'Expansion' || p.campaign === 'Building Fund').reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0) + donations.filter(d => (d.campaign === 'Expansion' || d.campaign === 'Building Fund') && d.type !== 'pledge-payment').reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0) + (parseFloat(settings.expansionCashOnHand) || 0))}</p>
                    <p className="text-sm text-gray-600">🕌 Expansion Raised</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-2xl font-bold text-blue-600">{pledges.filter(p => p.status === 'active').length}</p>
                    <p className="text-sm text-gray-600">📋 Active Pledges</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <p className="text-2xl font-bold text-amber-600">{donors.length}</p>
                    <p className="text-sm text-gray-600">👥 Total Donors</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Donations Tab */}
        {activeTab === 'donations' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Donation Categories</h2>
                <p className="text-gray-500 text-sm mt-1">Select a category to add or view donations</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportToCSV}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
                <button
                  onClick={exportDonationsToExcel}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
                <button
                  onClick={() => openImportModal('donations')}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Import
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className="bg-emerald-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Category
                </button>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">All Time</span>
                </div>
                <p className="text-gray-400 text-sm">Total Donations</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(donations.reduce((sum, d) => sum + d.amount, 0))}</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">This Month</span>
                </div>
                <p className="text-emerald-100 text-sm">Monthly Total</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(donations.filter(d => {
                    const donationDate = new Date(d.date);
                    const now = new Date();
                    return donationDate.getMonth() === now.getMonth() && donationDate.getFullYear() === now.getFullYear();
                  }).reduce((sum, d) => sum + d.amount, 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Count</span>
                </div>
                <p className="text-blue-100 text-sm">Total Transactions</p>
                <p className="text-2xl font-bold mt-1">{donations.length}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Avg</span>
                </div>
                <p className="text-purple-100 text-sm">Average Donation</p>
                <p className="text-2xl font-bold mt-1">
                  {donations.length > 0 ? formatCurrency(donations.reduce((sum, d) => sum + d.amount, 0) / donations.length) : '$0'}
                </p>
              </div>
            </div>

            {/* Campaign Category Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {settings.campaigns.map((campaign, index) => {
                const campaignDonations = donations.filter(d => d.campaign === campaign);
                const totalAmount = campaignDonations.reduce((sum, d) => sum + d.amount, 0);
                const donationCount = campaignDonations.length;
                const totalAllDonations = donations.reduce((sum, d) => sum + d.amount, 0);
                const percentage = totalAllDonations > 0 ? (totalAmount / totalAllDonations) * 100 : 0;
                
                // Gradient color schemes for each card
                const gradients = [
                  { from: 'from-emerald-500', to: 'to-teal-600', light: 'bg-emerald-50', ring: 'ring-emerald-500/20', text: 'text-emerald-600', bar: 'bg-emerald-500' },
                    { from: 'from-blue-500', to: 'to-indigo-600', light: 'bg-blue-50', ring: 'ring-blue-500/20', text: 'text-blue-600', bar: 'bg-blue-500' },
                    { from: 'from-purple-500', to: 'to-pink-600', light: 'bg-purple-50', ring: 'ring-purple-500/20', text: 'text-purple-600', bar: 'bg-purple-500' },
                    { from: 'from-amber-500', to: 'to-orange-600', light: 'bg-amber-50', ring: 'ring-amber-500/20', text: 'text-amber-600', bar: 'bg-amber-500' },
                    { from: 'from-rose-500', to: 'to-red-600', light: 'bg-rose-50', ring: 'ring-rose-500/20', text: 'text-rose-600', bar: 'bg-rose-500' },
                    { from: 'from-cyan-500', to: 'to-blue-600', light: 'bg-cyan-50', ring: 'ring-cyan-500/20', text: 'text-cyan-600', bar: 'bg-cyan-500' },
                    { from: 'from-violet-500', to: 'to-purple-600', light: 'bg-violet-50', ring: 'ring-violet-500/20', text: 'text-violet-600', bar: 'bg-violet-500' },
                    { from: 'from-teal-500', to: 'to-emerald-600', light: 'bg-teal-50', ring: 'ring-teal-500/20', text: 'text-teal-600', bar: 'bg-teal-500' },
                  ];
                  const gradient = gradients[index % gradients.length];
                  
                  // Icons for campaigns
                  const getIcon = (name) => {
                    const lower = name.toLowerCase();
                    if (lower.includes('general') || lower.includes('fund')) return '💰';
                    if (lower.includes('building') || lower.includes('construction') || lower.includes('masjid')) return '🕌';
                    if (lower.includes('youth') || lower.includes('education') || lower.includes('school')) return '📚';
                    if (lower.includes('emergency') || lower.includes('relief') || lower.includes('disaster')) return '🆘';
                    if (lower.includes('scholarship')) return '🎓';
                    if (lower.includes('community') || lower.includes('outreach')) return '🤝';
                    if (lower.includes('food') || lower.includes('pantry')) return '🍲';
                    if (lower.includes('ramadan') || lower.includes('eid')) return '🌙';
                    if (lower.includes('zakat')) return '📿';
                    if (lower.includes('sadaqah')) return '❤️';
                    return '✨';
                  };
                  
                  return (
                    <div
                      key={campaign}
                      className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden ring-1 ring-gray-200 hover:ring-2 hover:ring-offset-2 hover:ring-offset-white transform hover:-translate-y-1"
                      style={{ ['--tw-ring-color']: gradient.ring }}
                      onClick={() => {
                        setEditingItem(null);
                        setShowDonationModal(true);
                        setTimeout(() => {
                          const campaignSelect = document.querySelector('select[name="campaign"]');
                          if (campaignSelect) {
                            campaignSelect.value = campaign;
                            campaignSelect.dispatchEvent(new Event('change', { bubbles: true }));
                          }
                        }, 100);
                      }}
                    >
                      {/* Gradient Header */}
                      <div className={`h-24 bg-gradient-to-br ${gradient.from} ${gradient.to} relative overflow-hidden`}>
                        {/* Decorative circles */}
                        <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full"></div>
                        <div className="absolute -right-8 top-8 w-20 h-20 bg-white/10 rounded-full"></div>
                        <div className="absolute right-4 bottom-3 text-4xl opacity-90 transform group-hover:scale-110 transition-transform duration-300">
                          {getIcon(campaign)}
                        </div>
                        {/* Add button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem(null);
                            setShowDonationModal(true);
                          }}
                          className="absolute top-3 right-3 p-2 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all opacity-0 group-hover:opacity-100 transform translate-y-1 group-hover:translate-y-0"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      
                      {/* Card Content */}
                      <div className="p-5">
                        <h4 className="text-lg font-bold text-gray-900 mb-1">{campaign}</h4>
                        <p className="text-sm text-gray-500 mb-4">{donationCount} donation{donationCount !== 1 ? 's' : ''}</p>
                        
                        {/* Amount Display */}
                        <div className="flex items-end justify-between mb-4">
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Raised</p>
                            <p className={`text-2xl font-bold ${gradient.text}`}>{formatCurrency(totalAmount)}</p>
                          </div>
                          <div className={`${gradient.light} px-3 py-1.5 rounded-full`}>
                            <span className={`text-sm font-semibold ${gradient.text}`}>{percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                          <div 
                            className={`h-full ${gradient.bar} rounded-full transition-all duration-500`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                        
                        {/* Donation Box Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDonationBoxCampaign(campaign);
                            setShowDonationBoxModal(true);
                          }}
                          className={`w-full py-2.5 px-4 ${gradient.light} ${gradient.text} rounded-xl font-medium text-sm flex items-center justify-center gap-2 hover:opacity-80 transition-all mb-4 border border-current/20`}
                        >
                          <span className="text-lg">📦</span>
                          Donation Box Fund
                        </button>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          {donationCount > 0 ? (
                            <p className="text-xs text-gray-400">
                              Last: {formatDate(campaignDonations.sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date)}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400">No donations yet</p>
                          )}
                          <div className={`flex items-center gap-1 ${gradient.text} text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                            <span>Donate</span>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            {/* Category Charts Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Monthly Donation Trends</h3>
                <p className="text-sm text-gray-500 mt-0.5">Donation amounts by category over the last 6 months</p>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {settings.campaigns.map((campaign, index) => {
                  // Get last 6 months
                  const months = [];
                  for (let i = 5; i >= 0; i--) {
                    const d = new Date();
                    d.setMonth(d.getMonth() - i);
                    months.push({
                      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                    });
                  }
                  
                  // Calculate monthly totals for this campaign
                  const monthlyData = months.map(month => {
                    const total = donations
                      .filter(d => {
                        const donationMonth = d.date.substring(0, 7);
                        return d.campaign === campaign && donationMonth === month.key;
                      })
                      .reduce((sum, d) => sum + d.amount, 0);
                    return { ...month, amount: total };
                  });
                  
                  const maxAmount = Math.max(...monthlyData.map(m => m.amount), 1);
                  const totalCampaign = donations.filter(d => d.campaign === campaign).reduce((sum, d) => sum + d.amount, 0);
                  
                  // Colors for charts
                  const chartColors = [
                    { bar: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-600' },
                    { bar: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
                    { bar: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-600' },
                    { bar: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-600' },
                    { bar: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-600' },
                    { bar: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-600' },
                    { bar: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-600' },
                    { bar: 'bg-teal-500', light: 'bg-teal-100', text: 'text-teal-600' },
                  ];
                  const color = chartColors[index % chartColors.length];
                  
                  // Icons for campaigns
                  const getIcon = (name) => {
                    const lower = name.toLowerCase();
                    if (lower.includes('general') || lower.includes('fund')) return '💰';
                    if (lower.includes('building') || lower.includes('construction') || lower.includes('masjid')) return '🕌';
                    if (lower.includes('youth') || lower.includes('education') || lower.includes('school')) return '📚';
                    if (lower.includes('emergency') || lower.includes('relief') || lower.includes('disaster')) return '🆘';
                    if (lower.includes('scholarship')) return '🎓';
                    if (lower.includes('community') || lower.includes('outreach')) return '🤝';
                    if (lower.includes('food') || lower.includes('pantry')) return '🍲';
                    if (lower.includes('ramadan') || lower.includes('eid')) return '🌙';
                    if (lower.includes('zakat')) return '📿';
                    if (lower.includes('sadaqah')) return '❤️';
                    return '✨';
                  };
                  
                  return (
                    <div key={campaign} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Chart Header */}
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${color.light} rounded-xl flex items-center justify-center text-xl`}>
                            {getIcon(campaign)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{campaign}</h4>
                            <p className="text-xs text-gray-500">Last 6 months</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${color.text}`}>{formatCurrency(totalCampaign)}</p>
                          <p className="text-xs text-gray-400">Total</p>
                        </div>
                      </div>
                      
                      {/* Chart Body */}
                      <div className="p-5">
                        <div className="flex items-end justify-between gap-2 h-40">
                          {monthlyData.map((month, idx) => (
                            <div key={month.key} className="flex-1 flex flex-col items-center">
                              {/* Amount label */}
                              <div className="mb-2 text-xs font-medium text-gray-600 h-8 flex items-end">
                                {month.amount > 0 && formatCurrency(month.amount).replace('.00', '')}
                              </div>
                              {/* Bar */}
                              <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '100px' }}>
                                <div
                                  className={`absolute bottom-0 left-0 right-0 ${color.bar} rounded-t-lg transition-all duration-500 hover:opacity-80`}
                                  style={{ 
                                    height: month.amount > 0 ? `${Math.max((month.amount / maxAmount) * 100, 5)}%` : '0%',
                                  }}
                                ></div>
                              </div>
                              {/* Month label */}
                              <p className="mt-2 text-xs text-gray-500 font-medium">{month.label}</p>
                            </div>
                          ))}
                        </div>
                        
                        {/* No data message */}
                        {totalCampaign === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                            <p className="text-gray-400 text-sm">No donations yet</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Chart Footer */}
                      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          {donations.filter(d => d.campaign === campaign).length} total donations
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDonationBoxCampaign(campaign);
                            setShowDonationBoxModal(true);
                          }}
                          className={`text-xs ${color.text} font-medium hover:underline flex items-center gap-1`}
                        >
                          <span>📦</span> Add Box Donation
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* View Reports Link */}
              {donations.length > 0 && (
                <div className="text-center">
                  <button
                    onClick={() => setActiveTab('reports')}
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium inline-flex items-center gap-1"
                  >
                    View detailed reports with all {donations.length} donations
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Annual Fund Raising Tab */}
        {activeTab === 'annual-fund' && (
          <div className="space-y-6">
            {/* Distinctive Header Banner - Expenses Portal */}
            <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
              {/* Background Pattern - Circuit/Grid Lines */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px), repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px)'}}></div>
              </div>
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              
              <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-white rotate-180" />
                  </button>
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                    <span className="text-3xl">⚡</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold">CIC Expenses Portal</h2>
                      <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">OPERATIONAL</span>
                    </div>
                    <p className="text-emerald-100 text-sm mt-1">💡 Utilities • 💧 Water • 🔥 Gas • 🔧 Maintenance • 🌿 Landscaping</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={exportToCSV}
                    className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-white/30 border border-white/30 text-sm transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    CSV
                  </button>
                  <button
                    onClick={exportDonationsToExcel}
                    className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-white/30 border border-white/30 text-sm transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Portal Tabs - Emerald/Teal Theme */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-emerald-100 p-1.5">
              <div className="flex gap-1">
                {[
                  { id: 'overview', label: 'Overview & Goals', icon: Target },
                  { id: 'add', label: 'Add Contribution', icon: Plus },
                  { id: 'trends', label: 'Monthly Trends', icon: TrendingUp },
                  { id: 'history', label: 'Transaction History', icon: Clock }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setAnnualFundTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      annualFundTab === tab.id
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25'
                        : 'text-gray-600 hover:bg-emerald-50'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Tab */}
            {annualFundTab === 'overview' && (
              <>
            {/* Annual Fund Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Landmark className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">All Time</span>
                </div>
                <p className="text-indigo-100 text-sm">Total Annual Fund</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(donations.filter(d => d.type === 'annual-fund').reduce((sum, d) => sum + d.amount, 0))}</p>
              </div>
              <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">This Month</span>
                </div>
                <p className="text-violet-100 text-sm">Monthly Total</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(donations.filter(d => {
                    if (d.type !== 'annual-fund') return false;
                    const donationDate = new Date(d.date);
                    const now = new Date();
                    return donationDate.getMonth() === now.getMonth() && donationDate.getFullYear() === now.getFullYear();
                  }).reduce((sum, d) => sum + d.amount, 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Count</span>
                </div>
                <p className="text-fuchsia-100 text-sm">Total Contributions</p>
                <p className="text-2xl font-bold mt-1">{donations.filter(d => d.type === 'annual-fund').length}</p>
              </div>
              <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Avg</span>
                </div>
                <p className="text-pink-100 text-sm">Average Contribution</p>
                <p className="text-2xl font-bold mt-1">
                  {(() => {
                    const annualDonations = donations.filter(d => d.type === 'annual-fund');
                    return annualDonations.length > 0 ? formatCurrency(annualDonations.reduce((sum, d) => sum + d.amount, 0) / annualDonations.length) : '$0';
                  })()}
                </p>
              </div>
            </div>

            {/* Annual Fund Category Cards */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Expense Categories</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {annualFundCategories.map((category, index) => {
                const goalAmount = settings.annualFundGoals?.[category] || 0;
                const categoryDonations = donations.filter(d => d.type === 'annual-fund' && d.campaign === category);
                const totalAmount = categoryDonations.reduce((sum, d) => sum + d.amount, 0);
                const donationCount = categoryDonations.length;
                const goalPercentage = goalAmount > 0 ? (totalAmount / goalAmount) * 100 : 0;
                
                // Gradient color schemes for each card
                const gradients = [
                  { from: 'from-blue-500', to: 'to-blue-600', light: 'bg-blue-50', text: 'text-blue-600', bar: 'bg-blue-500' },
                  { from: 'from-indigo-500', to: 'to-indigo-600', light: 'bg-indigo-50', text: 'text-indigo-600', bar: 'bg-indigo-500' },
                  { from: 'from-yellow-500', to: 'to-yellow-600', light: 'bg-yellow-50', text: 'text-yellow-600', bar: 'bg-yellow-500' },
                  { from: 'from-cyan-500', to: 'to-cyan-600', light: 'bg-cyan-50', text: 'text-cyan-600', bar: 'bg-cyan-500' },
                  { from: 'from-orange-500', to: 'to-orange-600', light: 'bg-orange-50', text: 'text-orange-600', bar: 'bg-orange-500' },
                  { from: 'from-green-500', to: 'to-green-600', light: 'bg-green-50', text: 'text-green-600', bar: 'bg-green-500' },
                  { from: 'from-violet-500', to: 'to-violet-600', light: 'bg-violet-50', text: 'text-violet-600', bar: 'bg-violet-500' },
                  { from: 'from-slate-500', to: 'to-slate-600', light: 'bg-slate-50', text: 'text-slate-600', bar: 'bg-slate-500' },
                  { from: 'from-teal-500', to: 'to-teal-600', light: 'bg-teal-50', text: 'text-teal-600', bar: 'bg-teal-500' },
                  { from: 'from-rose-500', to: 'to-rose-600', light: 'bg-rose-50', text: 'text-rose-600', bar: 'bg-rose-500' },
                ];
                const gradient = gradients[index % gradients.length];
                
                // Icons for annual fund categories
                const getIcon = (name) => {
                  switch(name) {
                    case 'Maintenance': return '🔧';
                    case 'Security': return '🛡️';
                    case 'Electricity': return '💡';
                    case 'Water': return '💧';
                    case 'Gas': return '🔥';
                    case 'Landscaping': return '🌳';
                    case 'Phone/Internet': return '📡';
                    case 'Trash': return '🗑️';
                    case 'Supplies': return '📦';
                    case 'Activities': return '🎯';
                    case 'Insurance': return '🛡️';
                    default: return '📋';
                  }
                };
                
                return (
                  <div
                    key={category}
                    className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden ring-1 ring-gray-200 hover:ring-2 hover:ring-offset-2 transform hover:-translate-y-1"
                    onClick={() => {
                      setAnnualFundCategory(category);
                      setShowAnnualFundModal(true);
                    }}
                  >
                    {/* Gradient Header */}
                    <div className={`h-20 bg-gradient-to-br ${gradient.from} ${gradient.to} relative overflow-hidden`}>
                      <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/10 rounded-full"></div>
                      <div className="absolute right-3 bottom-2 text-3xl opacity-90 transform group-hover:scale-110 transition-transform duration-300">
                        {getIcon(category)}
                      </div>
                      {/* Goal Badge with Edit Button */}
                      <div className="absolute left-3 top-3 flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGoalCategory(category);
                            setEditingGoalAmount(goalAmount.toString());
                          }}
                          className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded-full font-medium transition-colors"
                        >
                          <Target className="w-3 h-3" />
                          Goal: {formatCurrency(goalAmount)}
                          <Edit className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Card Content */}
                    <div className="p-4">
                      <h4 className="text-sm font-bold text-gray-900 mb-1">{category}</h4>
                      <p className="text-xs text-gray-500 mb-3">{donationCount} contribution{donationCount !== 1 ? 's' : ''}</p>
                      
                      {/* Amount Display */}
                      <div className="flex items-end justify-between mb-2">
                        <p className={`text-lg font-bold ${gradient.text}`}>{formatCurrency(totalAmount)}</p>
                        <span className={`text-xs font-semibold ${goalPercentage >= 100 ? 'text-green-600 bg-green-100' : `${gradient.text} ${gradient.light}`} px-2 py-0.5 rounded-full`}>
                          {goalPercentage.toFixed(0)}%
                        </span>
                      </div>
                      
                      {/* Goal Progress Bar */}
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                        <div 
                          className={`h-full ${goalPercentage >= 100 ? 'bg-green-500' : gradient.bar} rounded-full transition-all duration-500`}
                          style={{ width: `${Math.min(goalPercentage, 100)}%` }}
                        ></div>
                      </div>
                      
                      {/* Remaining */}
                      <p className="text-xs text-gray-400">
                        {goalPercentage >= 100 ? (
                          <span className="text-green-600 font-medium">✓ Goal reached!</span>
                        ) : (
                          <span>{formatCurrency(goalAmount - totalAmount)} remaining</span>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            {/* Edit Goal Modal */}
            {editingGoalCategory && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-violet-600 px-6 py-4 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Target className="w-6 h-6" />
                        <div>
                          <h3 className="font-bold">Set Donation Goal</h3>
                          <p className="text-indigo-100 text-sm">{editingGoalCategory}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => { setEditingGoalCategory(null); setEditingGoalAmount(''); }}
                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Annual Goal Amount
                    </label>
                    <div className="relative mb-4">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <DollarSign className="w-5 h-5 text-indigo-500" />
                      </div>
                      <input
                        type="number"
                        value={editingGoalAmount}
                        onChange={(e) => setEditingGoalAmount(e.target.value)}
                        min="0"
                        step="100"
                        autoFocus
                        placeholder="0.00"
                        className="w-full pl-11 pr-4 py-3 text-xl font-semibold border-2 border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => { setEditingGoalCategory(null); setEditingGoalAmount(''); }}
                        className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdateAnnualFundGoal(editingGoalCategory, parseFloat(editingGoalAmount) || 0)}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-violet-600 text-white rounded-xl hover:from-indigo-600 hover:to-violet-700 font-medium shadow-lg shadow-indigo-500/25 transition-all"
                      >
                        Save Goal
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
            )}

            {/* Add Contribution Tab */}
            {annualFundTab === 'add' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 text-white">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Add Contribution
                  </h3>
                  <p className="text-emerald-100 text-sm">Record a new contribution to an expense category</p>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Category Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Select Category</label>
                      <div className="grid grid-cols-2 gap-2">
                        {annualFundCategories.map((category) => {
                          const icons = {
                            'Maintenance': '🔧', 'Security': '🛡️', 'Electricity': '💡', 'Water': '💧',
                            'Gas': '🔥', 'Landscaping': '🌳', 'Phone/Internet': '📡', 'Trash': '🗑️',
                            'Supplies': '📦', 'Activities': '🎯', 'Insurance': '🛡️'
                          };
                          return (
                            <button
                              key={category}
                              type="button"
                              onClick={() => {
                                setAnnualFundCategory(category);
                                setShowAnnualFundModal(true);
                              }}
                              className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                            >
                              <span className="text-xl">{icons[category] || '📋'}</span>
                              <span className="text-sm font-medium text-gray-700">{category}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Quick Stats</label>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Total Collected (All Time)</span>
                            <span className="font-bold text-gray-900">
                              {formatCurrency(donations.filter(d => d.type === 'annual-fund').reduce((sum, d) => sum + d.amount, 0))}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">This Month</span>
                            <span className="font-bold text-emerald-600">
                              {formatCurrency(donations.filter(d => {
                                if (d.type !== 'annual-fund') return false;
                                const donationDate = new Date(d.date);
                                const now = new Date();
                                return donationDate.getMonth() === now.getMonth() && donationDate.getFullYear() === now.getFullYear();
                              }).reduce((sum, d) => sum + d.amount, 0))}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Total Contributions</span>
                            <span className="font-bold text-gray-900">{donations.filter(d => d.type === 'annual-fund').length}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <p className="text-sm text-emerald-800">
                          <strong>Tip:</strong> Click on any category to add a contribution. You can record cash and check payments separately.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trends Tab */}
            {annualFundTab === 'trends' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Monthly Trends by Category</h3>
                <p className="text-sm text-gray-500 mt-0.5">Annual fund contributions over the last 6 months</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {annualFundCategories.map((category, index) => {
                  // Get last 6 months
                  const months = [];
                  for (let i = 5; i >= 0; i--) {
                    const d = new Date();
                    d.setMonth(d.getMonth() - i);
                    months.push({
                      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                    });
                  }
                  
                  // Calculate monthly totals for this category
                  const monthlyData = months.map(month => {
                    const total = donations
                      .filter(d => {
                        const donationMonth = d.date.substring(0, 7);
                        return d.type === 'annual-fund' && d.campaign === category && donationMonth === month.key;
                      })
                      .reduce((sum, d) => sum + d.amount, 0);
                    return { ...month, amount: total };
                  });
                  
                  const maxAmount = Math.max(...monthlyData.map(m => m.amount), 1);
                  const totalCategory = donations.filter(d => d.type === 'annual-fund' && d.campaign === category).reduce((sum, d) => sum + d.amount, 0);
                  
                  // Only show charts for categories that have donations
                  if (totalCategory === 0) return null;
                  
                  // Colors for charts
                  const chartColors = [
                    { bar: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
                    { bar: 'bg-indigo-500', light: 'bg-indigo-100', text: 'text-indigo-600' },
                    { bar: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-600' },
                    { bar: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-600' },
                    { bar: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-600' },
                    { bar: 'bg-green-500', light: 'bg-green-100', text: 'text-green-600' },
                    { bar: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-600' },
                    { bar: 'bg-slate-500', light: 'bg-slate-100', text: 'text-slate-600' },
                    { bar: 'bg-teal-500', light: 'bg-teal-100', text: 'text-teal-600' },
                    { bar: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-600' },
                  ];
                  const color = chartColors[index % chartColors.length];
                  
                  // Icons for categories
                  const getIcon = (name) => {
                    switch(name) {
                      case 'Maintenance': return '🔧';
                      case 'Security': return '🛡️';
                      case 'Electricity': return '💡';
                      case 'Water': return '💧';
                      case 'Gas': return '🔥';
                      case 'Landscaping': return '🌳';
                      case 'Phone/Internet': return '📡';
                      case 'Trash': return '🗑️';
                      case 'Supplies': return '📦';
                      case 'Activities': return '🎯';
                      case 'Insurance': return '🛡️';
                      default: return '📋';
                    }
                  };
                  
                  return (
                    <div key={category} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Chart Header */}
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${color.light} rounded-xl flex items-center justify-center text-xl`}>
                            {getIcon(category)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{category}</h4>
                            <p className="text-xs text-gray-500">Last 6 months</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${color.text}`}>{formatCurrency(totalCategory)}</p>
                          <p className="text-xs text-gray-400">Total</p>
                        </div>
                      </div>
                      
                      {/* Chart Body */}
                      <div className="p-5">
                        <div className="flex items-end justify-between gap-2 h-32">
                          {monthlyData.map((month) => (
                            <div key={month.key} className="flex-1 flex flex-col items-center">
                              {/* Amount label */}
                              <div className="mb-2 text-xs font-medium text-gray-600 h-6 flex items-end">
                                {month.amount > 0 && formatCurrency(month.amount).replace('.00', '')}
                              </div>
                              {/* Bar */}
                              <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '80px' }}>
                                <div
                                  className={`absolute bottom-0 left-0 right-0 ${color.bar} rounded-t-lg transition-all duration-500 hover:opacity-80`}
                                  style={{ 
                                    height: month.amount > 0 ? `${Math.max((month.amount / maxAmount) * 100, 5)}%` : '0%',
                                  }}
                                ></div>
                              </div>
                              {/* Month label */}
                              <p className="mt-2 text-xs text-gray-500 font-medium">{month.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Empty state */}
              {donations.filter(d => d.type === 'annual-fund').length === 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                  <Landmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Annual Fund Contributions Yet</h3>
                  <p className="text-gray-500 mb-6">Click on any category above to add your first contribution</p>
                </div>
              )}
            </div>
            )}

            {/* Transaction History Tab */}
            {annualFundTab === 'history' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-slate-600 to-slate-700 px-6 py-4 text-white">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Transaction History
                  </h3>
                  <p className="text-slate-200 text-sm">All annual fund contributions</p>
                </div>
                
                <div className="p-6">
                  {/* Filters */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <select
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      onChange={(e) => {
                        // Filter functionality can be added later
                      }}
                    >
                      <option value="">All Categories</option>
                      {annualFundCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Transaction List */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Donor</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Method</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations
                          .filter(d => d.type === 'annual-fund')
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .slice(0, 50)
                          .map((donation) => {
                            const icons = {
                              'Maintenance': '🔧', 'Security': '🛡️', 'Electricity': '💡', 'Water': '💧',
                              'Gas': '🔥', 'Landscaping': '🌳', 'Phone/Internet': '📡', 'Trash': '🗑️',
                              'Supplies': '📦', 'Activities': '🎯', 'Insurance': '🛡️'
                            };
                            return (
                              <tr key={donation.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-3 px-4 text-gray-600">{formatDate(donation.date)}</td>
                                <td className="py-3 px-4">
                                  <span className="flex items-center gap-2">
                                    <span>{icons[donation.campaign] || '📋'}</span>
                                    <span className="font-medium text-gray-900">{donation.campaign}</span>
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-gray-600">{donation.donorName}</td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    donation.method === 'cash' ? 'bg-green-100 text-green-700' :
                                    donation.method === 'check' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {donation.method}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-right font-semibold text-emerald-600">{formatCurrency(donation.amount)}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                    
                    {donations.filter(d => d.type === 'annual-fund').length === 0 && (
                      <div className="text-center py-12">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No transactions yet</p>
                      </div>
                    )}
                    
                    {donations.filter(d => d.type === 'annual-fund').length > 50 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        Showing most recent 50 transactions. Use export to see all.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Expansion Donation Portal */}
        {activeTab === 'expansion-portal' && (
          <div className="space-y-6">
            {/* Distinctive Header Banner - Expansion Portal */}
            <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-700 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
              {/* Background Pattern - Building Silhouettes */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute bottom-0 left-8 w-16 h-24 bg-white/30 rounded-t-lg"></div>
                <div className="absolute bottom-0 left-28 w-20 h-36 bg-white/30 rounded-t-lg"></div>
                <div className="absolute bottom-0 left-52 w-12 h-20 bg-white/30 rounded-t-lg"></div>
                <div className="absolute bottom-0 right-40 w-24 h-32 bg-white/30 rounded-t-lg"></div>
                <div className="absolute bottom-0 right-12 w-14 h-28 bg-white/30 rounded-t-lg"></div>
                {/* Dome shape */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-16 bg-white/20 rounded-t-full"></div>
              </div>
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              
              <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-white rotate-180" />
                  </button>
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                    <span className="text-3xl">🕌</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold">CIC Expansion Portal</h2>
                      <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">CAPITAL CAMPAIGN</span>
                    </div>
                    <p className="text-violet-200 text-sm mt-1">🏗️ Building Fund • 📊 Pledges • 💳 Payments • 📧 Reminders</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right hidden md:block">
                    <p className="text-violet-200 text-xs">Goal Progress</p>
                    <p className="text-xl font-bold">{Math.round(((pledges.filter(p => p.campaign === 'Expansion' || p.campaign === 'Building Fund').reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0) + donations.filter(d => (d.campaign === 'Expansion' || d.campaign === 'Building Fund') && d.type !== 'pledge-payment').reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0) + (parseFloat(settings.expansionCashOnHand) || 0)) / (settings.expansionGoal || 500000)) * 100)}%</p>
                  </div>
                  <div className="w-px h-10 bg-white/20 hidden md:block"></div>
                  <div className="text-right hidden md:block">
                    <p className="text-violet-200 text-xs">Active Pledges</p>
                    <p className="text-xl font-bold">{pledges.filter(p => (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && p.status !== 'completed').length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Portal Tabs - Violet/Purple Theme */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-violet-100 p-1.5">
              <div className="flex gap-1">
                {[
                  { id: 'overview', label: 'Overview', icon: Building },
                  { id: 'pledges', label: 'Active Pledges', icon: Calendar },
                  { id: 'completed', label: 'Completed', icon: CheckCircle },
                  { id: 'receive', label: 'Record Payment', icon: DollarSign },
                  { id: 'reminders', label: 'Reminders', icon: Bell }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setExpansionPortalTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      expansionPortalTab === tab.id
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/25'
                        : 'text-gray-600 hover:bg-violet-50'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Tab - Thermometer + New Pledge Form Side by Side */}
            {expansionPortalTab === 'overview' && (
              <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Thermometer Card */}
                <div 
                  className="rounded-3xl p-6 transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #1a2332 0%, #0f172a 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Building className="w-5 h-5 text-violet-400" />
                        Expansion Fund
                      </h3>
                      <p className="text-slate-400 text-sm mt-0.5">Track our progress</p>
                    </div>
                    <button
                      onClick={() => {
                        const goal = settings.expansionGoal || 500000;
                        const newGoal = prompt('Enter new expansion goal amount:', goal);
                        if (newGoal && !isNaN(parseFloat(newGoal))) {
                          handleSettingsUpdate({ ...settings, expansionGoal: parseFloat(newGoal) });
                        }
                      }}
                      className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      title="Edit Goal"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Thermometer + Stats */}
                  {(() => {
                    const expansionPledges = pledges.filter(p => p.campaign === 'Expansion' || p.campaign === 'Building Fund');
                    const pledgePaidAmount = expansionPledges.reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
                    
                    // Also count one-time donations to Expansion/Building Fund from donations table
                    const expansionDonations = donations.filter(d => 
                      (d.campaign === 'Expansion' || d.campaign === 'Building Fund') && d.type !== 'pledge-payment'
                    );
                    const donationsAmount = expansionDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
                    
                    // Include cash on hand in total
                    const cashOnHand = parseFloat(settings.expansionCashOnHand) || 0;
                    const totalRaised = pledgePaidAmount + donationsAmount + cashOnHand;
                    const totalPledged = expansionPledges.reduce((sum, p) => sum + (parseFloat(p.totalAmount) || 0), 0);
                    const goal = settings.expansionGoal || 500000;
                    const percentage = Math.min((totalRaised / goal) * 100, 100);
                    // Minimum visible fill of 5% if any money raised, for visual feedback
                    const visualPercentage = totalRaised > 0 ? Math.max(percentage, 5) : 0;
                    const isComplete = percentage >= 100;
                    // Display percentage with 1 decimal if < 1%, otherwise round
                    const displayPercentage = percentage < 1 && percentage > 0 
                      ? percentage.toFixed(1) 
                      : Math.round(percentage);
                    
                    return (
                      <div className="flex items-center gap-6">
                        {/* Thermometer */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          {/* Stem */}
                          <div 
                            className="relative overflow-hidden"
                            style={{
                              width: '32px',
                              height: '200px',
                              background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                              borderRadius: '100px',
                              boxShadow: 'inset 0 4px 8px rgba(0,0,0,0.5), 0 0 0 1px rgba(99, 102, 241, 0.2)',
                            }}
                          >
                            {/* Fill */}
                            <div 
                              className="absolute bottom-0 left-0 w-full transition-all duration-1000"
                              style={{
                                height: `${visualPercentage}%`,
                                background: isComplete 
                                  ? 'linear-gradient(0deg, #f59e0b, #fbbf24, #fcd34d)'
                                  : 'linear-gradient(0deg, #6366f1, #8b5cf6, #a78bfa)',
                                boxShadow: isComplete 
                                  ? '0 0 20px rgba(245, 158, 11, 0.5)'
                                  : '0 0 20px rgba(99, 102, 241, 0.5)',
                                borderRadius: '100px 100px 0 0'
                              }}
                            />
                          </div>
                          
                          {/* Bulb */}
                          <div 
                            className="relative flex items-center justify-center"
                            style={{
                              width: '52px',
                              height: '52px',
                              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                              borderRadius: '50%',
                              marginTop: '-6px',
                              boxShadow: '0 0 0 1px rgba(99, 102, 241, 0.2), 0 10px 20px rgba(0,0,0,0.3)'
                            }}
                          >
                            <div 
                              className="transition-all duration-500"
                              style={{
                                width: '36px',
                                height: '36px',
                                background: isComplete 
                                  ? 'radial-gradient(circle at 35% 35%, #fcd34d, #f59e0b)'
                                  : 'radial-gradient(circle at 35% 35%, #a78bfa, #6366f1)',
                                borderRadius: '50%',
                                boxShadow: isComplete 
                                  ? '0 0 25px rgba(245, 158, 11, 0.6)'
                                  : '0 0 25px rgba(99, 102, 241, 0.6)'
                              }}
                            />
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex-1 space-y-4">
                          {/* Main Amount */}
                          <div>
                            <p className="text-3xl font-bold text-white">{formatCurrency(totalRaised)}</p>
                            <p className="text-slate-400 text-sm">of {formatCurrency(goal)} goal</p>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="relative h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-1000"
                              style={{
                                width: `${visualPercentage}%`,
                                background: isComplete 
                                  ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                                  : 'linear-gradient(90deg, #6366f1, #8b5cf6)'
                              }}
                            />
                          </div>
                          
                          {/* Stats Row */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-slate-800/50 rounded-xl p-3">
                              <p className="text-xl font-semibold text-white">{formatCurrency(totalPledged)}</p>
                              <p className="text-xs text-slate-400">Total Pledged</p>
                            </div>
                            <div className="bg-slate-800/50 rounded-xl p-3">
                              <p className="text-xl font-semibold text-white">{displayPercentage}%</p>
                              <p className="text-xs text-slate-400">Progress</p>
                            </div>
                          </div>
                          
                          {/* Pledge Count */}
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Users className="w-4 h-4" />
                            <span>{expansionPledges.filter(p => p.status === 'active').length} active pledge{expansionPledges.filter(p => p.status === 'active').length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Cash on Hand Card */}
                  <div className="mt-4 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-4 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute -right-4 -top-4 w-20 h-20 bg-white rounded-full"></div>
                      <div className="absolute -left-4 -bottom-4 w-16 h-16 bg-white rounded-full"></div>
                    </div>
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">💵</span>
                        </div>
                        <div>
                          <p className="text-emerald-100 text-xs font-medium uppercase tracking-wide">Cash on Hand</p>
                          <p className="text-2xl font-bold text-white">{formatCurrency(settings.expansionCashOnHand || 0)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentCash = settings.expansionCashOnHand || 0;
                          const newCash = prompt('Enter cash on hand amount:', currentCash);
                          if (newCash !== null && !isNaN(parseFloat(newCash))) {
                            handleSettingsUpdate({ ...settings, expansionCashOnHand: parseFloat(newCash) });
                          }
                        }}
                        className="p-2 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                        title="Edit Cash on Hand"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: New Pledge Form */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4 text-white">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      New Pledge
                    </h3>
                    <p className="text-violet-100 text-sm">Add a new expansion pledge</p>
                  </div>
                  
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      
                      const pledgeData = {
                        donorName: formData.get('donorName'),
                        donorEmail: formData.get('donorEmail'),
                        donorPhone: formData.get('donorPhone'),
                        totalAmount: parseFloat(formData.get('totalAmount')) || 0,
                        startDate: formData.get('startDate'),
                        endDate: formData.get('endDate'),
                        frequency: formData.get('frequency'),
                        campaign: 'Expansion',
                        status: 'active'
                      };
                      
                      // Check if donor exists, if not create
                      let donorId = null;
                      const existingDonor = donors.find(d => 
                        d.email.toLowerCase() === pledgeData.donorEmail.toLowerCase() ||
                        d.name.toLowerCase() === pledgeData.donorName.toLowerCase()
                      );
                      
                      if (existingDonor) {
                        donorId = existingDonor.id;
                      } else {
                        const { data: newDonor } = await supabase
                          .from('donors')
                          .insert([{
                            name: pledgeData.donorName,
                            email: pledgeData.donorEmail,
                            phone: pledgeData.donorPhone,
                            total_donated: 0,
                            donation_count: 0
                          }])
                          .select()
                          .single();
                        
                        if (newDonor) {
                          donorId = newDonor.id;
                          setDonors([...donors, {
                            id: newDonor.id,
                            name: newDonor.name,
                            email: newDonor.email,
                            phone: newDonor.phone,
                            address: '',
                            totalDonated: 0,
                            donationCount: 0
                          }]);
                        }
                      }
                      
                      const { data: newPledge, error } = await supabase
                        .from('pledges')
                        .insert([{
                          donor_id: donorId,
                          donor_name: pledgeData.donorName,
                          donor_email: pledgeData.donorEmail,
                          total_amount: pledgeData.totalAmount,
                          paid_amount: 0,
                          start_date: pledgeData.startDate,
                          end_date: pledgeData.endDate,
                          frequency: pledgeData.frequency,
                          campaign: 'Expansion',
                          status: 'active'
                        }])
                        .select()
                        .single();
                      
                      if (newPledge && !error) {
                        setPledges([{
                          id: newPledge.id,
                          donorId: newPledge.donor_id,
                          donorName: newPledge.donor_name,
                          donorEmail: newPledge.donor_email,
                          totalAmount: parseFloat(newPledge.total_amount),
                          paidAmount: 0,
                          startDate: newPledge.start_date,
                          endDate: newPledge.end_date,
                          frequency: newPledge.frequency,
                          campaign: newPledge.campaign,
                          status: newPledge.status,
                          payments: []
                        }, ...pledges]);
                        
                        await logActivity('CREATE_PLEDGE', `Created expansion pledge: ${formatCurrency(pledgeData.totalAmount)} from ${pledgeData.donorName}`, 'pledge', newPledge.id);
                        e.target.reset();
                        alert('Pledge created successfully!');
                      }
                    }}
                    className="p-5 space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Donor Name *</label>
                        <input
                          type="text"
                          name="donorName"
                          required
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                          placeholder="Full Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          name="donorEmail"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                        <input
                          type="tel"
                          name="donorPhone"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Pledge Amount *</label>
                        <div className="relative">
                          <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="number"
                            name="totalAmount"
                            required
                            min="1"
                            step="0.01"
                            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Start Date *</label>
                        <input
                          type="date"
                          name="startDate"
                          required
                          defaultValue={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">End Date *</label>
                        <input
                          type="date"
                          name="endDate"
                          required
                          defaultValue={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Frequency *</label>
                        <select
                          name="frequency"
                          required
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        >
                          <option value="one-time">One-Time</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg hover:from-violet-600 hover:to-purple-700 font-medium shadow-lg shadow-violet-500/25 transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create Pledge
                    </button>
                  </form>
                </div>
              </div>

              {/* Quick Stats Row */}
              {(() => {
                const expansionPledges = pledges.filter(p => p.campaign === 'Expansion' || p.campaign === 'Building Fund');
                const pledgePaidAmount = expansionPledges.reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
                
                // Also count one-time donations to Expansion/Building Fund
                const expansionDonations = donations.filter(d => 
                  (d.campaign === 'Expansion' || d.campaign === 'Building Fund') && d.type !== 'pledge-payment'
                );
                const donationsAmount = expansionDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
                
                // Include cash on hand
                const cashOnHand = parseFloat(settings.expansionCashOnHand) || 0;
                const totalRaised = pledgePaidAmount + donationsAmount + cashOnHand;
                const totalPledged = expansionPledges.reduce((sum, p) => sum + (parseFloat(p.totalAmount) || 0), 0);
                const activePledges = expansionPledges.filter(p => p.status === 'active').length;
                const completedPledges = expansionPledges.filter(p => p.status === 'completed').length;
                
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(totalPledged)}</p>
                          <p className="text-xs text-gray-500">Total Pledged</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(totalRaised)}</p>
                          <p className="text-xs text-gray-500">Collected</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{activePledges}</p>
                          <p className="text-xs text-gray-500">Active Pledges</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Award className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{completedPledges}</p>
                          <p className="text-xs text-gray-500">Completed</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

            {/* Edit Pledge Modal */}
            {editingExpansionPledge && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Edit className="w-6 h-6" />
                        <div>
                          <h3 className="font-bold">Edit Pledge</h3>
                          <p className="text-violet-100 text-sm">{editingExpansionPledge.donorName}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setEditingExpansionPledge(null)}
                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      
                      const updates = {
                        donor_name: formData.get('editDonorName'),
                        donor_email: formData.get('editDonorEmail'),
                        total_amount: parseFloat(formData.get('editTotalAmount')),
                        start_date: formData.get('editStartDate'),
                        end_date: formData.get('editEndDate'),
                        frequency: formData.get('editFrequency'),
                        status: formData.get('editStatus'),
                        pledge_notes: formData.get('editNotes') || ''
                      };
                      
                      const { error } = await supabase
                        .from('pledges')
                        .update(updates)
                        .eq('id', editingExpansionPledge.id);
                      
                      if (error) {
                        alert('Error updating pledge: ' + error.message);
                        return;
                      }
                      
                      setPledges(prev => prev.map(p => 
                        p.id === editingExpansionPledge.id 
                          ? {
                              ...p,
                              donorName: updates.donor_name,
                              donorEmail: updates.donor_email,
                              totalAmount: updates.total_amount,
                              startDate: updates.start_date,
                              endDate: updates.end_date,
                              frequency: updates.frequency,
                              status: updates.status,
                              pledgeNotes: updates.pledge_notes
                            }
                          : p
                      ));
                      
                      await logActivity('UPDATE_PLEDGE', `Updated pledge for ${updates.donor_name}`, 'pledge', editingExpansionPledge.id);
                      setEditingExpansionPledge(null);
                      alert('Pledge updated successfully!');
                    }}
                    className="p-6 space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Donor Name *</label>
                        <input type="text" name="editDonorName" defaultValue={editingExpansionPledge.donorName} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" name="editDonorEmail" defaultValue={editingExpansionPledge.donorEmail} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
                        <input type="number" name="editTotalAmount" defaultValue={editingExpansionPledge.totalAmount} required min="0" step="0.01" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount</label>
                        <input type="text" value={formatCurrency(editingExpansionPledge.paidAmount)} disabled className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                        <input type="date" name="editStartDate" defaultValue={editingExpansionPledge.startDate} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                        <input type="date" name="editEndDate" defaultValue={editingExpansionPledge.endDate} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                        <select name="editFrequency" defaultValue={editingExpansionPledge.frequency} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500">
                          <option value="one-time">One-Time</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select name="editStatus" defaultValue={editingExpansionPledge.status} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500">
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="paused">Paused</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea name="editNotes" defaultValue={editingExpansionPledge.pledgeNotes || ''} rows={2} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500" placeholder="Optional notes..." />
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={() => setEditingExpansionPledge(null)} className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors">Cancel</button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!confirm(`Delete this pledge?\n\nDonor: ${editingExpansionPledge.donorName}\nAmount: ${formatCurrency(editingExpansionPledge.totalAmount)}`)) return;
                          await supabase.from('pledge_payments').delete().eq('pledge_id', editingExpansionPledge.id);
                          const { error } = await supabase.from('pledges').delete().eq('id', editingExpansionPledge.id);
                          if (error) { alert('Error: ' + error.message); return; }
                          setPledges(prev => prev.filter(p => p.id !== editingExpansionPledge.id));
                          await logActivity('DELETE_PLEDGE', `Deleted pledge for ${editingExpansionPledge.donorName}`, 'pledge', editingExpansionPledge.id);
                          setEditingExpansionPledge(null);
                          alert('Pledge deleted!');
                        }}
                        className="px-4 py-2.5 border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 font-medium transition-colors"
                      >Delete</button>
                      <button type="submit" className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl hover:from-violet-600 hover:to-purple-700 font-medium shadow-lg shadow-violet-500/25 transition-all">Save</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            </>
            )}

            {/* All Pledges Tab */}
            {expansionPortalTab === 'pledges' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4 text-white flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Active Pledges
                    </h3>
                    <p className="text-violet-100 text-sm">Manage active expansion pledges</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{pledges.filter(p => (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && p.status !== 'completed').length}</p>
                    <p className="text-violet-200 text-xs">Active Pledges</p>
                  </div>
                </div>
                
                {/* Search Bar */}
                <div className="px-4 pt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, email, or phone..."
                      value={expansionSearchTerm}
                      onChange={(e) => setExpansionSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    />
                    {expansionSearchTerm && (
                      <button
                        onClick={() => setExpansionSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="p-4">
                  {(() => {
                    const searchLower = expansionSearchTerm.toLowerCase();
                    const searchDigits = expansionSearchTerm.replace(/[^0-9]/g, '');
                    const activePledges = pledges
                      .filter(p => (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && p.status !== 'completed')
                      .filter(p => {
                        if (!expansionSearchTerm) return true;
                        const donorName = (p.donorName || '').toLowerCase();
                        const donorEmail = (p.donorEmail || '').toLowerCase();
                        // Look up phone from donors list
                        const donor = donors.find(d => d.id === p.donorId);
                        const donorPhone = (donor?.phone || '').replace(/[^0-9]/g, '');
                        return donorName.includes(searchLower) || 
                               donorEmail.includes(searchLower) || 
                               (searchDigits && donorPhone.includes(searchDigits));
                      });
                    
                    if (activePledges.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {expansionSearchTerm ? 'No Matching Pledges' : 'No Active Pledges'}
                          </h3>
                          <p className="text-gray-500 mb-4">
                            {expansionSearchTerm ? 'Try a different search term' : 'Add your first pledge from the Overview tab'}
                          </p>
                          {!expansionSearchTerm && (
                            <button
                              onClick={() => setExpansionPortalTab('overview')}
                              className="px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors"
                            >
                              Go to Overview
                            </button>
                          )}
                        </div>
                      );
                    }
                    
                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Donor</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Pledged</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Paid</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Progress</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Frequency</th>
                              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activePledges
                              .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                              .map(pledge => {
                                const paidAmt = parseFloat(pledge.paidAmount) || 0;
                                const totalAmt = parseFloat(pledge.totalAmount) || 1;
                                const progress = (paidAmt / totalAmt) * 100;
                                
                                return (
                                  <tr key={pledge.id} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-3 px-4">
                                      <p className="font-medium text-gray-900">{pledge.donorName}</p>
                                      <p className="text-sm text-gray-500">{pledge.donorEmail}</p>
                                    </td>
                                    <td className="py-3 px-4 font-medium text-gray-900">{formatCurrency(totalAmt)}</td>
                                    <td className="py-3 px-4 font-medium text-emerald-600">{formatCurrency(paidAmt)}</td>
                                    <td className="py-3 px-4">
                                      <div className="w-24">
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                          <div 
                                            className={`h-full rounded-full ${progress >= 100 ? 'bg-emerald-500' : 'bg-violet-500'}`}
                                            style={{ width: `${Math.min(progress, 100)}%` }}
                                          />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">{Math.round(progress)}%</p>
                                      </div>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        pledge.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                        pledge.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                        pledge.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {pledge.status}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-600 capitalize">{pledge.frequency}</td>
                                    <td className="py-3 px-4 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <button
                                          onClick={() => setEditingExpansionPledge(pledge)}
                                          className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                          title="Edit Pledge"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (!confirm(`Delete this pledge?\n\nDonor: ${pledge.donorName}\nAmount: ${formatCurrency(pledge.totalAmount)}`)) return;
                                            await supabase.from('pledge_payments').delete().eq('pledge_id', pledge.id);
                                            const { error } = await supabase.from('pledges').delete().eq('id', pledge.id);
                                            if (error) { alert('Error: ' + error.message); return; }
                                            setPledges(prev => prev.filter(p => p.id !== pledge.id));
                                            await logActivity('DELETE_PLEDGE', `Deleted pledge for ${pledge.donorName}`, 'pledge', pledge.id);
                                            alert('Pledge deleted!');
                                          }}
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Delete Pledge"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Completed Pledges Tab */}
            {expansionPortalTab === 'completed' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 text-white flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Completed Pledges
                    </h3>
                    <p className="text-emerald-100 text-sm">Pledges that have been fully paid</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{pledges.filter(p => (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && p.status === 'completed').length}</p>
                    <p className="text-emerald-200 text-xs">Completed</p>
                  </div>
                </div>
                
                {/* Search Bar */}
                <div className="px-4 pt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, email, or phone..."
                      value={expansionSearchTerm}
                      onChange={(e) => setExpansionSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    {expansionSearchTerm && (
                      <button
                        onClick={() => setExpansionSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="p-4">
                  {(() => {
                    const searchLower = expansionSearchTerm.toLowerCase();
                    const searchDigits = expansionSearchTerm.replace(/[^0-9]/g, '');
                    const completedPledges = pledges
                      .filter(p => (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && p.status === 'completed')
                      .filter(p => {
                        if (!expansionSearchTerm) return true;
                        const donorName = (p.donorName || '').toLowerCase();
                        const donorEmail = (p.donorEmail || '').toLowerCase();
                        // Look up phone from donors list
                        const donor = donors.find(d => d.id === p.donorId);
                        const donorPhone = (donor?.phone || '').replace(/[^0-9]/g, '');
                        return donorName.includes(searchLower) || 
                               donorEmail.includes(searchLower) || 
                               (searchDigits && donorPhone.includes(searchDigits));
                      });
                    
                    if (completedPledges.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {expansionSearchTerm ? 'No Matching Pledges' : 'No Completed Pledges Yet'}
                          </h3>
                          <p className="text-gray-500">
                            {expansionSearchTerm ? 'Try a different search term' : 'Pledges will appear here once they are fully paid'}
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Donor</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Pledged</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Paid</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Frequency</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Completed Date</th>
                              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {completedPledges
                              .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))
                              .map(pledge => {
                                const paidAmt = parseFloat(pledge.paidAmount) || 0;
                                const totalAmt = parseFloat(pledge.totalAmount) || 1;
                                
                                return (
                                  <tr key={pledge.id} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-3 px-4">
                                      <p className="font-medium text-gray-900">{pledge.donorName}</p>
                                      <p className="text-sm text-gray-500">{pledge.donorEmail}</p>
                                    </td>
                                    <td className="py-3 px-4 font-medium text-gray-900">{formatCurrency(totalAmt)}</td>
                                    <td className="py-3 px-4 font-medium text-emerald-600">{formatCurrency(paidAmt)}</td>
                                    <td className="py-3 px-4 text-sm text-gray-600 capitalize">{pledge.frequency}</td>
                                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(pledge.endDate)}</td>
                                    <td className="py-3 px-4 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <button
                                          onClick={() => setExpansionPledgeReceipt(pledge)}
                                          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                          title="Generate Receipt"
                                        >
                                          <FileText className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => setEditingExpansionPledge(pledge)}
                                          className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                          title="View/Edit Pledge"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (!confirm(`Delete this completed pledge?\n\nDonor: ${pledge.donorName}\nAmount: ${formatCurrency(pledge.totalAmount)}`)) return;
                                            await supabase.from('pledge_payments').delete().eq('pledge_id', pledge.id);
                                            const { error } = await supabase.from('pledges').delete().eq('id', pledge.id);
                                            if (error) { alert('Error: ' + error.message); return; }
                                            setPledges(prev => prev.filter(p => p.id !== pledge.id));
                                            await logActivity('DELETE_PLEDGE', `Deleted completed pledge for ${pledge.donorName}`, 'pledge', pledge.id);
                                            alert('Pledge deleted!');
                                          }}
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Delete Pledge"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Receive Donation Tab */}
            {expansionPortalTab === 'receive' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 text-white">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Receive Donation / Record Payment
                </h3>
                <p className="text-amber-100 text-sm">Record a payment against an existing pledge or a one-time donation</p>
              </div>
              
              <div className="p-6">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    
                    try {
                      const formData = new FormData(e.target);
                      
                      const paymentType = expansionPaymentType;
                      const pledgeId = formData.get('pledgeId') || '';
                      const donorName = formData.get('receiveDonorName') || '';
                      const donorEmail = formData.get('receiveDonorEmail') || '';
                      const donorPhone = formData.get('receiveDonorPhone') || '';
                      const amount = parseFloat(formData.get('receiveAmount')) || 0;
                      const paymentDate = formData.get('paymentDate');
                      const paymentMethod = formData.get('paymentMethod');
                      const checkNumber = formData.get('checkNumber') || '';
                      const notes = formData.get('receiveNotes') || '';
                      
                      console.log('Form submitted:', { paymentType, pledgeId, amount, paymentDate, paymentMethod });
                      
                      if (amount <= 0) {
                        alert('Please enter a valid amount');
                        return;
                      }
                      
                      if (paymentType === 'pledge-payment') {
                        if (!pledgeId) {
                          alert('Please select a pledge');
                          return;
                        }
                        
                        // Record payment against existing pledge
                        const pledge = pledges.find(p => String(p.id) === String(pledgeId));
                        console.log('Found pledge:', pledge);
                        
                        if (!pledge) {
                          alert('Pledge not found. Please refresh and try again.');
                          return;
                        }
                        
                        // Create pledge payment record
                        const { data: newPayment, error: paymentError } = await supabase
                          .from('pledge_payments')
                          .insert([{
                            pledge_id: pledgeId,
                            amount: amount,
                            date: paymentDate,
                            method: paymentMethod
                          }])
                          .select()
                          .single();
                        
                        console.log('Payment result:', { newPayment, paymentError });
                        
                        if (paymentError) {
                          console.error('Payment error:', paymentError);
                          alert('Error recording payment: ' + paymentError.message);
                          return;
                        }
                        
                        if (newPayment) {
                          const currentPaidAmount = parseFloat(pledge.paidAmount) || 0;
                          const pledgeTotalAmount = parseFloat(pledge.totalAmount) || 0;
                          const newPaidAmount = currentPaidAmount + amount;
                          const newStatus = newPaidAmount >= pledgeTotalAmount ? 'completed' : 'active';
                          
                          // Update pledge
                          const { error: updateError } = await supabase
                            .from('pledges')
                            .update({ 
                              paid_amount: newPaidAmount,
                              status: newStatus
                            })
                            .eq('id', pledgeId);
                          
                          if (updateError) {
                            console.error('Update error:', updateError);
                          }
                          
                          // Update local state
                          setPledges(prev => prev.map(p => 
                            String(p.id) === String(pledgeId) 
                              ? { 
                                  ...p, 
                                  paidAmount: newPaidAmount,
                                  status: newStatus,
                                  payments: [...(p.payments || []), {
                                    id: newPayment.id,
                                    amount: amount,
                                    date: paymentDate,
                                    method: paymentMethod,
                                    notes: notes
                                  }]
                                }
                              : p
                          ));
                          
                          // Update donor stats
                          const donor = donors.find(d => d.id === pledge.donorId);
                          if (donor) {
                            await supabase
                              .from('donors')
                              .update({
                                total_donated: donor.totalDonated + amount,
                                donation_count: donor.donationCount + 1
                              })
                              .eq('id', donor.id);
                            
                            setDonors(prev => prev.map(d =>
                              d.id === donor.id
                                ? { ...d, totalDonated: d.totalDonated + amount, donationCount: d.donationCount + 1 }
                                : d
                            ));
                          }
                          
                          await logActivity('PLEDGE_PAYMENT', `Received ${formatCurrency(amount)} expansion pledge payment from ${pledge.donorName}`, 'pledge', pledgeId);
                          
                          e.target.reset();
                          alert(`Payment of ${formatCurrency(amount)} recorded successfully!`);
                        }
                      } else {
                        // One-time donation
                        console.log('Processing one-time donation:', { donorName, donorEmail, amount });
                        
                        if (!donorName.trim()) {
                          alert('Please enter donor name');
                          return;
                        }
                      
                        // Check if donor exists, if not create
                        let donorId = null;
                        let existingDonor = donors.find(d => 
                          (donorEmail && d.email && d.email.toLowerCase() === donorEmail.toLowerCase()) ||
                          d.name.toLowerCase() === donorName.toLowerCase()
                        );
                        
                        if (existingDonor) {
                          donorId = existingDonor.id;
                        } else {
                          // Create new donor
                          const { data: newDonor, error: donorError } = await supabase
                            .from('donors')
                            .insert([{
                              name: donorName,
                              email: donorEmail || '',
                              phone: donorPhone || '',
                              total_donated: 0,
                              donation_count: 0
                            }])
                            .select()
                            .single();
                          
                          console.log('New donor result:', { newDonor, donorError });
                          
                          if (newDonor) {
                            donorId = newDonor.id;
                            existingDonor = {
                              id: newDonor.id,
                              name: newDonor.name,
                              email: newDonor.email,
                              phone: newDonor.phone,
                              address: '',
                              totalDonated: 0,
                              donationCount: 0
                            };
                            setDonors(prev => [...prev, existingDonor]);
                          }
                        }
                        
                        // Create a one-time pledge that's immediately completed
                        const { data: newPledge, error: pledgeError } = await supabase
                          .from('pledges')
                          .insert([{
                            donor_id: donorId,
                            donor_name: donorName,
                            donor_email: donorEmail || '',
                            total_amount: amount,
                            paid_amount: amount,
                            start_date: paymentDate,
                            end_date: paymentDate,
                            frequency: 'one-time',
                            campaign: 'Expansion',
                            status: 'completed'
                          }])
                          .select()
                          .single();
                        
                        console.log('New pledge result:', { newPledge, pledgeError });
                        
                        if (pledgeError) {
                          alert('Error creating donation: ' + pledgeError.message);
                          return;
                        }
                        
                        if (newPledge) {
                          // Create pledge payment record
                          const { data: newPayment } = await supabase
                            .from('pledge_payments')
                            .insert([{
                              pledge_id: newPledge.id,
                              amount: amount,
                              date: paymentDate,
                              method: paymentMethod
                            }])
                            .select()
                            .single();
                          
                          setPledges(prev => [{
                            id: newPledge.id,
                            donorId: newPledge.donor_id,
                            donorName: newPledge.donor_name,
                            donorEmail: newPledge.donor_email,
                            totalAmount: amount,
                            paidAmount: amount,
                            startDate: paymentDate,
                            endDate: paymentDate,
                            frequency: 'one-time',
                            campaign: 'Expansion',
                            status: 'completed',
                            payments: newPayment ? [{
                              id: newPayment.id,
                              amount: amount,
                              date: paymentDate,
                              method: paymentMethod,
                              notes: notes
                            }] : []
                          }, ...prev]);
                          
                          // Update donor stats
                          if (existingDonor) {
                            await supabase
                              .from('donors')
                              .update({
                                total_donated: (existingDonor.totalDonated || 0) + amount,
                                donation_count: (existingDonor.donationCount || 0) + 1
                              })
                              .eq('id', existingDonor.id);
                            
                            setDonors(prev => prev.map(d =>
                              d.id === existingDonor.id
                                ? { ...d, totalDonated: (d.totalDonated || 0) + amount, donationCount: (d.donationCount || 0) + 1 }
                                : d
                            ));
                          }
                          
                          await logActivity('EXPANSION_DONATION', `Received ${formatCurrency(amount)} expansion donation from ${donorName}`, 'pledge', newPledge.id);
                          
                          e.target.reset();
                          alert(`Donation of ${formatCurrency(amount)} recorded successfully!`);
                        }
                      }
                    } catch (error) {
                      console.error('Form submission error:', error);
                      alert('An error occurred: ' + error.message);
                    }
                  }}
                  className="space-y-4"
                >
                  {/* Payment Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div 
                        onClick={() => setExpansionPaymentType('pledge-payment')}
                        className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                          expansionPaymentType === 'pledge-payment' 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-gray-200 hover:border-amber-300'
                        }`}
                      >
                        <input
                          type="hidden"
                          name="paymentType"
                          value={expansionPaymentType}
                        />
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Pledge Payment</p>
                            <p className="text-sm text-gray-500">Payment against existing pledge</p>
                          </div>
                        </div>
                      </div>
                      
                      <div 
                        onClick={() => setExpansionPaymentType('one-time')}
                        className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                          expansionPaymentType === 'one-time' 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-gray-200 hover:border-amber-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">One-Time Donation</p>
                            <p className="text-sm text-gray-500">New expansion donation</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pledge Payment Section */}
                  {expansionPaymentType === 'pledge-payment' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Pledge *</label>
                        <select
                          name="pledgeId"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        >
                          <option value="">-- Select a pledge --</option>
                          {pledges
                            .filter(p => (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && p.status === 'active')
                            .map(p => (
                              <option key={p.id} value={p.id}>
                                {p.donorName} - Pledged: {formatCurrency(p.totalAmount)} | Remaining: {formatCurrency(p.totalAmount - p.paidAmount)}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    </div>
                  )}

                  {/* One-Time Donation Section */}
                  {expansionPaymentType === 'one-time' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Donor Name *</label>
                          <input
                            type="text"
                            name="receiveDonorName"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Full Name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            name="receiveDonorEmail"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            name="receiveDonorPhone"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Common Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="number"
                          name="receiveAmount"
                          required
                          min="0.01"
                          step="0.01"
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                      <input
                        type="date"
                        name="paymentDate"
                        required
                        defaultValue={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Method *</label>
                      <select
                        name="paymentMethod"
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      >
                        <option value="Cash">Cash</option>
                        <option value="Check">Check</option>
                        <option value="Credit Card">Credit Card</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Zelle">Zelle</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Check #</label>
                      <input
                        type="text"
                        name="checkNumber"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Check number"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      name="receiveNotes"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="Optional notes..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 font-medium shadow-lg shadow-amber-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <DollarSign className="w-5 h-5" />
                    Record Payment
                  </button>
                </form>
              </div>
            </div>
            )}

            {/* Reminders Tab */}
            {expansionPortalTab === 'reminders' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-rose-500 to-pink-600 px-6 py-4 text-white">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Pledge Payment Reminders
                </h3>
                <p className="text-rose-100 text-sm">Send friendly reminders for upcoming and overdue pledge payments</p>
              </div>
              
              <div className="p-6">
                {(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const oneWeekFromNow = new Date(today);
                  oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
                  
                  const expansionPledges = pledges.filter(p => 
                    (p.campaign === 'Expansion' || p.campaign === 'Building Fund') && 
                    p.status === 'active'
                  );
                  
                  // Calculate next payment due date based on frequency
                  const getNextDueDate = (pledge) => {
                    const startDate = new Date(pledge.startDate);
                    const endDate = new Date(pledge.endDate);
                    const lastPaymentDate = pledge.payments && pledge.payments.length > 0 
                      ? new Date(Math.max(...pledge.payments.map(p => new Date(p.date))))
                      : startDate;
                    
                    let nextDue = new Date(lastPaymentDate);
                    
                    switch(pledge.frequency) {
                      case 'weekly':
                        nextDue.setDate(nextDue.getDate() + 7);
                        break;
                      case 'monthly':
                        nextDue.setMonth(nextDue.getMonth() + 1);
                        break;
                      case 'quarterly':
                        nextDue.setMonth(nextDue.getMonth() + 3);
                        break;
                      case 'annually':
                        nextDue.setFullYear(nextDue.getFullYear() + 1);
                        break;
                      case 'one-time':
                      default:
                        nextDue = endDate;
                    }
                    
                    return nextDue > endDate ? endDate : nextDue;
                  };
                  
                  // Categorize pledges
                  const overduePledges = expansionPledges.filter(p => {
                    const dueDate = getNextDueDate(p);
                    const paidAmount = parseFloat(p.paidAmount) || 0;
                    const totalAmount = parseFloat(p.totalAmount) || 0;
                    return dueDate < today && paidAmount < totalAmount;
                  });
                  
                  const dueSoonPledges = expansionPledges.filter(p => {
                    const dueDate = getNextDueDate(p);
                    const paidAmount = parseFloat(p.paidAmount) || 0;
                    const totalAmount = parseFloat(p.totalAmount) || 0;
                    return dueDate >= today && dueDate <= oneWeekFromNow && paidAmount < totalAmount;
                  });
                  
                  const sendEmailReminder = async (pledge, type) => {
                    const donor = donors.find(d => d.id === pledge.donorId);
                    const email = donor?.email || pledge.donorEmail;
                    
                    if (!email) {
                      alert('No email address found for this donor');
                      return;
                    }
                    
                    const remaining = (parseFloat(pledge.totalAmount) || 0) - (parseFloat(pledge.paidAmount) || 0);
                    const dueDate = getNextDueDate(pledge);
                    
                    const subject = type === 'overdue' 
                      ? `Friendly Reminder: Pledge Payment Overdue - ${settings.organizationName}`
                      : `Upcoming Pledge Payment Reminder - ${settings.organizationName}`;
                    
                    const message = type === 'overdue'
                      ? `Dear ${pledge.donorName},\n\nWe hope this message finds you well. This is a friendly reminder that your pledge payment is currently overdue.\n\nPledge Details:\n- Total Pledged: ${formatCurrency(pledge.totalAmount)}\n- Amount Paid: ${formatCurrency(pledge.paidAmount)}\n- Remaining Balance: ${formatCurrency(remaining)}\n- Due Date: ${formatDate(dueDate)}\n\nWe understand that circumstances can change, and we're here to help. If you have any questions or need to adjust your payment schedule, please don't hesitate to reach out.\n\nThank you for your continued support of ${settings.organizationName}.\n\nWith gratitude,\n${settings.organizationName}`
                      : `Dear ${pledge.donorName},\n\nWe hope this message finds you well. This is a friendly reminder that your pledge payment is coming up soon.\n\nPledge Details:\n- Total Pledged: ${formatCurrency(pledge.totalAmount)}\n- Amount Paid: ${formatCurrency(pledge.paidAmount)}\n- Remaining Balance: ${formatCurrency(remaining)}\n- Due Date: ${formatDate(dueDate)}\n\nThank you for your generous commitment to ${settings.organizationName}. Your support makes a real difference!\n\nWith gratitude,\n${settings.organizationName}`;
                    
                    try {
                      const response = await fetch('/api/send-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          to: email,
                          subject: subject,
                          text: message,
                          html: message.replace(/\n/g, '<br>')
                        })
                      });
                      
                      if (response.ok) {
                        alert(`Email reminder sent to ${email}`);
                        await logActivity('SEND_REMINDER', `Sent ${type} email reminder to ${pledge.donorName}`, 'pledge', pledge.id);
                      } else {
                        alert('Failed to send email. Please try again.');
                      }
                    } catch (error) {
                      console.error('Email error:', error);
                      alert('Failed to send email: ' + error.message);
                    }
                  };
                  
                  const sendSMSReminder = async (pledge, type) => {
                    const donor = donors.find(d => d.id === pledge.donorId);
                    const phone = donor?.phone || '';
                    
                    if (!phone) {
                      alert('No phone number found for this donor');
                      return;
                    }
                    
                    const remaining = (parseFloat(pledge.totalAmount) || 0) - (parseFloat(pledge.paidAmount) || 0);
                    const firstName = pledge.donorName.split(' ')[0];
                    const projectName = pledge.campaign || 'General Fund';
                    
                    const message = `Assalamu Alaikum ${firstName},\nWe hope this message finds you in the best of health and iman.\nThis is a friendly reminder regarding your generous pledge of ${formatCurrency(pledge.totalAmount)} toward the ${projectName}. Your support is the backbone of our community, and we are eager to move forward with our goals.\nYou can fulfill your pledge here: https://clemmonsislamiccenter.org/donate-online/\nJazakum Allahu Khairan for your continued support!`;
                    
                    if (!confirm(`Send SMS to ${phone}?\n\nMessage:\n"${message}"`)) return;
                    
                    try {
                      const response = await fetch('/api/send-sms', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          to: phone,
                          message: message
                        })
                      });
                      
                      const result = await response.json();
                      
                      if (response.ok && result.success) {
                        alert(`SMS sent to ${phone}!${result.quotaRemaining !== undefined ? `\n(${result.quotaRemaining} free SMS remaining today)` : ''}`);
                        await logActivity('SEND_SMS_REMINDER', `Sent ${type} SMS reminder to ${pledge.donorName}`, 'pledge', pledge.id);
                      } else {
                        alert('Failed to send SMS: ' + (result.error || 'Unknown error'));
                      }
                    } catch (error) {
                      console.error('SMS error:', error);
                      alert('Failed to send SMS: ' + error.message);
                    }
                  };
                  
                  return (
                    <div className="space-y-6">
                      {/* Overdue Pledges */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <h4 className="font-semibold text-gray-900">Overdue Payments ({overduePledges.length})</h4>
                        </div>
                        
                        {overduePledges.length === 0 ? (
                          <p className="text-gray-500 text-sm pl-5">No overdue payments 🎉</p>
                        ) : (
                          <div className="space-y-2">
                            {overduePledges.map(pledge => {
                              const remaining = (parseFloat(pledge.totalAmount) || 0) - (parseFloat(pledge.paidAmount) || 0);
                              const dueDate = getNextDueDate(pledge);
                              const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                              const donor = donors.find(d => d.id === pledge.donorId);
                              
                              return (
                                <div key={pledge.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-gray-900">{pledge.donorName}</p>
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                        {daysOverdue} days overdue
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                      Remaining: {formatCurrency(remaining)} • Due: {formatDate(dueDate)}
                                    </p>
                                    <div className="flex gap-2 text-xs text-gray-500 mt-1">
                                      {donor?.email && <span>📧 {donor.email}</span>}
                                      {donor?.phone && <span>📱 {donor.phone}</span>}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => sendEmailReminder(pledge, 'overdue')}
                                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
                                      title="Send Email Reminder"
                                    >
                                      <Mail className="w-4 h-4" />
                                      Email
                                    </button>
                                    <button
                                      onClick={() => sendSMSReminder(pledge, 'overdue')}
                                      className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 flex items-center gap-1"
                                      title="Send SMS Reminder"
                                    >
                                      <Phone className="w-4 h-4" />
                                      SMS
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      
                      {/* Due Within Week */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                          <h4 className="font-semibold text-gray-900">Due Within 7 Days ({dueSoonPledges.length})</h4>
                        </div>
                        
                        {dueSoonPledges.length === 0 ? (
                          <p className="text-gray-500 text-sm pl-5">No payments due in the next 7 days</p>
                        ) : (
                          <div className="space-y-2">
                            {dueSoonPledges.map(pledge => {
                              const remaining = (parseFloat(pledge.totalAmount) || 0) - (parseFloat(pledge.paidAmount) || 0);
                              const dueDate = getNextDueDate(pledge);
                              const daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
                              const donor = donors.find(d => d.id === pledge.donorId);
                              
                              return (
                                <div key={pledge.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-gray-900">{pledge.donorName}</p>
                                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                        {daysUntilDue === 0 ? 'Due today' : `Due in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                      Remaining: {formatCurrency(remaining)} • Due: {formatDate(dueDate)}
                                    </p>
                                    <div className="flex gap-2 text-xs text-gray-500 mt-1">
                                      {donor?.email && <span>📧 {donor.email}</span>}
                                      {donor?.phone && <span>📱 {donor.phone}</span>}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => sendEmailReminder(pledge, 'upcoming')}
                                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
                                      title="Send Email Reminder"
                                    >
                                      <Mail className="w-4 h-4" />
                                      Email
                                    </button>
                                    <button
                                      onClick={() => sendSMSReminder(pledge, 'upcoming')}
                                      className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 flex items-center gap-1"
                                      title="Send SMS Reminder"
                                    >
                                      <Phone className="w-4 h-4" />
                                      SMS
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      
                      {/* Send All Reminders */}
                      {(overduePledges.length > 0 || dueSoonPledges.length > 0) && (
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex gap-3">
                            <button
                              onClick={async () => {
                                if (!confirm(`Send email reminders to all ${overduePledges.length + dueSoonPledges.length} pledges?`)) return;
                                for (const pledge of overduePledges) {
                                  await sendEmailReminder(pledge, 'overdue');
                                }
                                for (const pledge of dueSoonPledges) {
                                  await sendEmailReminder(pledge, 'upcoming');
                                }
                              }}
                              className="flex-1 py-2.5 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium flex items-center justify-center gap-2"
                            >
                              <Mail className="w-5 h-5" />
                              Email All Reminders
                            </button>
                            <button
                              onClick={async () => {
                                if (!confirm(`Send SMS reminders to all ${overduePledges.length} overdue pledges?`)) return;
                                
                                let sent = 0;
                                let failed = 0;
                                
                                for (const pledge of overduePledges) {
                                  const donor = donors.find(d => d.id === pledge.donorId);
                                  const phone = donor?.phone || '';
                                  
                                  if (!phone) {
                                    failed++;
                                    continue;
                                  }
                                  
                                  const firstName = pledge.donorName.split(' ')[0];
                                  const projectName = pledge.campaign || 'General Fund';
                                  const message = `Assalamu Alaikum ${firstName},\nWe hope this message finds you in the best of health and iman.\nThis is a friendly reminder regarding your generous pledge of ${formatCurrency(pledge.totalAmount)} toward the ${projectName}. Your support is the backbone of our community, and we are eager to move forward with our goals.\nYou can fulfill your pledge here: https://clemmonsislamiccenter.org/donate-online/\nJazakum Allahu Khairan for your continued support!`;
                                  
                                  try {
                                    const response = await fetch('/api/send-sms', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ to: phone, message })
                                    });
                                    const result = await response.json();
                                    if (result.success) {
                                      sent++;
                                      await logActivity('SEND_SMS_REMINDER', `Sent overdue SMS reminder to ${pledge.donorName}`, 'pledge', pledge.id);
                                    } else {
                                      failed++;
                                    }
                                  } catch {
                                    failed++;
                                  }
                                }
                                
                                alert(`SMS reminders sent!\n✓ Sent: ${sent}\n✗ Failed: ${failed}`);
                              }}
                              className="flex-1 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 font-medium shadow-lg shadow-rose-500/25 flex items-center justify-center gap-2"
                            >
                              <Phone className="w-5 h-5" />
                              SMS All Overdue
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            )}

            {/* Expansion Pledge Receipt Modal */}
            {expansionPledgeReceipt && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 text-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-6 h-6" />
                      <div>
                        <h3 className="font-bold text-lg">Pledge Completion Receipt</h3>
                        <p className="text-emerald-100 text-sm">Tax-deductible donation receipt</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpansionPledgeReceipt(null)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Receipt Content */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]" id="expansion-receipt-content">
                    {/* Organization Header */}
                    <div className="text-center border-b-2 border-gray-200 pb-6 mb-6">
                      {settings.logo && (
                        <img src={settings.logo} alt="Logo" className="h-16 mx-auto mb-3" />
                      )}
                      <h1 className="text-2xl font-bold text-gray-900">{settings.organizationName || 'Organization Name'}</h1>
                      <p className="text-gray-600 mt-1">{settings.address || 'Organization Address'}</p>
                      <div className="flex items-center justify-center gap-4 mt-2 text-sm text-gray-500">
                        {settings.phone && <span>📞 {settings.phone}</span>}
                        {settings.email && <span>✉️ {settings.email}</span>}
                      </div>
                      {settings.website && (
                        <p className="text-sm text-emerald-600 mt-1">🌐 {settings.website}</p>
                      )}
                      {settings.taxId && (
                        <p className="text-sm text-gray-500 mt-2">Tax ID: {settings.taxId}</p>
                      )}
                    </div>
                    
                    {/* Receipt Title */}
                    <div className="text-center mb-6">
                      <h2 className="text-xl font-bold text-emerald-700">DONATION RECEIPT</h2>
                      <p className="text-gray-500 text-sm">Receipt #{expansionPledgeReceipt.id?.slice(-8).toUpperCase() || 'N/A'}</p>
                      <p className="text-gray-500 text-sm">Date: {formatDate(new Date().toISOString().split('T')[0])}</p>
                    </div>
                    
                    {/* Donor Information */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Donor Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Name</p>
                          <p className="font-medium text-gray-900">{expansionPledgeReceipt.donorName}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Email</p>
                          <p className="font-medium text-gray-900">{expansionPledgeReceipt.donorEmail || 'N/A'}</p>
                        </div>
                        {(() => {
                          const donor = donors.find(d => d.id === expansionPledgeReceipt.donorId);
                          return donor?.phone ? (
                            <div>
                              <p className="text-gray-500">Phone</p>
                              <p className="font-medium text-gray-900">{donor.phone}</p>
                            </div>
                          ) : null;
                        })()}
                        {(() => {
                          const donor = donors.find(d => d.id === expansionPledgeReceipt.donorId);
                          return donor?.address ? (
                            <div>
                              <p className="text-gray-500">Address</p>
                              <p className="font-medium text-gray-900">{donor.address}</p>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    
                    {/* Pledge Details */}
                    <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                      <h3 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Pledge Details
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                          <span className="text-gray-600">Campaign</span>
                          <span className="font-semibold text-gray-900">{expansionPledgeReceipt.campaign}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                          <span className="text-gray-600">Pledge Period</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(expansionPledgeReceipt.startDate)} - {formatDate(expansionPledgeReceipt.endDate)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                          <span className="text-gray-600">Frequency</span>
                          <span className="font-medium text-gray-900 capitalize">{expansionPledgeReceipt.frequency}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                          <span className="text-gray-600">Total Pledged</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(expansionPledgeReceipt.totalAmount)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                          <span className="text-gray-600">Total Paid</span>
                          <span className="font-semibold text-emerald-600">{formatCurrency(expansionPledgeReceipt.paidAmount)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Status</span>
                          <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-medium rounded-full">
                            ✓ Completed
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Tax Deduction Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                      <p className="text-sm text-amber-800">
                        <strong>Tax Deduction Notice:</strong> This receipt confirms your charitable contribution. 
                        No goods or services were provided in exchange for this donation. 
                        Please retain this receipt for your tax records.
                      </p>
                    </div>
                    
                    {/* Islamic Message */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 mb-6">
                      <div className="text-center">
                        <p className="text-emerald-800 font-semibold text-lg mb-3">
                          Assalamu Alaikum {expansionPledgeReceipt.donorName.split(' ')[0]},
                        </p>
                        <p className="text-gray-700 italic mb-4 text-sm leading-relaxed">
                          The Prophet (peace be upon him) said: "The most beloved of deeds to Allah are those that are most consistent, even if they are small."
                        </p>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          We are so grateful for your pledge of <strong>{formatCurrency(expansionPledgeReceipt.totalAmount)}</strong> to support Clemmons Islamic Center. Your contribution is a Sadaqah that will benefit our community for generations to come.
                        </p>
                      </div>
                    </div>
                    
                    {/* Thank You Message */}
                    <div className="text-center text-gray-600">
                      <p className="text-lg font-medium">Jazakum Allahu Khairan!</p>
                      <p className="text-sm mt-1">May Allah accept your contribution and reward you abundantly.</p>
                    </div>
                  </div>
                  
                  {/* Modal Footer */}
                  <div className="bg-gray-50 px-6 py-4 flex gap-3">
                    <button
                      onClick={() => setExpansionPledgeReceipt(null)}
                      className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={async () => {
                        const donorEmail = expansionPledgeReceipt.donorEmail;
                        if (!donorEmail) {
                          alert('No email address found for this donor. Please add an email first.');
                          return;
                        }
                        
                        if (!confirm(`Send receipt to ${donorEmail}?`)) return;
                        
                        const receiptHtml = `
                          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                            <div style="text-align: center; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px;">
                              <h1 style="color: #111827; margin: 0;">${settings.organizationName || 'Clemmons Islamic Center'}</h1>
                              <p style="color: #6b7280; margin: 5px 0;">${settings.address || ''}</p>
                              <p style="color: #6b7280; margin: 5px 0;">${settings.phone ? '📞 ' + settings.phone : ''} ${settings.email ? '✉️ ' + settings.email : ''}</p>
                              ${settings.taxId ? '<p style="color: #6b7280; margin: 5px 0;">Tax ID: ' + settings.taxId + '</p>' : ''}
                            </div>
                            
                            <div style="text-align: center; margin-bottom: 20px;">
                              <h2 style="color: #047857; margin: 0;">DONATION RECEIPT</h2>
                              <p style="color: #6b7280; font-size: 14px;">Receipt #${expansionPledgeReceipt.id?.slice(-8).toUpperCase() || 'N/A'}</p>
                              <p style="color: #6b7280; font-size: 14px;">Date: ${formatDate(new Date().toISOString().split('T')[0])}</p>
                            </div>
                            
                            <div style="background: #f9fafb; border-radius: 10px; padding: 15px; margin-bottom: 20px;">
                              <h3 style="color: #374151; margin: 0 0 10px 0;">Donor Information</h3>
                              <p style="margin: 5px 0;"><strong>Name:</strong> ${expansionPledgeReceipt.donorName}</p>
                              <p style="margin: 5px 0;"><strong>Email:</strong> ${expansionPledgeReceipt.donorEmail || 'N/A'}</p>
                            </div>
                            
                            <div style="background: #ecfdf5; border-radius: 10px; padding: 15px; margin-bottom: 20px;">
                              <h3 style="color: #047857; margin: 0 0 10px 0;">Pledge Details</h3>
                              <p style="margin: 5px 0;"><strong>Campaign:</strong> ${expansionPledgeReceipt.campaign}</p>
                              <p style="margin: 5px 0;"><strong>Period:</strong> ${formatDate(expansionPledgeReceipt.startDate)} - ${formatDate(expansionPledgeReceipt.endDate)}</p>
                              <p style="margin: 5px 0;"><strong>Frequency:</strong> ${expansionPledgeReceipt.frequency}</p>
                              <p style="margin: 5px 0;"><strong>Total Pledged:</strong> ${formatCurrency(expansionPledgeReceipt.totalAmount)}</p>
                              <p style="margin: 5px 0;"><strong>Total Paid:</strong> <span style="color: #059669;">${formatCurrency(expansionPledgeReceipt.paidAmount)}</span></p>
                            </div>
                            
                            <div style="background: #fffbeb; border: 1px solid #fcd34d; border-radius: 10px; padding: 15px; margin-bottom: 20px;">
                              <p style="color: #92400e; font-size: 14px; margin: 0;">
                                <strong>Tax Deduction Notice:</strong> This receipt confirms your charitable contribution. No goods or services were provided in exchange for this donation. Please retain this receipt for your tax records.
                              </p>
                            </div>
                            
                            <div style="background: linear-gradient(135deg, #ecfdf5, #f0fdfa); border: 1px solid #a7f3d0; border-radius: 10px; padding: 20px; margin-bottom: 20px; text-align: center;">
                              <p style="color: #047857; font-size: 18px; font-weight: bold; margin: 0 0 10px 0;">
                                Assalamu Alaikum ${expansionPledgeReceipt.donorName.split(' ')[0]},
                              </p>
                              <p style="color: #374151; font-style: italic; margin: 0 0 15px 0; font-size: 14px;">
                                The Prophet (peace be upon him) said: "The most beloved of deeds to Allah are those that are most consistent, even if they are small."
                              </p>
                              <p style="color: #374151; margin: 0; font-size: 14px;">
                                We are so grateful for your pledge of <strong>${formatCurrency(expansionPledgeReceipt.totalAmount)}</strong> to support Clemmons Islamic Center. Your contribution is a Sadaqah that will benefit our community for generations to come.
                              </p>
                            </div>
                            
                            <div style="text-align: center; color: #4b5563;">
                              <p style="font-size: 18px; font-weight: bold; margin: 0;">Jazakum Allahu Khairan!</p>
                              <p style="font-size: 14px; margin: 5px 0;">May Allah accept your contribution and reward you abundantly.</p>
                            </div>
                          </div>
                        `;
                        
                        try {
                          const response = await fetch('/api/send-email', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              to: donorEmail,
                              subject: `Donation Receipt - ${settings.organizationName || 'Clemmons Islamic Center'}`,
                              body: `Donation Receipt for ${expansionPledgeReceipt.donorName}`,
                              html: receiptHtml
                            })
                          });
                          
                          const result = await response.json();
                          
                          if (response.ok && result.success) {
                            alert('Receipt emailed successfully to ' + donorEmail);
                            await logActivity('EMAIL_RECEIPT', `Emailed receipt to ${expansionPledgeReceipt.donorName} (${donorEmail})`, 'pledge', expansionPledgeReceipt.id);
                          } else {
                            alert('Failed to send email: ' + (result.error || 'Unknown error'));
                          }
                        } catch (error) {
                          console.error('Email error:', error);
                          alert('Failed to send email: ' + error.message);
                        }
                      }}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-medium shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 transition-all"
                    >
                      <Mail className="w-4 h-4" />
                      Email Receipt
                    </button>
                    <button
                      onClick={() => {
                        const content = document.getElementById('expansion-receipt-content');
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>Donation Receipt - ${expansionPledgeReceipt.donorName}</title>
                              <style>
                                body { font-family: Arial, sans-serif; padding: 40px; max-width: 700px; margin: 0 auto; }
                                .text-center { text-align: center; }
                                .font-bold { font-weight: bold; }
                                .text-2xl { font-size: 1.5rem; }
                                .text-xl { font-size: 1.25rem; }
                                .text-lg { font-size: 1.1rem; }
                                .text-sm { font-size: 0.875rem; }
                                .mt-1 { margin-top: 0.25rem; }
                                .mt-2 { margin-top: 0.5rem; }
                                .mb-3 { margin-bottom: 0.75rem; }
                                .mb-4 { margin-bottom: 1rem; }
                                .mb-6 { margin-bottom: 1.5rem; }
                                .pb-6 { padding-bottom: 1.5rem; }
                                .p-4 { padding: 1rem; }
                                .p-5 { padding: 1.25rem; }
                                .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
                                .border-b { border-bottom: 1px solid #e5e7eb; }
                                .border-b-2 { border-bottom: 2px solid #e5e7eb; }
                                .border { border: 1px solid #a7f3d0; }
                                .rounded-xl { border-radius: 0.75rem; }
                                .bg-gray-50 { background-color: #f9fafb; }
                                .bg-emerald-50 { background-color: #ecfdf5; }
                                .bg-amber-50 { background-color: #fffbeb; }
                                .bg-gradient-to-br { background: linear-gradient(135deg, #ecfdf5, #f0fdfa); }
                                .text-gray-500 { color: #6b7280; }
                                .text-gray-600 { color: #4b5563; }
                                .text-gray-700 { color: #374151; }
                                .text-gray-900 { color: #111827; }
                                .text-emerald-600 { color: #059669; }
                                .text-emerald-700 { color: #047857; }
                                .text-emerald-800 { color: #065f46; }
                                .text-amber-800 { color: #92400e; }
                                .flex { display: flex; }
                                .justify-between { justify-content: space-between; }
                                .items-center { align-items: center; }
                                .gap-4 { gap: 1rem; }
                                .grid { display: grid; }
                                .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
                                .space-y-3 > * + * { margin-top: 0.75rem; }
                                .leading-relaxed { line-height: 1.625; }
                                .italic { font-style: italic; }
                                img { max-height: 64px; }
                                @media print { body { padding: 20px; } }
                              </style>
                            </head>
                            <body>${content.innerHTML}</body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.print();
                      }}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 font-medium shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2 transition-all"
                    >
                      <Printer className="w-4 h-4" />
                      Print Receipt
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pledges Tab */}
        {activeTab === 'pledges' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Pledges</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {pledges.filter(p => p.status !== 'completed').length} active • {pledges.filter(p => p.status === 'completed').length} completed
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportPledgesToCSV}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
                <button
                  onClick={exportPledgesToExcel}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
                <button
                  onClick={() => openImportModal('pledges')}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Import
                </button>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setPledgeViewFilter('all')}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${
                      pledgeViewFilter === 'all' 
                        ? 'bg-blue-600 text-white shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    All
                  </button>
                  <button
                    onClick={() => setPledgeViewFilter('active')}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${
                      pledgeViewFilter === 'active' 
                        ? 'bg-amber-500 text-white shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    Active
                  </button>
                  <button
                    onClick={() => setPledgeViewFilter('completed')}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${
                      pledgeViewFilter === 'completed' 
                        ? 'bg-emerald-600 text-white shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Completed
                  </button>
                </div>
                <button
                  onClick={() => { setEditingItem(null); setShowPledgeModal(true); }}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Pledge
                </button>
              </div>
            </div>

            {/* Summary Cards - Show stats based on current view */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Pledged</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(pledges.reduce((sum, p) => sum + p.totalAmount, 0))}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Collected</p>
                <p className="text-xl font-bold text-emerald-600 mt-1">{formatCurrency(pledges.reduce((sum, p) => sum + p.paidAmount, 0))}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Outstanding</p>
                <p className="text-xl font-bold text-orange-600 mt-1">{formatCurrency(pledges.reduce((sum, p) => sum + (p.totalAmount - p.paidAmount), 0))}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Collection Rate</p>
                <p className="text-xl font-bold text-blue-600 mt-1">
                  {pledges.reduce((sum, p) => sum + p.totalAmount, 0) > 0 
                    ? Math.round((pledges.reduce((sum, p) => sum + p.paidAmount, 0) / pledges.reduce((sum, p) => sum + p.totalAmount, 0)) * 100) 
                    : 0}%
                </p>
              </div>
            </div>

            {/* Pledges Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Donor</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Campaign</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Pledged</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Paid</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Period</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pledges
                      .filter(p => {
                        if (pledgeViewFilter === 'all') return true;
                        if (pledgeViewFilter === 'active') return p.status !== 'completed';
                        if (pledgeViewFilter === 'completed') return p.status === 'completed';
                        return true;
                      })
                      .map(pledge => {
                        const progress = (pledge.paidAmount / pledge.totalAmount) * 100;
                        const remaining = pledge.totalAmount - pledge.paidAmount;
                        const isOverdue = new Date(pledge.endDate) < new Date() && pledge.status !== 'completed';
                        const donor = donors.find(d => d.id === pledge.donorId);
                        
                        return (
                          <tr key={pledge.id} className="hover:bg-gray-50 group">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-emerald-700 font-semibold text-xs">
                                    {pledge.donorName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </span>
                              </div>
                              <div className="min-w-0 relative group/tooltip">
                                <p className="font-medium text-gray-900 text-sm truncate cursor-pointer hover:text-emerald-600">
                                  {pledge.donorName}
                                  {(pledge.pledgeForm || pledge.responsible || pledge.pledgeNotes) && (
                                    <span className="ml-1 text-blue-500 text-xs">ℹ️</span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">{pledge.frequency}</p>
                                {(pledge.pledgeForm || pledge.responsible || pledge.pledgeNotes) && (
                                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover/tooltip:block z-20">
                                    <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                      {pledge.responsible && (
                                        <p className="flex items-center gap-2 mb-1">
                                          <span className="text-amber-400">👤 Responsible:</span> {pledge.responsible}
                                        </p>
                                      )}
                                      {pledge.pledgeForm && (
                                        <p className="flex items-center gap-2 mb-1">
                                          <span className="text-emerald-400">📋 Pledge Form:</span> {pledge.pledgeForm === 'Y' ? 'Yes' : pledge.pledgeForm === 'N' ? 'No' : pledge.pledgeForm}
                                        </p>
                                      )}
                                      {pledge.pledgeNotes && (
                                        <p className="flex items-center gap-2 max-w-xs">
                                          <span className="text-blue-400">📝 Notes:</span> <span className="whitespace-normal">{pledge.pledgeNotes}</span>
                                        </p>
                                      )}
                                    </div>
                                    <div className="absolute left-4 -bottom-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{pledge.campaign}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              pledge.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              pledge.status === 'active' ? 'bg-blue-100 text-blue-700' :
                              pledge.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {pledge.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {pledge.status === 'active' && <Clock className="w-3 h-3 mr-1" />}
                              {isOverdue && <AlertCircle className="w-3 h-3 mr-1" />}
                              {isOverdue ? 'Overdue' : pledge.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-medium text-gray-900">{formatCurrency(pledge.totalAmount)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-medium text-emerald-600">{formatCurrency(pledge.paidAmount)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`text-sm font-medium ${remaining > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                              {remaining > 0 ? formatCurrency(remaining) : 'Paid'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden min-w-[60px]">
                                <div 
                                  className={`h-full rounded-full ${progress >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                  style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 w-8">{Math.round(progress)}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="text-xs text-gray-500">
                              <p>{formatDate(pledge.startDate)}</p>
                              <p className="text-gray-400">to {formatDate(pledge.endDate)}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {pledge.status !== 'completed' && (
                                <button
                                  onClick={() => { setSelectedPledge(pledge); setShowPledgePaymentModal(true); }}
                                  className="p-1.5 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                                  title="Record Payment"
                                >
                                  <DollarSign className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => { setEditingItem(pledge); setShowPledgeModal(true); }}
                                className="p-1.5 bg-amber-100 text-amber-600 hover:bg-amber-200 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeletePledge(pledge.id)}
                                className="p-1.5 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => {
                                  const donor = donors.find(d => d.id === pledge.donorId);
                                  const remainingBalance = pledge.totalAmount - pledge.paidAmount;
                                  const progress = (pledge.paidAmount / pledge.totalAmount) * 100;
                                  const receiptNumber = `PSR-${pledge.id.slice(0, 8).toUpperCase()}-${new Date().getFullYear()}`;
                                  
                                  const subject = `Pledge Summary - ${receiptNumber} - ${settings.organizationName}`;
                                  const body = 
`Dear ${donor?.name || pledge.donorName},

Thank you for your continued support of ${settings.organizationName}!

PLEDGE SUMMARY
==============
Receipt Number: ${receiptNumber}
Date: ${formatDate(new Date().toISOString())}

Donor Information:
${donor?.name || pledge.donorName}
${donor?.address || ''}
${donor?.email || ''}
${donor?.phone || ''}

Pledge Information:
-------------------
Campaign: ${pledge.campaign}
Pledge Period: ${formatDate(pledge.startDate)} - ${formatDate(pledge.endDate)}
Frequency: ${pledge.frequency}

====================================
        PLEDGE SUMMARY
====================================
Total Amount Pledged:  ${formatCurrency(pledge.totalAmount)}
Total Payments Made:   ${formatCurrency(pledge.paidAmount)}
Pledge Remaining:      ${remainingBalance > 0 ? formatCurrency(remainingBalance) : 'PAID IN FULL'}
====================================

Pledge Progress: ${Math.round(progress)}%

Status: ${pledge.status.toUpperCase()}

"When a man dies, his deeds come to an end except for three: Sadaqah Jariyah (continuous charity), knowledge which is beneficial, or a virtuous descendant who prays for him."

Thank you for your generous support!

${settings.organizationName}
${settings.address}
Tax ID: ${settings.taxId}
${settings.phone || ''}
${settings.email || ''}
`;
                                  sendEmail(pledge.donorEmail || donor?.email || '', donor?.name || pledge.donorName, subject, body);
                                }}
                                className="p-1.5 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg transition-colors"
                                title="Email Pledge Summary"
                              >
                                <Mail className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => {
                                  const donor = donors.find(d => d.id === pledge.donorId);
                                  setPledgeSummaryReceipt({
                                    pledge: pledge,
                                    donor: donor
                                  });
                                  setShowPledgeSummaryModal(true);
                                }}
                                className="p-1.5 bg-purple-100 text-purple-600 hover:bg-purple-200 rounded-lg transition-colors"
                                title="Print Pledge Summary"
                              >
                                <Printer className="w-3.5 h-3.5" />
                              </button>
                              {pledge.payments && pledge.payments.length > 0 && (
                                <button
                                  onClick={() => {
                                    const lastPayment = pledge.payments[pledge.payments.length - 1];
                                    const previousPayments = pledge.payments.slice(0, -1).reduce((sum, p) => sum + p.amount, 0);
                                    const paidAfterThis = previousPayments + lastPayment.amount;
                                    const remainingAfterThis = pledge.totalAmount - paidAfterThis;
                                    const donor = donors.find(d => d.id === pledge.donorId);
                                    
                                    setPledgePaymentReceipt({
                                      pledge: { ...pledge, paidAmount: previousPayments },
                                      donor: donor,
                                      paymentAmount: lastPayment.amount,
                                      newPaidAmount: paidAfterThis,
                                      remainingBalance: remainingAfterThis,
                                      paymentDate: lastPayment.date,
                                      receiptId: `${pledge.id}-${pledge.payments.length - 1}`
                                    });
                                    setShowPledgeReceiptModal(true);
                                  }}
                                  className="p-1.5 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg transition-colors"
                                  title="View Last Receipt"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {pledges.filter(p => {
                if (pledgeViewFilter === 'all') return true;
                if (pledgeViewFilter === 'active') return p.status !== 'completed';
                if (pledgeViewFilter === 'completed') return p.status === 'completed';
                return true;
              }).length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {pledgeViewFilter === 'all' && 'No pledges yet. Add your first pledge to get started.'}
                    {pledgeViewFilter === 'active' && 'No active pledges. All pledges have been completed!'}
                    {pledgeViewFilter === 'completed' && 'No completed pledges yet. Pledges will appear here once fully paid.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Donors Tab */}
        {activeTab === 'donors' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Donors</h2>
              <div className="flex gap-2">
                <button
                  onClick={exportDonorsToCSV}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
                <button
                  onClick={exportDonorsToExcel}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
                <button
                  onClick={() => openImportModal('donors')}
                  className="bg-white text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300 text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Import
                </button>
                <button
                  onClick={() => { setEditingItem(null); setShowDonorModal(true); }}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Donor
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Donor</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Address</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">Donations</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">Total Donated</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {donors.map(donor => (
                    <tr key={donor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-emerald-100 p-2 rounded-full">
                            <Users className="w-4 h-4 text-emerald-600" />
                          </div>
                          <span className="font-medium text-gray-900">{donor.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1 text-sm text-gray-600">
                          {donor.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              {donor.email}
                            </div>
                          )}
                          {donor.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {donor.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {donor.address && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate max-w-xs">{donor.address}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {donor.donationCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="font-semibold text-emerald-600">{formatCurrency(donor.totalDonated)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => { setEditingItem(donor); setShowDonorModal(true); }}
                            className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDonor(donor.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {donors.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No donors yet. Add your first donor to get started.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Fundraising Goals</h2>
              <button
                onClick={() => { setEditingItem(null); setShowGoalModal(true); }}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" />
                Add Goal
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {goals.map(goal => {
                const progress = (goal.current / goal.target) * 100;
                const isOverdue = new Date(goal.deadline) < new Date() && progress < 100;
                
                return (
                  <div key={goal.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">{goal.name}</h3>
                        <p className="text-sm text-gray-500">{goal.campaign}</p>
                      </div>
                      {progress >= 100 ? (
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                          Completed
                        </span>
                      ) : isOverdue ? (
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                          Overdue
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                          In Progress
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm mb-4">{goal.description}</p>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Progress</span>
                        <span className="font-medium">
                          {formatCurrency(goal.current)} / {formatCurrency(goal.target)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${progress >= 100 ? 'bg-emerald-600' : 'bg-blue-600'}`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>{Math.round(progress)}% complete</span>
                        <span>Due: {formatDate(goal.deadline)}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingItem(goal); setShowGoalModal(true); }}
                        className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteGoal(goal.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bank Activity Tab */}
        {activeTab === 'bank-activity' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  Bank Activity
                </h2>
                <p className="text-gray-500 text-sm mt-1">Track all deposits and credits by category</p>
              </div>
              <button
                onClick={() => {
                  setEditingBankDeposit(null);
                  setShowBankDepositModal(true);
                }}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-2.5 rounded-xl flex items-center gap-2 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/25 transition-all"
              >
                <Plus className="w-5 h-5" />
                Add Deposit
              </button>
            </div>

            {/* Filters Row */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Month Picker */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                  <input
                    type="month"
                    value={bankActivityMonth}
                    onChange={(e) => setBankActivityMonth(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                
                {/* Type Filter */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Deposit Type</label>
                  <select
                    value={bankActivityFilter}
                    onChange={(e) => setBankActivityFilter(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="all">All Types</option>
                    <option value="cash">Cash Deposits</option>
                    <option value="check">Check Deposits</option>
                    <option value="zelle">Zelle Transfers</option>
                    <option value="credit_card">Credit Card</option>
                  </select>
                </div>
                
                {/* Quick Month Navigation */}
                <div className="flex items-end gap-2">
                  <button
                    onClick={() => {
                      const date = new Date(bankActivityMonth + '-01');
                      date.setMonth(date.getMonth() - 1);
                      setBankActivityMonth(date.toISOString().slice(0, 7));
                    }}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => setBankActivityMonth(new Date().toISOString().slice(0, 7))}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => {
                      const date = new Date(bankActivityMonth + '-01');
                      date.setMonth(date.getMonth() + 1);
                      setBankActivityMonth(date.toISOString().slice(0, 7));
                    }}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            {(() => {
              const [year, month] = bankActivityMonth.split('-');
              const monthDeposits = bankDeposits.filter(d => {
                const depositDate = new Date(d.date);
                return depositDate.getFullYear() === parseInt(year) && 
                       (depositDate.getMonth() + 1) === parseInt(month);
              });
              
              const cashTotal = monthDeposits.filter(d => d.type === 'cash').reduce((sum, d) => sum + d.amount, 0);
              const checkTotal = monthDeposits.filter(d => d.type === 'check').reduce((sum, d) => sum + d.amount, 0);
              const zelleTotal = monthDeposits.filter(d => d.type === 'zelle').reduce((sum, d) => sum + d.amount, 0);
              const creditCardTotal = monthDeposits.filter(d => d.type === 'credit_card').reduce((sum, d) => sum + d.amount, 0);
              const grandTotal = cashTotal + checkTotal + zelleTotal + creditCardTotal;
              
              const monthName = new Date(year, parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
              
              return (
                <>
                  {/* Month Title */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-800">{monthName}</h3>
                    <p className="text-gray-500 text-sm">Bank Activity Summary</p>
                  </div>
                  
                  {/* Summary Cards Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Cash */}
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg shadow-green-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-xl">💵</span>
                        </div>
                        <span className="font-medium">Cash</span>
                      </div>
                      <p className="text-2xl font-bold">{formatCurrency(cashTotal)}</p>
                      <p className="text-green-100 text-sm mt-1">{monthDeposits.filter(d => d.type === 'cash').length} deposits</p>
                    </div>
                    
                    {/* Checks */}
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white shadow-lg shadow-blue-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-xl">📝</span>
                        </div>
                        <span className="font-medium">Checks</span>
                      </div>
                      <p className="text-2xl font-bold">{formatCurrency(checkTotal)}</p>
                      <p className="text-blue-100 text-sm mt-1">{monthDeposits.filter(d => d.type === 'check').length} deposits</p>
                    </div>
                    
                    {/* Zelle */}
                    <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl p-5 text-white shadow-lg shadow-purple-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-xl">⚡</span>
                        </div>
                        <span className="font-medium">Zelle</span>
                      </div>
                      <p className="text-2xl font-bold">{formatCurrency(zelleTotal)}</p>
                      <p className="text-purple-100 text-sm mt-1">{monthDeposits.filter(d => d.type === 'zelle').length} transfers</p>
                    </div>
                    
                    {/* Credit Card */}
                    <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg shadow-orange-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-xl">💳</span>
                        </div>
                        <span className="font-medium">Credit Card</span>
                      </div>
                      <p className="text-2xl font-bold">{formatCurrency(creditCardTotal)}</p>
                      <p className="text-orange-100 text-sm mt-1">{monthDeposits.filter(d => d.type === 'credit_card').length} credits</p>
                    </div>
                    
                    {/* Total */}
                    <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl p-5 text-white shadow-lg col-span-2 lg:col-span-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-xl">🏦</span>
                        </div>
                        <span className="font-medium">Total</span>
                      </div>
                      <p className="text-2xl font-bold">{formatCurrency(grandTotal)}</p>
                      <p className="text-slate-300 text-sm mt-1">{monthDeposits.length} total entries</p>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Transactions Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4 text-white flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Transaction History
                  </h3>
                  <p className="text-slate-300 text-sm">All bank deposits and credits</p>
                </div>
              </div>
              
              <div className="p-4">
                {(() => {
                  const [year, month] = bankActivityMonth.split('-');
                  let filteredDeposits = bankDeposits.filter(d => {
                    const depositDate = new Date(d.date);
                    return depositDate.getFullYear() === parseInt(year) && 
                           (depositDate.getMonth() + 1) === parseInt(month);
                  });
                  
                  if (bankActivityFilter !== 'all') {
                    filteredDeposits = filteredDeposits.filter(d => d.type === bankActivityFilter);
                  }
                  
                  filteredDeposits.sort((a, b) => new Date(b.date) - new Date(a.date));
                  
                  if (filteredDeposits.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Found</h3>
                        <p className="text-gray-500 mb-4">Add your first bank deposit to get started</p>
                        <button
                          onClick={() => {
                            setEditingBankDeposit(null);
                            setShowBankDepositModal(true);
                          }}
                          className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                        >
                          Add Deposit
                        </button>
                      </div>
                    );
                  }
                  
                  const typeConfig = {
                    cash: { label: 'Cash', bg: 'bg-green-100', text: 'text-green-700', icon: '💵' },
                    check: { label: 'Check', bg: 'bg-blue-100', text: 'text-blue-700', icon: '📝' },
                    zelle: { label: 'Zelle', bg: 'bg-purple-100', text: 'text-purple-700', icon: '⚡' },
                    credit_card: { label: 'Credit Card', bg: 'bg-orange-100', text: 'text-orange-700', icon: '💳' }
                  };
                  
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Reference</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                            <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                            <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDeposits.map(deposit => {
                            const config = typeConfig[deposit.type] || typeConfig.cash;
                            return (
                              <tr key={deposit.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-3 px-4">
                                  <p className="font-medium text-gray-900">{formatDate(deposit.date)}</p>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                                    <span>{config.icon}</span>
                                    {config.label}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-gray-600">{deposit.reference || '-'}</td>
                                <td className="py-3 px-4 text-gray-600 max-w-xs truncate">{deposit.description || '-'}</td>
                                <td className="py-3 px-4 text-right font-bold text-emerald-600">{formatCurrency(deposit.amount)}</td>
                                <td className="py-3 px-4 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => {
                                        setEditingBankDeposit(deposit);
                                        setShowBankDepositModal(true);
                                      }}
                                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Edit"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (!confirm(`Delete this ${config.label} deposit of ${formatCurrency(deposit.amount)}?`)) return;
                                        
                                        const { error } = await supabase
                                          .from('bank_deposits')
                                          .delete()
                                          .eq('id', deposit.id);
                                        
                                        if (error) {
                                          alert('Error deleting deposit: ' + error.message);
                                          return;
                                        }
                                        
                                        setBankDeposits(prev => prev.filter(d => d.id !== deposit.id));
                                        await logActivity('DELETE_BANK_DEPOSIT', `Deleted ${config.label} deposit: ${formatCurrency(deposit.amount)}`, 'bank', deposit.id);
                                      }}
                                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Add/Edit Bank Deposit Modal */}
            {showBankDepositModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-6 h-6" />
                        <div>
                          <h3 className="font-bold">{editingBankDeposit ? 'Edit Deposit' : 'New Bank Deposit'}</h3>
                          <p className="text-emerald-100 text-sm">Record a bank transaction</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowBankDepositModal(false);
                          setEditingBankDeposit(null);
                        }}
                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      
                      const depositData = {
                        date: formData.get('depositDate'),
                        type: formData.get('depositType'),
                        amount: parseFloat(formData.get('depositAmount')) || 0,
                        reference: formData.get('depositReference') || '',
                        description: formData.get('depositDescription') || '',
                        recorded_by: currentUser?.username || 'admin'
                      };
                      
                      if (editingBankDeposit) {
                        // Update existing
                        const { error } = await supabase
                          .from('bank_deposits')
                          .update(depositData)
                          .eq('id', editingBankDeposit.id);
                        
                        if (error) {
                          alert('Error updating deposit: ' + error.message);
                          return;
                        }
                        
                        setBankDeposits(prev => prev.map(d => 
                          d.id === editingBankDeposit.id ? { ...d, ...depositData } : d
                        ));
                        await logActivity('UPDATE_BANK_DEPOSIT', `Updated bank deposit: ${formatCurrency(depositData.amount)}`, 'bank', editingBankDeposit.id);
                      } else {
                        // Create new
                        const { data: newDeposit, error } = await supabase
                          .from('bank_deposits')
                          .insert([depositData])
                          .select()
                          .single();
                        
                        if (error) {
                          alert('Error creating deposit: ' + error.message);
                          return;
                        }
                        
                        setBankDeposits(prev => [{ id: newDeposit.id, ...depositData }, ...prev]);
                        await logActivity('CREATE_BANK_DEPOSIT', `Added bank deposit: ${formatCurrency(depositData.amount)} (${depositData.type})`, 'bank', newDeposit.id);
                      }
                      
                      setShowBankDepositModal(false);
                      setEditingBankDeposit(null);
                    }}
                    className="p-6 space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                        <input
                          type="date"
                          name="depositDate"
                          required
                          defaultValue={editingBankDeposit?.date || new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                        <select
                          name="depositType"
                          required
                          defaultValue={editingBankDeposit?.type || 'cash'}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="cash">💵 Cash Deposit</option>
                          <option value="check">📝 Check Deposit</option>
                          <option value="zelle">⚡ Zelle Transfer</option>
                          <option value="credit_card">💳 Credit Card</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            name="depositAmount"
                            required
                            min="0.01"
                            step="0.01"
                            defaultValue={editingBankDeposit?.amount || ''}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                        <input
                          type="text"
                          name="depositReference"
                          defaultValue={editingBankDeposit?.reference || ''}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Check #, Sender name, etc."
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        name="depositDescription"
                        rows={2}
                        defaultValue={editingBankDeposit?.description || ''}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Optional notes about this deposit..."
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowBankDepositModal(false);
                          setEditingBankDeposit(null);
                        }}
                        className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 font-medium shadow-lg shadow-emerald-500/25 transition-all"
                      >
                        {editingBankDeposit ? 'Save Changes' : 'Add Deposit'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
              <div className="flex gap-2">
                <button
                  onClick={exportToCSV}
                  className="bg-white text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 border border-gray-300"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                <button
                  onClick={exportDonationsToExcel}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </button>
                <button
                  onClick={exportFullReportToExcel}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                >
                  <FileText className="w-4 h-4" />
                  Full Report
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Filter Options</h3>
                {(reportDateRange.start || reportDateRange.end || reportType !== 'all') && (
                  <button
                    onClick={() => {
                      setReportDateRange({ start: '', end: '' });
                      setReportType('all');
                    }}
                    className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear Filters
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={reportDateRange.start}
                    onChange={(e) => setReportDateRange({ ...reportDateRange, start: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={reportDateRange.end}
                    onChange={(e) => setReportDateRange({ ...reportDateRange, end: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="all">All Campaigns</option>
                    {settings.campaigns.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2 w-full">
                    Showing <span className="font-semibold text-gray-900">{getFilteredDonations().length}</span> of <span className="font-semibold">{donations.length}</span> donations
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(getFilteredDonations().reduce((sum, d) => sum + d.amount, 0))}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <p className="text-sm text-gray-500">Number of Donations</p>
                <p className="text-2xl font-bold text-gray-900">{getFilteredDonations().length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <p className="text-sm text-gray-500">Average Donation</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(
                    getFilteredDonations().length > 0
                      ? getFilteredDonations().reduce((sum, d) => sum + d.amount, 0) / getFilteredDonations().length
                      : 0
                  )}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <p className="text-sm text-gray-500">Unique Donors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(getFilteredDonations().map(d => d.donorId)).size}
                </p>
              </div>
            </div>

            {/* Pledge Payment Status Pie Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pledge Payment Status</h3>
                {(() => {
                  const fullyPaid = pledges.filter(p => p.paidAmount >= p.totalAmount && p.totalAmount > 0).length;
                  const partialPaid = pledges.filter(p => p.paidAmount > 0 && p.paidAmount < p.totalAmount).length;
                  const notPaid = pledges.filter(p => !p.paidAmount || p.paidAmount === 0).length;
                  const total = fullyPaid + partialPaid + notPaid;
                  
                  if (total === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <PieChart className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No pledges to display</p>
                      </div>
                    );
                  }
                  
                  // Calculate percentages
                  const fullyPaidPct = (fullyPaid / total) * 100;
                  const partialPaidPct = (partialPaid / total) * 100;
                  const notPaidPct = (notPaid / total) * 100;
                  
                  // SVG pie chart parameters
                  const radius = 80;
                  const circumference = 2 * Math.PI * radius;
                  
                  // Calculate stroke dash arrays for each segment
                  const fullyPaidDash = (fullyPaidPct / 100) * circumference;
                  const partialPaidDash = (partialPaidPct / 100) * circumference;
                  const notPaidDash = (notPaidPct / 100) * circumference;
                  
                  // Calculate rotation offsets (cumulative)
                  const fullyPaidRotation = -90; // Start at top
                  const partialPaidRotation = -90 + (fullyPaidPct / 100) * 360;
                  const notPaidRotation = -90 + ((fullyPaidPct + partialPaidPct) / 100) * 360;
                  
                  return (
                    <div className="flex items-center justify-center gap-8">
                      {/* Pie Chart */}
                      <div className="relative">
                        <svg width="200" height="200" viewBox="0 0 200 200">
                          {/* Background circle */}
                          <circle
                            cx="100"
                            cy="100"
                            r={radius}
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="40"
                          />
                          {/* Fully Paid (Green) */}
                          {fullyPaid > 0 && (
                            <circle
                              cx="100"
                              cy="100"
                              r={radius}
                              fill="none"
                              stroke="#10b981"
                              strokeWidth="40"
                              strokeDasharray={`${fullyPaidDash} ${circumference}`}
                              strokeDashoffset="0"
                              transform={`rotate(${fullyPaidRotation} 100 100)`}
                            />
                          )}
                          {/* Partial Paid (Orange) */}
                          {partialPaid > 0 && (
                            <circle
                              cx="100"
                              cy="100"
                              r={radius}
                              fill="none"
                              stroke="#f59e0b"
                              strokeWidth="40"
                              strokeDasharray={`${partialPaidDash} ${circumference}`}
                              strokeDashoffset="0"
                              transform={`rotate(${partialPaidRotation} 100 100)`}
                            />
                          )}
                          {/* Not Paid (Red) */}
                          {notPaid > 0 && (
                            <circle
                              cx="100"
                              cy="100"
                              r={radius}
                              fill="none"
                              stroke="#ef4444"
                              strokeWidth="40"
                              strokeDasharray={`${notPaidDash} ${circumference}`}
                              strokeDashoffset="0"
                              transform={`rotate(${notPaidRotation} 100 100)`}
                            />
                          )}
                          {/* Center text */}
                          <text x="100" y="95" textAnchor="middle" className="text-2xl font-bold fill-gray-900">
                            {total}
                          </text>
                          <text x="100" y="115" textAnchor="middle" className="text-sm fill-gray-500">
                            Total Pledges
                          </text>
                        </svg>
                      </div>
                      
                      {/* Legend */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
                          <div>
                            <p className="font-medium text-gray-900">Fully Paid</p>
                            <p className="text-sm text-gray-500">{fullyPaid} pledges ({fullyPaidPct.toFixed(1)}%)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-amber-500"></div>
                          <div>
                            <p className="font-medium text-gray-900">Partial Paid</p>
                            <p className="text-sm text-gray-500">{partialPaid} pledges ({partialPaidPct.toFixed(1)}%)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-red-500"></div>
                          <div>
                            <p className="font-medium text-gray-900">Not Paid</p>
                            <p className="text-sm text-gray-500">{notPaid} pledges ({notPaidPct.toFixed(1)}%)</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Pledge Amount Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pledge Amount Summary</h3>
                {(() => {
                  const totalPledged = pledges.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
                  const totalCollected = pledges.reduce((sum, p) => sum + (p.paidAmount || 0), 0);
                  const totalOutstanding = totalPledged - totalCollected;
                  const collectionRate = totalPledged > 0 ? (totalCollected / totalPledged) * 100 : 0;
                  
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-600">Total Pledged</span>
                        <span className="text-xl font-bold text-gray-900">{formatCurrency(totalPledged)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                        <span className="text-emerald-700">Total Collected</span>
                        <span className="text-xl font-bold text-emerald-600">{formatCurrency(totalCollected)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <span className="text-red-700">Outstanding Balance</span>
                        <span className="text-xl font-bold text-red-600">{formatCurrency(totalOutstanding)}</span>
                      </div>
                      
                      {/* Collection Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Collection Rate</span>
                          <span className="font-medium text-gray-900">{collectionRate.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-emerald-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(collectionRate, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Filtered Results */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {getFilteredDonations().length === 0 ? (
                <div className="p-12 text-center">
                  <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Donations Found</h3>
                  <p className="text-gray-500 mb-4">
                    {donations.length === 0 
                      ? "No donations have been recorded yet. Add donations or record pledge payments to see them here."
                      : "No donations match your current filters. Try adjusting the date range or campaign filter."}
                  </p>
                  {donations.length === 0 && (
                    <button
                      onClick={() => setActiveTab('donations')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add First Donation
                    </button>
                  )}
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Date</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Donor</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Amount</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Campaign</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getFilteredDonations().map(donation => {
                      const donor = donors.find(d => d.id === donation.donorId);
                      return (
                        <tr key={donation.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">{formatDate(donation.date)}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="group relative inline-block">
                              <span className="cursor-pointer hover:text-emerald-600">
                                {donation.donorName}
                                {(donor?.pledgeForm || donor?.responsible || donor?.donorNotes) && (
                                  <span className="ml-1 text-blue-500 text-xs">ℹ️</span>
                                )}
                              </span>
                              {(donor?.pledgeForm || donor?.responsible || donor?.donorNotes) && (
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-20">
                                  <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                    {donor?.responsible && (
                                      <p className="flex items-center gap-2 mb-1">
                                        <span className="text-amber-400">👤 Responsible:</span> {donor.responsible}
                                      </p>
                                    )}
                                    {donor?.pledgeForm && (
                                      <p className="flex items-center gap-2 mb-1">
                                        <span className="text-emerald-400">📋 Pledge Form:</span> {donor.pledgeForm === 'Y' ? 'Yes' : donor.pledgeForm === 'N' ? 'No' : donor.pledgeForm}
                                      </p>
                                    )}
                                    {donor?.donorNotes && (
                                      <p className="flex items-center gap-2 max-w-xs">
                                        <span className="text-blue-400">📝 Notes:</span> <span className="whitespace-normal">{donor.donorNotes}</span>
                                      </p>
                                    )}
                                  </div>
                                  <div className="absolute left-4 -bottom-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-emerald-600">{formatCurrency(donation.amount)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{donation.campaign}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsPanel
            settings={settings}
            onSave={handleSettingsUpdate}
          />
        )}

        {/* Users Tab (Admin Only) */}
        {activeTab === 'users' && currentUser?.role === 'admin' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
              <button
                onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700"
              >
                <UserPlus className="w-4 h-4" />
                Add User
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">User</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Username</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Role</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Created</th>
                    <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${user.role === 'admin' ? 'bg-purple-100' : 'bg-gray-100'}`}>
                            {user.role === 'admin' ? (
                              <Shield className="w-5 h-5 text-purple-600" />
                            ) : (
                              <User className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.username}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatDate(user.createdAt)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                            className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {user.id !== 'admin' && (
                            <button
                              onClick={() => handleToggleUserStatus(user.id)}
                              className={`p-2 rounded-lg transition-colors ${user.status === 'active' ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
                              title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              {user.status === 'active' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Log Tab (Admin Only) */}
        {activeTab === 'activity' && currentUser?.role === 'admin' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Activity Log</h2>
              <p className="text-sm text-gray-500">{activityLog.length} activities recorded</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Timestamp</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">User</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Action</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activityLog.map(activity => (
                      <tr key={activity.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                          {formatDateTime(activity.timestamp)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{activity.userName}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              activity.userRole === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {activity.userRole}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            activity.action.includes('CREATE') ? 'bg-emerald-100 text-emerald-700' :
                            activity.action.includes('UPDATE') ? 'bg-blue-100 text-blue-700' :
                            activity.action.includes('DELETE') ? 'bg-red-100 text-red-700' :
                            activity.action.includes('LOGIN') ? 'bg-green-100 text-green-700' :
                            activity.action.includes('LOGOUT') ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {activity.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{activity.details}</td>
                      </tr>
                    ))}
                    {activityLog.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>No activity recorded yet</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {showDonationModal && (
        <Modal title={editingItem ? 'Edit Donation' : 'New Donation'} onClose={() => { setShowDonationModal(false); setEditingItem(null); }}>
          <DonationForm
            donation={editingItem}
            donors={donors}
            campaigns={settings.campaigns}
            onSubmit={handleDonationSubmit}
            onCancel={() => { setShowDonationModal(false); setEditingItem(null); }}
          />
        </Modal>
      )}

      {/* Donation Box Modal */}
      {showDonationBoxModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                    📦
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Donation Box</h3>
                    <p className="text-emerald-100 text-sm">{donationBoxCampaign}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowDonationBoxModal(false); setDonationBoxCampaign(''); }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const cashAmount = parseFloat(formData.get('cashAmount')) || 0;
                const checkAmount = parseFloat(formData.get('checkAmount')) || 0;
                const date = formData.get('date');
                handleDonationBoxSubmit(cashAmount, checkAmount, date);
              }}
              className="p-6 space-y-5"
            >
              {/* Date Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    📅 Date
                  </span>
                </label>
                <input
                  type="date"
                  name="date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 text-lg font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>

              {/* Cash Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    💵 Cash Amount
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                  </div>
                  <input
                    type="number"
                    name="cashAmount"
                    min="0"
                    step="0.01"
                    autoFocus
                    placeholder="0.00"
                    className="w-full pl-11 pr-4 py-3 text-xl font-semibold border-2 border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-emerald-50/50"
                  />
                </div>
              </div>
              
              {/* Check Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    📝 Check Amount
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <DollarSign className="w-5 h-5 text-blue-500" />
                  </div>
                  <input
                    type="number"
                    name="checkAmount"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full pl-11 pr-4 py-3 text-xl font-semibold border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-blue-50/50"
                  />
                </div>
              </div>
              
              {/* Total Display */}
              <div className="p-4 bg-gray-100 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Total Collection</span>
                  <span className="text-xl font-bold text-gray-900" id="totalDisplay">$0.00</span>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowDonationBoxModal(false); setDonationBoxCampaign(''); }}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 font-medium shadow-lg shadow-emerald-500/25 transition-all"
                >
                  Add Donation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Annual Fund Modal */}
      {showAnnualFundModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-indigo-500 to-violet-600 px-6 py-5 text-white sticky top-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                    🏛️
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Annual Fund</h3>
                    <p className="text-indigo-100 text-sm">{annualFundCategory}</p>
                  </div>
                </div>
                <button
                  onClick={() => { 
                    setShowAnnualFundModal(false); 
                    setAnnualFundCategory(''); 
                    setAnnualFundPaymentMode('one-time');
                    setMonthlyPledgePayments(Array(12).fill(false));
                    setMonthlyPledgeAmount('');
                    setShowExistingPayments(false);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                
                if (annualFundPaymentMode === 'monthly-pledge') {
                  // Handle monthly pledge payments
                  const pledgeAmount = parseFloat(monthlyPledgeAmount) || 0;
                  const monthlyAmount = pledgeAmount / 12;
                  const checkedMonths = monthlyPledgePayments
                    .map((checked, index) => checked ? index : null)
                    .filter(index => index !== null);
                  
                  if (checkedMonths.length > 0 && monthlyAmount > 0) {
                    // Submit each checked month as a separate donation
                    checkedMonths.forEach(async (monthIndex) => {
                      const paymentDate = new Date();
                      paymentDate.setMonth(monthIndex);
                      const dateStr = paymentDate.toISOString().split('T')[0];
                      
                      const { data: donation } = await supabase
                        .from('donations')
                        .insert([{
                          donor_id: null,
                          donor_name: 'Annual Fund - Monthly Pledge',
                          amount: monthlyAmount,
                          date: dateStr,
                          type: 'annual-fund',
                          campaign: annualFundCategory,
                          method: 'Pledge Payment',
                          notes: `Monthly Pledge Payment ${monthIndex + 1}/12 - ${annualFundCategory}`,
                          created_by: currentUser?.id
                        }])
                        .select()
                        .single();
                      
                      if (donation) {
                        setDonations(prev => [{
                          id: donation.id,
                          donorId: donation.donor_id,
                          donorName: donation.donor_name,
                          amount: parseFloat(donation.amount),
                          date: donation.date,
                          type: donation.type,
                          campaign: donation.campaign,
                          method: donation.method,
                          notes: donation.notes
                        }, ...prev]);
                        await logActivity('CREATE_DONATION', `Added monthly pledge payment: ${formatCurrency(monthlyAmount)} to ${annualFundCategory}`, 'donation', donation.id);
                      }
                    });
                  }
                  
                  setShowAnnualFundModal(false);
                  setAnnualFundCategory('');
                  setAnnualFundPaymentMode('one-time');
                  setMonthlyPledgePayments(Array(12).fill(false));
                  setMonthlyPledgeAmount('');
                  setShowExistingPayments(false);
                } else {
                  // Handle one-time or paid-in-full
                  const cashAmount = parseFloat(formData.get('cashAmount')) || 0;
                  const checkAmount = parseFloat(formData.get('checkAmount')) || 0;
                  const date = formData.get('date');
                  const isPaidInFull = annualFundPaymentMode === 'paid-in-full';
                  handleAnnualFundSubmit(cashAmount, checkAmount, date, isPaidInFull);
                }
              }}
              className="p-6 space-y-5"
            >
              {/* Payment Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Payment Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setAnnualFundPaymentMode('one-time')}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      annualFundPaymentMode === 'one-time'
                        ? 'bg-indigo-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    💵 One-Time
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnnualFundPaymentMode('paid-in-full')}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      annualFundPaymentMode === 'paid-in-full'
                        ? 'bg-green-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ✅ Paid in Full
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnnualFundPaymentMode('monthly-pledge')}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      annualFundPaymentMode === 'monthly-pledge'
                        ? 'bg-violet-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    📆 Monthly Pledge
                  </button>
                </div>
              </div>

              {/* One-Time or Paid in Full Fields */}
              {(annualFundPaymentMode === 'one-time' || annualFundPaymentMode === 'paid-in-full') && (
                <>
                  {/* Paid in Full Badge */}
                  {annualFundPaymentMode === 'paid-in-full' && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 font-medium text-sm">This payment will be marked as Paid in Full</span>
                    </div>
                  )}
                  
                  {/* Date Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        📅 Date
                      </span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      defaultValue={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 text-lg font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                    />
                  </div>

                  {/* Cash Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        💵 Cash Amount
                      </span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <DollarSign className="w-5 h-5 text-indigo-500" />
                      </div>
                      <input
                        type="number"
                        name="cashAmount"
                        min="0"
                        step="0.01"
                        autoFocus
                        placeholder="0.00"
                        className="w-full pl-11 pr-4 py-3 text-xl font-semibold border-2 border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-indigo-50/50"
                      />
                    </div>
                  </div>
                  
                  {/* Check Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        📝 Check Amount
                      </span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <DollarSign className="w-5 h-5 text-violet-500" />
                      </div>
                      <input
                        type="number"
                        name="checkAmount"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full pl-11 pr-4 py-3 text-xl font-semibold border-2 border-violet-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all bg-violet-50/50"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Monthly Pledge Fields */}
              {annualFundPaymentMode === 'monthly-pledge' && (
                <>
                  {/* Total Pledge Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        💰 Total Annual Pledge Amount
                      </span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <DollarSign className="w-5 h-5 text-violet-500" />
                      </div>
                      <input
                        type="number"
                        value={monthlyPledgeAmount}
                        onChange={(e) => setMonthlyPledgeAmount(e.target.value)}
                        min="0"
                        step="0.01"
                        autoFocus
                        placeholder="0.00"
                        className="w-full pl-11 pr-4 py-3 text-xl font-semibold border-2 border-violet-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all bg-violet-50/50"
                      />
                    </div>
                    {monthlyPledgeAmount && parseFloat(monthlyPledgeAmount) > 0 && (
                      <p className="mt-2 text-sm text-violet-600 font-medium">
                        Monthly Payment: {formatCurrency(parseFloat(monthlyPledgeAmount) / 12)} × 12 months
                      </p>
                    )}
                  </div>

                  {/* 12 Month Checkboxes */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      <span className="flex items-center gap-2">
                        📆 Monthly Payments (Check completed payments)
                      </span>
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
                        <label
                          key={month}
                          className={`flex items-center gap-2 p-2.5 rounded-xl cursor-pointer transition-all ${
                            monthlyPledgePayments[index]
                              ? 'bg-green-100 border-2 border-green-500'
                              : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={monthlyPledgePayments[index]}
                            onChange={(e) => {
                              const newPayments = [...monthlyPledgePayments];
                              newPayments[index] = e.target.checked;
                              setMonthlyPledgePayments(newPayments);
                            }}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                          <span className={`text-sm font-medium ${monthlyPledgePayments[index] ? 'text-green-700' : 'text-gray-600'}`}>
                            {month}
                          </span>
                          {monthlyPledgePayments[index] && (
                            <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                          )}
                        </label>
                      ))}
                    </div>
                    
                    {/* Summary */}
                    <div className="mt-4 p-3 bg-violet-50 rounded-xl">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-violet-600">Payments Selected:</span>
                        <span className="font-bold text-violet-700">{monthlyPledgePayments.filter(p => p).length} of 12</span>
                      </div>
                      {monthlyPledgeAmount && parseFloat(monthlyPledgeAmount) > 0 && (
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-violet-600">Amount to Add:</span>
                          <span className="font-bold text-violet-700">
                            {formatCurrency((parseFloat(monthlyPledgeAmount) / 12) * monthlyPledgePayments.filter(p => p).length)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Existing Payments Section */}
              {(() => {
                const categoryPayments = donations.filter(d => d.type === 'annual-fund' && d.campaign === annualFundCategory);
                if (categoryPayments.length === 0) return null;
                
                return (
                  <div className="border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowExistingPayments(!showExistingPayments)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          Existing Payments ({categoryPayments.length})
                        </span>
                        <span className="text-sm text-gray-500">
                          Total: {formatCurrency(categoryPayments.reduce((sum, d) => sum + d.amount, 0))}
                        </span>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showExistingPayments ? 'rotate-90' : ''}`} />
                    </button>
                    
                    {showExistingPayments && (
                      <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                        {categoryPayments
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map(payment => (
                          <div 
                            key={payment.id}
                            className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl group hover:border-red-200 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  payment.method === 'Cash' ? 'bg-green-100 text-green-700' : 
                                  payment.method === 'Check' ? 'bg-blue-100 text-blue-700' :
                                  'bg-violet-100 text-violet-700'
                                }`}>
                                  {payment.method}
                                </span>
                                {payment.notes?.includes('PAID IN FULL') && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                    ✓ Paid in Full
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {new Date(payment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {payment.donorName && payment.donorName !== 'Annual Fund' && ` • ${payment.donorName}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteAnnualFundPayment(payment.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete payment"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { 
                    setShowAnnualFundModal(false); 
                    setAnnualFundCategory(''); 
                    setAnnualFundPaymentMode('one-time');
                    setMonthlyPledgePayments(Array(12).fill(false));
                    setMonthlyPledgeAmount('');
                    setShowExistingPayments(false);
                  }}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`flex-1 px-4 py-3 text-white rounded-xl font-medium shadow-lg transition-all ${
                    annualFundPaymentMode === 'paid-in-full'
                      ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-green-500/25'
                      : annualFundPaymentMode === 'monthly-pledge'
                      ? 'bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 shadow-violet-500/25'
                      : 'bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 shadow-indigo-500/25'
                  }`}
                >
                  {annualFundPaymentMode === 'monthly-pledge' 
                    ? `Add ${monthlyPledgePayments.filter(p => p).length} Payment${monthlyPledgePayments.filter(p => p).length !== 1 ? 's' : ''}`
                    : 'Add Contribution'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPledgeModal && (
        <Modal title={editingItem ? 'Edit Pledge' : 'New Pledge'} onClose={() => { setShowPledgeModal(false); setEditingItem(null); }}>
          <PledgeForm
            pledge={editingItem}
            donors={donors}
            campaigns={settings.campaigns}
            onSubmit={handlePledgeSubmit}
            onCancel={() => { setShowPledgeModal(false); setEditingItem(null); }}
          />
        </Modal>
      )}

      {showGoalModal && (
        <Modal title={editingItem ? 'Edit Goal' : 'New Goal'} onClose={() => { setShowGoalModal(false); setEditingItem(null); }}>
          <GoalForm
            goal={editingItem}
            campaigns={settings.campaigns}
            onSubmit={handleGoalSubmit}
            onCancel={() => { setShowGoalModal(false); setEditingItem(null); }}
          />
        </Modal>
      )}

      {showDonorModal && (
        <Modal title={editingItem ? 'Edit Donor' : 'New Donor'} onClose={() => { setShowDonorModal(false); setEditingItem(null); }}>
          <DonorForm
            donor={editingItem}
            onSubmit={handleDonorSubmit}
            onCancel={() => { setShowDonorModal(false); setEditingItem(null); }}
          />
        </Modal>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Import {importType.charAt(0).toUpperCase() + importType.slice(1)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {importStep === 1 && 'Upload a CSV or Excel file'}
                    {importStep === 2 && 'Map columns to fields'}
                    {importStep === 3 && 'Preview and confirm'}
                    {importStep === 4 && 'Importing data...'}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {/* Step 1: Upload File */}
              {importStep === 1 && (
                <div className="text-center py-8">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-emerald-500 transition-colors">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Drag and drop your file here, or click to browse</p>
                    <p className="text-sm text-gray-500 mb-4">Supports CSV and Excel files (.csv, .xlsx, .xls)</p>
                    <label className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-emerald-700">
                      <Upload className="w-4 h-4" />
                      Select File
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  
                  <div className="mt-6 text-left bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Expected columns for {importType}:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {getRequiredFields().map(field => (
                        <li key={field.key} className="flex items-center gap-2">
                          {field.required ? (
                            <span className="text-red-500">*</span>
                          ) : (
                            <span className="text-gray-400">○</span>
                          )}
                          {field.label}
                          {field.required && <span className="text-xs text-gray-400">(required)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Step 2: Map Columns */}
              {importStep === 2 && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Map your file columns to the required fields. We've auto-detected some mappings for you.
                  </p>
                  
                  <div className="space-y-3">
                    {getRequiredFields().map(field => (
                      <div key={field.key} className="flex items-center gap-4">
                        <label className="w-1/3 text-sm font-medium text-gray-700 flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={importMapping[field.key] || ''}
                          onChange={(e) => setImportMapping({ ...importMapping, [field.key]: e.target.value })}
                          className={`flex-1 border rounded-lg px-3 py-2 text-sm ${
                            field.required && !importMapping[field.key] 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                        >
                          <option value="">-- Select column --</option>
                          {importHeaders.map(header => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                        {importMapping[field.key] && (
                          <Check className="w-5 h-5 text-emerald-500" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Sample data from your file:</h4>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr>
                            {importHeaders.slice(0, 6).map(h => (
                              <th key={h} className="text-left px-2 py-1 text-blue-700 font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importData.slice(0, 3).map((row, i) => (
                            <tr key={i}>
                              {importHeaders.slice(0, 6).map(h => (
                                <td key={h} className="px-2 py-1 text-blue-800 truncate max-w-[150px]">{row[h]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 3: Preview */}
              {importStep === 3 && (
                <div>
                  {importErrors.length > 0 && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                        <AlertTriangle className="w-5 h-5" />
                        Validation Errors
                      </div>
                      <ul className="text-sm text-red-600 space-y-1">
                        {importErrors.map((error, i) => (
                          <li key={i}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 text-emerald-700 font-medium">
                      <CheckCircle className="w-5 h-5" />
                      Ready to import {importData.length} {importType}
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-700">#</th>
                            {getRequiredFields().filter(f => importMapping[f.key]).map(field => (
                              <th key={field.key} className="text-left px-3 py-2 font-medium text-gray-700">{field.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {importData.slice(0, 10).map((row, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                              {getRequiredFields().filter(f => importMapping[f.key]).map(field => (
                                <td key={field.key} className="px-3 py-2 text-gray-900 truncate max-w-[150px]">
                                  {row[importMapping[field.key]]}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {importData.length > 10 && (
                      <div className="bg-gray-50 px-3 py-2 text-sm text-gray-500 text-center">
                        ... and {importData.length - 10} more rows
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Step 4: Importing */}
              {importStep === 4 && (
                <div className="text-center py-8">
                  {importSuccess < importData.length && importErrors.length === 0 ? (
                    <>
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Importing {importType}...</p>
                      <p className="text-sm text-gray-500 mt-2">{importSuccess} of {importData.length} completed</p>
                      <div className="w-64 h-2 bg-gray-200 rounded-full mx-auto mt-4">
                        <div 
                          className="h-2 bg-emerald-600 rounded-full transition-all duration-300"
                          style={{ width: `${(importSuccess / importData.length) * 100}%` }}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-emerald-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h3>
                      <p className="text-gray-600">Successfully imported {importSuccess} {importType}</p>
                      {importErrors.length > 0 && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left max-w-md mx-auto">
                          <p className="text-yellow-700 font-medium mb-2">{importErrors.length} rows had errors:</p>
                          <ul className="text-sm text-yellow-600 space-y-1">
                            {importErrors.slice(0, 5).map((error, i) => (
                              <li key={i}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  if (importStep === 1 || importStep === 4) {
                    setShowImportModal(false);
                  } else {
                    setImportStep(importStep - 1);
                  }
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                {importStep === 1 || importStep === 4 ? 'Cancel' : 'Back'}
              </button>
              
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4].map(step => (
                  <div
                    key={step}
                    className={`w-2 h-2 rounded-full ${
                      step === importStep ? 'bg-emerald-600' : 
                      step < importStep ? 'bg-emerald-300' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              {importStep === 2 && (
                <button
                  onClick={() => {
                    if (validateImportData()) {
                      setImportStep(3);
                    }
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Preview Import
                </button>
              )}
              
              {importStep === 3 && (
                <button
                  onClick={processImport}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Import {importData.length} {importType}
                </button>
              )}
              
              {importStep === 4 && importSuccess === importData.length && (
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showReceiptModal && selectedReceipt && (
        <Modal title="Donation Receipt" onClose={() => { setShowReceiptModal(false); setSelectedReceipt(null); }} large>
          <Receipt
            donation={selectedReceipt}
            donor={donors.find(d => d.id === selectedReceipt.donorId)}
            settings={settings}
            sendEmail={sendEmail}
          />
        </Modal>
      )}

      {showPledgePaymentModal && selectedPledge && (
        <Modal title="Record Pledge Payment" onClose={() => { setShowPledgePaymentModal(false); setSelectedPledge(null); }}>
          <PledgePaymentForm
            pledge={selectedPledge}
            onSubmit={(amount, method, checkDetails) => handlePledgePayment(selectedPledge.id, amount, method, checkDetails)}
            onCancel={() => { setShowPledgePaymentModal(false); setSelectedPledge(null); }}
          />
        </Modal>
      )}

      {showPledgeReceiptModal && pledgePaymentReceipt && (
        <Modal title="Pledge Payment Receipt" onClose={() => { setShowPledgeReceiptModal(false); setPledgePaymentReceipt(null); }} large>
          <PledgePaymentReceipt
            receiptData={pledgePaymentReceipt}
            settings={settings}
            sendEmail={sendEmail}
            onClose={() => { setShowPledgeReceiptModal(false); setPledgePaymentReceipt(null); }}
          />
        </Modal>
      )}

      {showPledgeSummaryModal && pledgeSummaryReceipt && (
        <Modal title="Pledge Summary Receipt" onClose={() => { setShowPledgeSummaryModal(false); setPledgeSummaryReceipt(null); }} large>
          <PledgeSummaryReceipt
            pledge={pledgeSummaryReceipt.pledge}
            donor={pledgeSummaryReceipt.donor}
            settings={settings}
            sendEmail={sendEmail}
            onClose={() => { setShowPledgeSummaryModal(false); setPledgeSummaryReceipt(null); }}
          />
        </Modal>
      )}

      {showUserModal && currentUser?.role === 'admin' && (
        <Modal title={editingUser ? 'Edit User' : 'Create New User'} onClose={() => { setShowUserModal(false); setEditingUser(null); }}>
          <UserForm
            user={editingUser}
            onSubmit={(userData) => editingUser ? handleUpdateUser(editingUser.id, userData) : handleCreateUser(userData)}
            onCancel={() => { setShowUserModal(false); setEditingUser(null); }}
          />
        </Modal>
      )}

      {showChangePasswordModal && (
        <Modal title="Change Password" onClose={() => setShowChangePasswordModal(false)}>
          <ChangePasswordForm
            onSubmit={handleChangePassword}
            onCancel={() => setShowChangePasswordModal(false)}
          />
        </Modal>
      )}
    </div>
  );
}

// Settings Panel Component
function SettingsPanel({ settings, onSave }) {
  const [form, setForm] = useState({ ...settings });
  const [newCampaign, setNewCampaign] = useState('');
  const [saved, setSaved] = useState(false);
  const logoInputRef = useRef(null);

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addCampaign = () => {
    if (newCampaign.trim() && !form.campaigns.includes(newCampaign.trim())) {
      setForm({ ...form, campaigns: [...form.campaigns, newCampaign.trim()] });
      setNewCampaign('');
    }
  };

  const removeCampaign = (campaign) => {
    setForm({ ...form, campaigns: form.campaigns.filter(c => c !== campaign) });
  };

  const moveCampaign = (index, direction) => {
    const newCampaigns = [...form.campaigns];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newCampaigns.length) {
      [newCampaigns[index], newCampaigns[newIndex]] = [newCampaigns[newIndex], newCampaigns[index]];
      setForm({ ...form, campaigns: newCampaigns });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) { // 500KB limit
        alert('Logo file size must be less than 500KB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file (PNG, JPG, etc.)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setForm({ ...form, logo: '' });
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <button
          onClick={handleSave}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
            saved ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'
          }`}
        >
          {saved ? (
            <>
              <CheckCircle className="w-4 h-4" />
              Saved!
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* Logo Upload */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-emerald-600" />
          Organization Logo
        </h3>
        <p className="text-sm text-gray-500 mb-6">Upload your logo to display in the app header and on receipts. Recommended size: 200x200px. Max file size: 500KB.</p>
        
        <div className="flex items-start gap-6">
          {/* Logo Preview */}
          <div className="flex-shrink-0">
            {form.logo ? (
              <div className="relative">
                <img 
                  src={form.logo} 
                  alt="Organization Logo" 
                  className="w-32 h-32 object-contain border border-gray-200 rounded-lg bg-gray-50 p-2"
                />
                <button
                  onClick={removeLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title="Remove logo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Heart className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                  <span className="text-xs text-gray-400">No logo</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Upload Controls */}
          <div className="flex-1">
            <input
              type="file"
              ref={logoInputRef}
              onChange={handleLogoUpload}
              accept="image/*"
              className="hidden"
              id="logo-upload"
            />
            <label
              htmlFor="logo-upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              {form.logo ? 'Change Logo' : 'Upload Logo'}
            </label>
            <p className="text-xs text-gray-500 mt-2">Supported formats: PNG, JPG, GIF, SVG</p>
          </div>
        </div>
      </div>

      {/* Organization Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-emerald-600" />
          Organization Details
        </h3>
        <p className="text-sm text-gray-500 mb-6">This information appears on receipts and throughout the application.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
            <input
              type="text"
              value={form.organizationName}
              onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Your Organization Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID / EIN *</label>
            <input
              type="text"
              value={form.taxId}
              onChange={(e) => setForm({ ...form, taxId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="XX-XXXXXXX"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="123 Main St, City, State ZIP"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="info@organization.org"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              type="text"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="www.organization.org"
            />
          </div>
        </div>
      </div>

      {/* Donation Campaigns */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-emerald-600" />
          Donation Campaigns
        </h3>
        <p className="text-sm text-gray-500 mb-6">Manage the campaigns donors can contribute to. Drag to reorder or use arrows.</p>
        
        {/* Add new campaign */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newCampaign}
            onChange={(e) => setNewCampaign(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addCampaign()}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="Enter new campaign name..."
          />
          <button
            onClick={addCampaign}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {/* Campaign list */}
        <div className="space-y-2">
          {form.campaigns.map((campaign, index) => (
            <div
              key={campaign}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 group hover:bg-gray-100"
            >
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => moveCampaign(index, -1)}
                  disabled={index === 0}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4 -rotate-90" />
                </button>
                <button
                  onClick={() => moveCampaign(index, 1)}
                  disabled={index === form.campaigns.length - 1}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
              </div>
              
              <div className="flex-1">
                <span className="font-medium text-gray-900">{campaign}</span>
              </div>
              
              <button
                onClick={() => removeCampaign(campaign)}
                className="p-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove campaign"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {form.campaigns.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No campaigns added yet.</p>
            <p className="text-sm">Add your first campaign above.</p>
          </div>
        )}
      </div>

      {/* Email Configuration Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-emerald-600" />
          Email Configuration
        </h3>
        <div className="bg-emerald-50 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-800">Email sending is configured on the server</p>
            <p className="text-sm text-emerald-700 mt-1">
              Receipts will be sent directly from your organization's email address. 
              Contact your administrator if you need to change email settings.
            </p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-emerald-600" />
          Receipt Preview
        </h3>
        <p className="text-sm text-gray-500 mb-6">This is how your organization info will appear on donation receipts.</p>
        
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="text-center">
            <div className="flex justify-center mb-3">
              {form.logo ? (
                <img 
                  src={form.logo} 
                  alt="Logo Preview" 
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <div className="bg-emerald-600 p-2 rounded-full">
                  <Heart className="w-6 h-6 text-white" />
                </div>
              )}
            </div>
            <h4 className="text-lg font-bold text-gray-900">{form.organizationName || 'Organization Name'}</h4>
            <p className="text-sm text-gray-500 mt-1">{form.address || 'Address'}</p>
            <p className="text-sm text-gray-500">Tax ID: {form.taxId || 'XX-XXXXXXX'}</p>
            {form.phone && <p className="text-sm text-gray-500">{form.phone}</p>}
            {form.email && <p className="text-sm text-gray-500">{form.email}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colors = {
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

// Modal Component
function Modal({ title, children, onClose, large }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto ${large ? 'w-full max-w-3xl' : 'w-full max-w-md'}`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// Donation Form Component
function DonationForm({ donation, donors, campaigns, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    donorId: donation?.donorId || '',
    donorName: donation?.donorName || '',
    amount: donation?.amount || '',
    date: donation?.date || new Date().toISOString().split('T')[0],
    type: donation?.type || 'one-time',
    campaign: donation?.campaign || campaigns[0] || '',
    method: donation?.method || 'Credit Card',
    notes: donation?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const donor = donors.find(d => d.id === form.donorId);
    onSubmit({
      ...form,
      donorName: donor?.name || form.donorName,
      amount: parseFloat(form.amount)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Donor</label>
        <select
          value={form.donorId}
          onChange={(e) => setForm({ ...form, donorId: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          required
        >
          <option value="">Select a donor</option>
          {donors.map(donor => (
            <option key={donor.id} value={donor.id}>{donor.name}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
          <select
            value={form.campaign}
            onChange={(e) => setForm({ ...form, campaign: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            {campaigns.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
          <select
            value={form.method}
            onChange={(e) => setForm({ ...form, method: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            {paymentMethods.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
        >
          <option value="one-time">One-time</option>
          <option value="recurring">Recurring</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          rows="2"
        />
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          {donation ? 'Update' : 'Add'} Donation
        </button>
      </div>
    </form>
  );
}

// Pledge Form Component
function PledgeForm({ pledge, donors, campaigns, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    donorId: pledge?.donorId || '',
    donorName: pledge?.donorName || '',
    donorEmail: pledge?.donorEmail || '',
    totalAmount: pledge?.totalAmount || '',
    paidAmount: pledge?.paidAmount || 0,
    startDate: pledge?.startDate || new Date().toISOString().split('T')[0],
    endDate: pledge?.endDate || '',
    frequency: pledge?.frequency || 'monthly',
    campaign: pledge?.campaign || campaigns[0] || '',
    status: pledge?.status || 'pending',
    responsible: pledge?.responsible || '',
    pledgeForm: pledge?.pledgeForm || 'N',
    pledgeNotes: pledge?.pledgeNotes || ''
  });

  // Auto-fill email when donor is selected
  const handleDonorChange = (donorId) => {
    const donor = donors.find(d => d.id === donorId);
    setForm({ 
      ...form, 
      donorId: donorId,
      donorEmail: donor?.email || form.donorEmail
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const donor = donors.find(d => d.id === form.donorId);
    onSubmit({
      ...form,
      donorName: donor?.name || form.donorName,
      totalAmount: parseFloat(form.totalAmount),
      paidAmount: parseFloat(form.paidAmount)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Donor</label>
          <select
            value={form.donorId}
            onChange={(e) => handleDonorChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          >
            <option value="">Select a donor</option>
            {donors.map(donor => (
              <option key={donor.id} value={donor.id}>{donor.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.donorEmail}
            onChange={(e) => setForm({ ...form, donorEmail: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="donor@email.com"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.totalAmount}
            onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
          <select
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={form.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={form.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
        <select
          value={form.campaign}
          onChange={(e) => setForm({ ...form, campaign: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
        >
          {campaigns.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      
      {/* Additional Info Section */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsible</label>
            <input
              type="text"
              value={form.responsible}
              onChange={(e) => setForm({ ...form, responsible: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Person responsible"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pledge Form</label>
            <select
              value={form.pledgeForm}
              onChange={(e) => setForm({ ...form, pledgeForm: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.pledgeNotes}
            onChange={(e) => setForm({ ...form, pledgeNotes: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            rows="2"
            placeholder="Any additional notes about this pledge..."
          />
        </div>
      </div>
      
      {pledge && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.paidAmount}
              onChange={(e) => setForm({ ...form, paidAmount: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      )}
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          {pledge ? 'Update' : 'Create'} Pledge
        </button>
      </div>
    </form>
  );
}

// Goal Form Component
function GoalForm({ goal, campaigns, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name: goal?.name || '',
    target: goal?.target || '',
    current: goal?.current || 0,
    deadline: goal?.deadline || '',
    campaign: goal?.campaign || campaigns[0] || '',
    description: goal?.description || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      target: parseFloat(form.target),
      current: parseFloat(form.current)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.target}
            onChange={(e) => setForm({ ...form, target: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
          <input
            type="date"
            value={form.deadline}
            onChange={(e) => setForm({ ...form, deadline: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            required
          />
        </div>
      </div>
      {goal && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
        <select
          value={form.campaign}
          onChange={(e) => setForm({ ...form, campaign: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
        >
          {campaigns.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          rows="2"
        />
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          {goal ? 'Update' : 'Create'} Goal
        </button>
      </div>
    </form>
  );
}

// Donor Form Component
function DonorForm({ donor, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name: donor?.name || '',
    email: donor?.email || '',
    phone: donor?.phone || '',
    address: donor?.address || '',
    totalDonated: donor?.totalDonated || 0,
    donationCount: donor?.donationCount || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
        <input
          type="text"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
        />
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          {donor ? 'Update' : 'Add'} Donor
        </button>
      </div>
    </form>
  );
}

// Pledge Payment Form Component
function PledgePaymentForm({ pledge, onSubmit, onCancel }) {
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [checkNumber, setCheckNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const remaining = pledge.totalAmount - pledge.paidAmount;

  const handleSubmit = (e) => {
    e.preventDefault();
    const checkDetails = paymentMethod === 'Check' ? { checkNumber, bankName } : null;
    onSubmit(parseFloat(amount), paymentMethod, checkDetails);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between mb-2">
          <span className="text-gray-600">Total Pledge:</span>
          <span className="font-medium">{formatCurrency(pledge.totalAmount)}</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="text-gray-600">Paid So Far:</span>
          <span className="font-medium text-emerald-600">{formatCurrency(pledge.paidAmount)}</span>
        </div>
        <div className="flex justify-between pt-2 border-t border-gray-200">
          <span className="text-gray-600">Remaining:</span>
          <span className="font-medium text-orange-600">{formatCurrency(remaining)}</span>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
        <input
          type="number"
          min="0.01"
          max={remaining}
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          required
        />
        <p className="text-sm text-gray-500 mt-1">Maximum: {formatCurrency(remaining)}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          required
        >
          <option value="Cash">Cash</option>
          <option value="Credit/Debit Card">Credit/Debit Card</option>
          <option value="Check">Check</option>
        </select>
      </div>
      
      {/* Conditional Check Fields */}
      {paymentMethod === 'Check' && (
        <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Check Details
          </h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check Number <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
              placeholder="Enter check number"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Enter bank name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>
        </div>
      )}
      
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          Record Payment
        </button>
      </div>
    </form>
  );
}

// Receipt Component
function Receipt({ donation, donor, settings, sendEmail }) {
  const receiptNumber = `RCP-${donation.id.toUpperCase()}-${new Date(donation.date).getFullYear()}`;

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    const donorEmail = donor?.email || '';
    const donorName = donor?.name || donation.donorName;
    
    const subject = `Donation Receipt - ${receiptNumber} - ${settings.organizationName}`;
    
    const body = 
`Dear ${donorName},

Thank you for your generous donation to ${settings.organizationName}!

DONATION RECEIPT
================
Receipt Number: ${receiptNumber}
Date: ${formatDate(donation.date)}

Donor Information:
${donorName}
${donor?.address || ''}
${donor?.email || ''}

Donation Details:
-----------------
Description: Charitable Donation
Campaign: ${donation.campaign}
Payment Method: ${donation.method}
Amount: ${formatCurrency(donation.amount)}

This receipt confirms that no goods or services were provided in exchange for this contribution. This donation may be tax-deductible. Please consult your tax advisor for details.

Thank you for your generous support!

${settings.organizationName}
${settings.address}
Tax ID: ${settings.taxId}
${settings.phone || ''}
${settings.email || ''}
${settings.website || ''}
`;

    if (sendEmail) {
      sendEmail(donorEmail, donorName, subject, body);
    }
  };

  return (
    <div className="space-y-4">
      <div id="receipt" className="bg-white border border-gray-300 rounded-lg p-8 print:border-0">
        {/* Header */}
        <div className="text-center border-b border-gray-200 pb-6 mb-6">
          <div className="flex justify-center mb-4">
            {settings.logo ? (
              <img 
                src={settings.logo} 
                alt="Organization Logo" 
                className="h-16 w-auto object-contain"
              />
            ) : (
              <div className="bg-emerald-600 p-3 rounded-full">
                <Heart className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Donation Receipt</h2>
          <p className="text-gray-500">{settings.organizationName}</p>
          <p className="text-sm text-gray-400 mt-2">{settings.address}</p>
          <p className="text-sm text-gray-400">Tax ID: {settings.taxId}</p>
          {settings.phone && <p className="text-sm text-gray-400">{settings.phone}</p>}
          {settings.email && <p className="text-sm text-gray-400">{settings.email}</p>}
        </div>

        {/* Receipt Info */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500">Receipt Number</p>
            <p className="font-medium text-gray-900">{receiptNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Date</p>
            <p className="font-medium text-gray-900">{formatDate(donation.date)}</p>
          </div>
        </div>

        {/* Donor Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">Donor Information</h3>
          <p className="text-gray-700">{donor?.name || donation.donorName}</p>
          {donor?.address && <p className="text-gray-500 text-sm">{donor.address}</p>}
          {donor?.email && <p className="text-gray-500 text-sm">{donor.email}</p>}
        </div>

        {/* Donation Details */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 text-sm text-gray-500">Description</th>
                <th className="text-right px-4 py-2 text-sm text-gray-500">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">Charitable Donation</p>
                  <p className="text-sm text-gray-500">Campaign: {donation.campaign}</p>
                  <p className="text-sm text-gray-500">Payment Method: {donation.method}</p>
                </td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600 text-xl">
                  {formatCurrency(donation.amount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Legal Text */}
        <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
          <p className="mb-2">
            This receipt confirms that no goods or services were provided in exchange for this contribution.
          </p>
          <p>
            This donation may be tax-deductible. Please consult your tax advisor for details.
          </p>
          <p className="mt-4 font-medium text-gray-700">Thank you for your generous support!</p>
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-end gap-3 print:hidden">
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50"
        >
          <Mail className="w-4 h-4" />
          Email Receipt
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          <Printer className="w-4 h-4" />
          Print Receipt
        </button>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #receipt, #receipt * {
            visibility: visible;
          }
          #receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

// Pledge Payment Receipt Component
function PledgePaymentReceipt({ receiptData, settings, sendEmail, onClose }) {
  const { pledge, donor, paymentAmount, paymentMethod, checkDetails, newPaidAmount, remainingBalance, paymentDate, receiptId } = receiptData;
  const receiptNumber = `PPR-${receiptId.toUpperCase()}-${new Date(paymentDate).getFullYear()}`;

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    const donorEmail = pledge.donorEmail || donor?.email || '';
    const donorName = donor?.name || pledge.donorName;
    
    const subject = `Pledge Payment Receipt - ${receiptNumber} - ${settings.organizationName}`;
    
    let checkInfo = '';
    if (checkDetails) {
      checkInfo = `
Check Number: ${checkDetails.checkNumber}
Bank Name: ${checkDetails.bankName}`;
    }
    
    const body = 
`Dear ${donorName},

Thank you for your pledge payment to ${settings.organizationName}!

PLEDGE PAYMENT RECEIPT
======================
Receipt Number: ${receiptNumber}
Payment Date: ${formatDate(paymentDate)}

Donor Information:
${donorName}
${donor?.address || ''}
${donor?.email || ''}

Pledge Information:
-------------------
Campaign: ${pledge.campaign}
Pledge Period: ${formatDate(pledge.startDate)} - ${formatDate(pledge.endDate)}
Frequency: ${pledge.frequency}

====================================
        PAYMENT SUMMARY
====================================
Total Amount Pledged:  ${formatCurrency(pledge.totalAmount)}
Payment Received:      ${formatCurrency(paymentAmount)}
Pledge Remaining:      ${remainingBalance > 0 ? formatCurrency(remainingBalance) : 'PAID IN FULL'}
====================================

Payment Details:
----------------
Payment Amount: ${formatCurrency(paymentAmount)}
Payment Method: ${paymentMethod || 'Not specified'}${checkInfo}

Payment History:
----------------
Previous Payments: ${formatCurrency(pledge.paidAmount)}
This Payment: ${formatCurrency(paymentAmount)}
Total Paid to Date: ${formatCurrency(newPaidAmount)}

Pledge Progress: ${Math.round((newPaidAmount / pledge.totalAmount) * 100)}%

This receipt confirms that no goods or services were provided in exchange for this contribution. This donation may be tax-deductible. Please consult your tax advisor for details.

Thank you for your generous support!

${settings.organizationName}
${settings.address}
Tax ID: ${settings.taxId}
${settings.phone || ''}
${settings.email || ''}
${settings.website || ''}
`;

    if (sendEmail) {
      sendEmail(donorEmail, donorName, subject, body);
    }
  };

  return (
    <div className="space-y-4">
      <div id="pledge-receipt" className="bg-white border border-gray-300 rounded-lg p-8 print:border-0">
        {/* Header */}
        <div className="text-center border-b border-gray-200 pb-6 mb-6">
          <div className="flex justify-center mb-4">
            {settings.logo ? (
              <img 
                src={settings.logo} 
                alt="Organization Logo" 
                className="h-16 w-auto object-contain"
              />
            ) : (
              <div className="bg-emerald-600 p-3 rounded-full">
                <Heart className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Pledge Payment Receipt</h2>
          <p className="text-gray-500">{settings.organizationName}</p>
          <p className="text-sm text-gray-400 mt-2">{settings.address}</p>
          <p className="text-sm text-gray-400">Tax ID: {settings.taxId}</p>
          {settings.phone && <p className="text-sm text-gray-400">{settings.phone}</p>}
          {settings.email && <p className="text-sm text-gray-400">{settings.email}</p>}
        </div>

        {/* Receipt Info */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500">Receipt Number</p>
            <p className="font-medium text-gray-900">{receiptNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Payment Date</p>
            <p className="font-medium text-gray-900">{formatDate(paymentDate)}</p>
          </div>
        </div>

        {/* Donor Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">Donor Information</h3>
          <p className="text-gray-700">{donor?.name || pledge.donorName}</p>
          {donor?.address && <p className="text-gray-500 text-sm">{donor.address}</p>}
          {donor?.email && <p className="text-gray-500 text-sm">{donor.email}</p>}
        </div>

        {/* Pledge Details */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-blue-900">Pledge Information</h3>
          </div>
          <div className="p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Campaign:</span>
              <span className="font-medium text-gray-900">{pledge.campaign}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pledge Period:</span>
              <span className="font-medium text-gray-900">{formatDate(pledge.startDate)} - {formatDate(pledge.endDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Frequency:</span>
              <span className="font-medium text-gray-900 capitalize">{pledge.frequency}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Pledge Amount:</span>
              <span className="font-medium text-gray-900">{formatCurrency(pledge.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 text-sm text-gray-500">Description</th>
                <th className="text-right px-4 py-2 text-sm text-gray-500">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">Pledge Payment Received</p>
                  <p className="text-sm text-gray-500">Payment toward {pledge.campaign} pledge</p>
                  <p className="text-sm text-gray-500">Payment Method: <span className="font-medium">{paymentMethod || 'Not specified'}</span></p>
                  {checkDetails && (
                    <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                      <p className="text-blue-800">Check #: <span className="font-medium">{checkDetails.checkNumber}</span></p>
                      <p className="text-blue-800">Bank: <span className="font-medium">{checkDetails.bankName}</span></p>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600 text-xl">
                  {formatCurrency(paymentAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Key Summary - Total Pledged, Payment Received, Remaining */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-600 font-medium mb-1">Total Amount Pledged</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(pledge.totalAmount)}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <p className="text-sm text-emerald-600 font-medium mb-1">Payment Received</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(paymentAmount)}</p>
          </div>
          <div className={`rounded-lg p-4 text-center ${remainingBalance > 0 ? 'bg-orange-50' : 'bg-emerald-50'}`}>
            <p className={`text-sm font-medium mb-1 ${remainingBalance > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>Pledge Remaining</p>
            <p className={`text-2xl font-bold ${remainingBalance > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
              {remainingBalance > 0 ? formatCurrency(remainingBalance) : 'PAID'}
            </p>
          </div>
        </div>

        {/* Detailed Balance Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Payment History</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Previous Payments:</span>
              <span className="font-medium text-gray-900">{formatCurrency(pledge.paidAmount)}</span>
            </div>
            <div className="flex justify-between text-emerald-600">
              <span className="font-medium">This Payment:</span>
              <span className="font-bold">+ {formatCurrency(paymentAmount)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-300">
              <span className="text-gray-700 font-medium">Total Paid to Date:</span>
              <span className="font-bold text-gray-900">{formatCurrency(newPaidAmount)}</span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Pledge Progress</span>
              <span className="font-medium">{Math.round((newPaidAmount / pledge.totalAmount) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${newPaidAmount >= pledge.totalAmount ? 'bg-emerald-600' : 'bg-blue-600'}`}
                style={{ width: `${Math.min((newPaidAmount / pledge.totalAmount) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Legal Text */}
        <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
          <p className="mb-2">
            This receipt confirms that no goods or services were provided in exchange for this contribution.
          </p>
          <p>
            This donation may be tax-deductible. Please consult your tax advisor for details.
          </p>
          <p className="mt-4 font-medium text-gray-700">Thank you for your generous support!</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 print:hidden">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50"
        >
          <Mail className="w-4 h-4" />
          Email Receipt
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          <Printer className="w-4 h-4" />
          Print Receipt
        </button>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #pledge-receipt, #pledge-receipt * {
            visibility: visible;
          }
          #pledge-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

// Login Screen Component
function LoginScreen({ onLogin, onResetPassword, organizationName }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: enter username/email, 2: enter new password
  const [resetUsername, setResetUsername] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [isResetting, setIsResetting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const result = await onLogin(username, password);
      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    setResetMessage({ type: '', text: '' });
    
    if (!resetUsername.trim() || !resetEmail.trim()) {
      setResetMessage({ type: 'error', text: 'Please enter both username and email' });
      return;
    }
    
    // Move to step 2 (enter new password)
    setResetStep(2);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage({ type: '', text: '' });
    
    if (newPassword.length < 6) {
      setResetMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setResetMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    
    setIsResetting(true);
    
    try {
      const result = await onResetPassword(resetUsername, resetEmail, newPassword);
      
      if (result.success) {
        setResetMessage({ type: 'success', text: result.message });
        // After 2 seconds, close the modal and go back to login
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetStep(1);
          setResetUsername('');
          setResetEmail('');
          setNewPassword('');
          setConfirmPassword('');
          setResetMessage({ type: '', text: '' });
        }, 2000);
      } else {
        setResetMessage({ type: 'error', text: result.error });
        // Go back to step 1 if verification failed
        setResetStep(1);
      }
    } catch (err) {
      setResetMessage({ type: 'error', text: 'Failed to reset password. Please try again.' });
    } finally {
      setIsResetting(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setResetStep(1);
    setResetUsername('');
    setResetEmail('');
    setNewPassword('');
    setConfirmPassword('');
    setResetMessage({ type: '', text: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 p-8 text-center">
          <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Donation Management</h1>
          <p className="text-emerald-100 mt-1">{organizationName}</p>
        </div>

        {/* Login Form */}
        <div className="p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">Sign In</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base"
                  placeholder="Enter your username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline font-medium"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-base font-medium touch-manipulation"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <LogOut className="w-5 h-5 rotate-180" />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Key className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Reset Password</h3>
                    <p className="text-amber-100 text-sm">
                      {resetStep === 1 ? 'Step 1: Verify your identity' : 'Step 2: Create new password'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeForgotPassword}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Progress Steps */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className={`flex items-center gap-2 ${resetStep >= 1 ? 'text-amber-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${resetStep >= 1 ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>
                    1
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Verify</span>
                </div>
                <div className={`w-8 h-0.5 ${resetStep >= 2 ? 'bg-amber-500' : 'bg-gray-200'}`}></div>
                <div className={`flex items-center gap-2 ${resetStep >= 2 ? 'text-amber-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${resetStep >= 2 ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Reset</span>
                </div>
              </div>

              {/* Messages */}
              {resetMessage.text && (
                <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                  resetMessage.type === 'error' 
                    ? 'bg-red-50 border border-red-200 text-red-700' 
                    : 'bg-green-50 border border-green-200 text-green-700'
                }`}>
                  {resetMessage.type === 'error' ? (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span className="text-sm">{resetMessage.text}</span>
                </div>
              )}

              {/* Step 1: Verify Identity */}
              {resetStep === 1 && (
                <form onSubmit={handleVerifyIdentity} className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your username and email address to verify your identity.
                  </p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={resetUsername}
                        onChange={(e) => setResetUsername(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Enter your username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeForgotPassword}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 font-medium shadow-lg shadow-amber-500/25 transition-all"
                    >
                      Continue
                    </button>
                  </div>
                </form>
              )}

              {/* Step 2: Enter New Password */}
              {resetStep === 2 && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Create a new password for your account.
                  </p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Enter new password"
                        minLength={6}
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="Confirm new password"
                        minLength={6}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setResetStep(1)}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isResetting}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 font-medium shadow-lg shadow-amber-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isResetting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Resetting...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Pledge Summary Receipt Component
function PledgeSummaryReceipt({ pledge, donor, settings, sendEmail, onClose }) {
  const receiptNumber = `PSR-${pledge.id.slice(0, 8).toUpperCase()}-${new Date().getFullYear()}`;
  const remainingBalance = pledge.totalAmount - pledge.paidAmount;
  const progress = (pledge.paidAmount / pledge.totalAmount) * 100;

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    const donorEmail = pledge.donorEmail || donor?.email || '';
    const donorName = donor?.name || pledge.donorName;
    
    const subject = `Pledge Summary - ${receiptNumber} - ${settings.organizationName}`;
    
    const body = 
`Dear ${donorName},

Thank you for your continued support of ${settings.organizationName}!

PLEDGE SUMMARY
==============
Receipt Number: ${receiptNumber}
Date: ${formatDate(new Date().toISOString())}

Donor Information:
${donorName}
${donor?.address || ''}
${donor?.email || ''}
${donor?.phone || ''}

Pledge Information:
-------------------
Campaign: ${pledge.campaign}
Pledge Period: ${formatDate(pledge.startDate)} - ${formatDate(pledge.endDate)}
Frequency: ${pledge.frequency}

====================================
        PLEDGE SUMMARY
====================================
Total Amount Pledged:  ${formatCurrency(pledge.totalAmount)}
Total Payments Made:   ${formatCurrency(pledge.paidAmount)}
Pledge Remaining:      ${remainingBalance > 0 ? formatCurrency(remainingBalance) : 'PAID IN FULL'}
====================================

Pledge Progress: ${Math.round(progress)}%

Status: ${pledge.status.toUpperCase()}

"When a man dies, his deeds come to an end except for three: Sadaqah Jariyah (continuous charity), knowledge which is beneficial, or a virtuous descendant who prays for him."

Thank you for your generous support!

${settings.organizationName}
${settings.address}
Tax ID: ${settings.taxId}
${settings.phone || ''}
${settings.email || ''}
`;

    if (sendEmail) {
      sendEmail(donorEmail, donorName, subject, body);
    }
  };

  return (
    <div className="space-y-4">
      <div id="pledge-summary-receipt" className="bg-white border border-gray-300 rounded-lg p-8 print:border-0">
        {/* Header */}
        <div className="text-center border-b border-gray-200 pb-6 mb-6">
          <div className="flex justify-center mb-4">
            {settings.logo ? (
              <img 
                src={settings.logo} 
                alt="Organization Logo" 
                className="h-16 w-auto object-contain"
              />
            ) : (
              <div className="bg-emerald-600 p-3 rounded-full">
                <Heart className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Pledge Summary</h2>
          <p className="text-gray-500">{settings.organizationName}</p>
          <p className="text-sm text-gray-400 mt-2">{settings.address}</p>
          <p className="text-sm text-gray-400">Tax ID: {settings.taxId}</p>
        </div>

        {/* Receipt Info */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500">Receipt Number</p>
            <p className="font-medium text-gray-900">{receiptNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Date</p>
            <p className="font-medium text-gray-900">{formatDate(new Date().toISOString())}</p>
          </div>
        </div>

        {/* Donor Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">Donor Information</h3>
          <p className="text-gray-700">{donor?.name || pledge.donorName}</p>
          {donor?.address && <p className="text-gray-500 text-sm">{donor.address}</p>}
          {donor?.email && <p className="text-gray-500 text-sm">{donor.email}</p>}
          {donor?.phone && <p className="text-gray-500 text-sm">{donor.phone}</p>}
        </div>

        {/* Pledge Details */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-blue-900">Pledge Information</h3>
          </div>
          <div className="p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Campaign:</span>
              <span className="font-medium text-gray-900">{pledge.campaign}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pledge Period:</span>
              <span className="font-medium text-gray-900">{formatDate(pledge.startDate)} - {formatDate(pledge.endDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Frequency:</span>
              <span className="font-medium text-gray-900 capitalize">{pledge.frequency}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`font-medium capitalize ${
                pledge.status === 'completed' ? 'text-emerald-600' :
                pledge.status === 'active' ? 'text-blue-600' :
                pledge.status === 'cancelled' ? 'text-red-600' :
                'text-yellow-600'
              }`}>{pledge.status}</span>
            </div>
          </div>
        </div>

        {/* Key Summary - Total Pledged, Payments Made, Remaining */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-600 font-medium mb-1">Total Amount Pledged</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(pledge.totalAmount)}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <p className="text-sm text-emerald-600 font-medium mb-1">Payments Received</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(pledge.paidAmount)}</p>
          </div>
          <div className={`rounded-lg p-4 text-center ${remainingBalance > 0 ? 'bg-orange-50' : 'bg-emerald-50'}`}>
            <p className={`text-sm font-medium mb-1 ${remainingBalance > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>Pledge Remaining</p>
            <p className={`text-2xl font-bold ${remainingBalance > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
              {remainingBalance > 0 ? formatCurrency(remainingBalance) : 'PAID'}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600 font-medium">Pledge Progress</span>
            <span className="font-bold text-gray-900">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full transition-all ${progress >= 100 ? 'bg-emerald-600' : 'bg-blue-600'}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>$0</span>
            <span>{formatCurrency(pledge.totalAmount)}</span>
          </div>
        </div>

        {/* Footer Message */}
        <div className="text-center text-sm text-gray-600 border-t border-gray-200 pt-6">
          <p className="italic mb-4">
            "When a man dies, his deeds come to an end except for three: Sadaqah Jariyah (continuous charity), knowledge which is beneficial, or a virtuous descendant who prays for him."
          </p>
          <p className="mt-4 font-medium text-gray-700">Thank you for your generous support!</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 print:hidden">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50"
        >
          <Mail className="w-4 h-4" />
          Email Summary
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Printer className="w-4 h-4" />
          Print Summary
        </button>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #pledge-summary-receipt, #pledge-summary-receipt * {
            visibility: visible;
          }
          #pledge-summary-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

// User Form Component
function UserForm({ user, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    username: user?.username || '',
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'user',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!user && form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!user && form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    const userData = {
      username: form.username,
      name: form.name,
      email: form.email,
      role: form.role
    };

    if (!user) {
      userData.password = form.password;
    } else if (form.password) {
      userData.newPassword = form.password;
    }

    onSubmit(userData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
        <input
          type="text"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="johndoe"
          required
          disabled={user?.id === 'admin'}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="John Doe"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="john@example.com"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          disabled={user?.id === 'admin'}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {user ? 'New Password (leave blank to keep current)' : 'Password *'}
        </label>
        <input
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="••••••••"
          required={!user}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {user ? 'Confirm New Password' : 'Confirm Password *'}
        </label>
        <input
          type="password"
          value={form.confirmPassword}
          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="••••••••"
          required={!user}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          {user ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
}

// Change Password Form Component
function ChangePasswordForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (form.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    const result = onSubmit(form.currentPassword, form.newPassword);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        onCancel();
      }, 1500);
    } else {
      setError(result.error);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900">Password Changed!</h3>
        <p className="text-gray-500 mt-2">Your password has been updated successfully.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password *</label>
        <input
          type="password"
          value={form.currentPassword}
          onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="••••••••"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
        <input
          type="password"
          value={form.newPassword}
          onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="••••••••"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password *</label>
        <input
          type="password"
          value={form.confirmPassword}
          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          placeholder="••••••••"
          required
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          Change Password
        </button>
      </div>
    </form>
  );
}
